import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import Navbar from "../navbar/Navbar";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import API, { ensureOrthancAuthCookie } from "../../services/api.ts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronRight, ChevronDown, Monitor, FileText, Users, Calendar, ArrowLeft, Tags, Plus, Trash2, X, HelpCircle, List, Type, Image, File, Star, Globe, ExternalLink, Layers, LayoutGrid, Map as MapIcon, Wrench, RefreshCw, Upload, Cpu, GitBranch, Play, Pause, Settings, GripVertical, SlidersHorizontal, Shuffle, UserCheck, Eye, CheckCircle, Clock } from "lucide-react";
import './StudyDashboard.css';
import '../shared/starfield.css';
import TaskTracker from './ConversionTracker';
import WorkflowTracker from './WorkflowTracker';
import ReaderProfileModal from './ReaderProfileModal';


// Helper to create a unique composite ID for each data item
// This prevents ID collisions between MedicalImage, MedicalDocument, and PatientData
const getCompositeId = (item) => `${item.id}_${item.source_model || 'Unknown'}`;

// Viewers offered as the "Reader viewer" option for randomized studies. Subset of
// ViewerCard's systemViewers — only viewers that can render a single patient's
// data without further selection. Label Studio / CVAT are project-level
// annotation tools, not patient viewers, so they're excluded here.
const READER_VIEWER_OPTIONS = [
    { value: "dv2", label: "DV2 (DICOM + Image + Document)", category: "Multimodal Viewers" },
    { value: "discovery-viewer-test", label: "Discovery Viewer Test (Image + Text)", category: "Multimodal Viewers" },
    { value: "image-viewer", label: "Image Viewer (JPG/PNG)", category: "Standard Images" },
    { value: "ohif", label: "OHIF Basic Viewer", category: "DICOM Viewers" },
    { value: "ohif-segmentation", label: "OHIF Segmentation", category: "DICOM Viewers" },
    { value: "ohif-3d", label: "OHIF 3D", category: "DICOM Viewers" },
    { value: "ohif-microscopy", label: "OHIF Microscopy", category: "DICOM Viewers" },
    { value: "trame-slicer", label: "3D Slicer", category: "DICOM Viewers" },
    { value: "ehr-viewer", label: "EHR Viewer (JSON/CSV)", category: "Clinical Data" },
    { value: "signal-viewer", label: "Signal Viewer (ECG/EEG)", category: "Clinical Data" },
    { value: "document-viewer", label: "Document Viewer (PDF/Text)", category: "Documents" },
    { value: "pme", label: "PME Pathology Viewer (WSI)", category: "Pathology" },
];

const DatasetTable = ({
    studyData = [],
    onSelectionChange,
    annotatedPatients = [],
    canManage = false,
    selectedReviewerId = '',
    reviewerAssignedPatientIds = [],
    showOnlyAssigned = false,
    onAssignPatientsToReviewer,
    onRemoveCaseFromReviewer,
}) => {
    console.log('DatasetTable received annotatedPatients:', annotatedPatients);
    const [selectedRowIds, setSelectedRowIds] = React.useState([]);
    const [studyIdFilter, setStudyIdFilter] = React.useState("");

    const assignedSet = React.useMemo(
        () => new Set(reviewerAssignedPatientIds || []),
        [reviewerAssignedPatientIds]
    );

    // open/closed state for each grouping level: Patient > Study > Type > Series
    const [openPatients, setOpenPatients] = React.useState({});
    const [openStudies, setOpenStudies] = React.useState({});
    const [openTypes, setOpenTypes] = React.useState({});
    const [openSeries, setOpenSeries] = React.useState({});

    // Get unique study IDs for filter dropdown
    const uniqueStudyIds = React.useMemo(() => {
        const ids = new Set();
        studyData.forEach(item => {
            if (item.study_id) {
                ids.add(item.study_id);
            }
        });
        return Array.from(ids).sort();
    }, [studyData]);

    // Filter study data by selected study ID and expand converted items
    // When a NIfTI item has been converted to DICOM, show both the original NIfTI and a DICOM row
    const filteredStudyData = React.useMemo(() => {
        let baseData = studyIdFilter
            ? studyData.filter(item => item.study_id === studyIdFilter)
            : studyData;

        // When "Show only assigned" is on, restrict to patients in the reviewer's set.
        // (The backend already does this when view_as is set; this is for admin's own view.)
        if (showOnlyAssigned && assignedSet.size > 0) {
            baseData = baseData.filter(item => assignedSet.has(item.patient_id));
        }

        // Expand items that have been converted to DICOM
        const expandedData = [];
        for (const item of baseData) {
            // Always add the original item
            expandedData.push(item);

            // If this NIfTI item has been converted, add a virtual DICOM row
            if (item.metadata?.dicom_output_dir &&
                item.original_file_type?.toLowerCase() === 'nifti' &&
                !item.is_annotation) {
                expandedData.push({
                    ...item,
                    // Mark this as the converted DICOM version
                    isConvertedDicom: true,
                    // Override the file type to show as DICOM
                    original_file_type: 'dicom',
                    // Keep the converted_at for the NEW badge
                    converted_at: item.metadata.converted_at,
                    // Create a unique ID for this virtual row
                    id: `${item.id}_dicom`,
                });
            }
        }
        return expandedData;
    }, [studyData, studyIdFilter, showOnlyAssigned, assignedSet]);

    // Helper function to render type badge with icon
    const renderTypeBadge = (row) => {
        const dataType = row.data_type;
        const type = row.type || row.modality || "Unknown";

        // Check if it's an annotation first
        if (row.is_annotation) {
            const annotationType = row.annotation_type?.toUpperCase() || 'MASK';
            return (
                <span className="type-badge annotation-badge">
                    <Layers size={14} />
                    {annotationType}
                </span>
            );
        }

        if (row.is_map) {
            return (
                <span className="type-badge map-badge" style={{ background: 'rgba(42, 157, 143, 0.15)', color: '#2a9d8f' }}>
                    <MapIcon size={14} />
                    MAP
                </span>
            );
        }

        if (dataType === 'document') {
            const docType = row.document_type?.toUpperCase() || 'DOC';
            return (
                <span className="type-badge document-badge">
                    <File size={14} />
                    {docType}
                </span>
            );
        } else {
            // Image type
            return (
                <span className="type-badge image-badge">
                    <Image size={14} />
                    {type}
                </span>
            );
        }
    };

    // Helper function to get type icon for group header
    const getTypeIcon = (type, isAnnotation = false) => {
        if (isAnnotation) {
            return <Tags size={14} className="type-icon annotation" />;
        }
        if (type === 'maps' || type === 'Maps') {
            return <MapIcon size={14} className="type-icon" style={{ color: '#2a9d8f' }} />;
        }
        if (type.toLowerCase().includes('document')) {
            return <File size={14} className="type-icon document" />;
        }
        return <Image size={14} className="type-icon image" />;
    };

    const handleRowSelect = (row) => {
        const compositeId = getCompositeId(row);
        setSelectedRowIds((prevSelected) => {
            const next = prevSelected.includes(compositeId)
                ? prevSelected.filter((rowId) => rowId !== compositeId)
                : [...prevSelected, compositeId];
            setTimeout(() => onSelectionChange?.(next), 0);
            return next;
        });
    };

    const handlePatientSelect = (patientId, studiesMap) => {
        const allCompositeIds = [];
        studiesMap.forEach((typesMap) => {
            typesMap.forEach((seriesMap) => {
                seriesMap.forEach((rows) => {
                    rows.forEach((row) => {
                        allCompositeIds.push(getCompositeId(row));
                    });
                });
            });
        });

        setSelectedRowIds((prevSelected) => {
            const allSelected = allCompositeIds.every((id) => prevSelected.includes(id));

            let next;
            if (allSelected) {
                // Deselect: remove all this patient's IDs
                next = prevSelected.filter((id) => !allCompositeIds.includes(id));
            } else {
                // Select: add this patient's IDs, ensuring no duplicates
                const newIds = allCompositeIds.filter((id) => !prevSelected.includes(id));
                next = [...prevSelected, ...newIds];
            }
            setTimeout(() => onSelectionChange?.(next), 0);
            return next;
        });
    };

    // Select all items within a study
    const handleStudySelect = (typesMap) => {
        const allCompositeIds = [];
        typesMap.forEach((seriesMap) => {
            seriesMap.forEach((rows) => {
                rows.forEach((row) => {
                    allCompositeIds.push(getCompositeId(row));
                });
            });
        });

        setSelectedRowIds((prevSelected) => {
            const allSelected = allCompositeIds.every((id) => prevSelected.includes(id));

            let next;
            if (allSelected) {
                // Deselect: remove all this study's IDs
                next = prevSelected.filter((id) => !allCompositeIds.includes(id));
            } else {
                // Select: add this study's IDs, ensuring no duplicates
                const newIds = allCompositeIds.filter((id) => !prevSelected.includes(id));
                next = [...prevSelected, ...newIds];
            }
            setTimeout(() => onSelectionChange?.(next), 0);
            return next;
        });
    };

    // Select all items within a type
    const handleTypeSelect = (seriesMap) => {
        const allCompositeIds = [];
        seriesMap.forEach((rows) => {
            rows.forEach((row) => {
                allCompositeIds.push(getCompositeId(row));
            });
        });

        setSelectedRowIds((prevSelected) => {
            const allSelected = allCompositeIds.every((id) => prevSelected.includes(id));

            let next;
            if (allSelected) {
                next = prevSelected.filter((id) => !allCompositeIds.includes(id));
            } else {
                const newIds = allCompositeIds.filter((id) => !prevSelected.includes(id));
                next = [...prevSelected, ...newIds];
            }
            setTimeout(() => onSelectionChange?.(next), 0);
            return next;
        });
    };

    // Select all items within a series
    const handleSeriesSelect = (rows) => {
        const allCompositeIds = rows.map((row) => getCompositeId(row));

        setSelectedRowIds((prevSelected) => {
            const allSelected = allCompositeIds.every((id) => prevSelected.includes(id));

            let next;
            if (allSelected) {
                next = prevSelected.filter((id) => !allCompositeIds.includes(id));
            } else {
                const newIds = allCompositeIds.filter((id) => !prevSelected.includes(id));
                next = [...prevSelected, ...newIds];
            }
            setTimeout(() => onSelectionChange?.(next), 0);
            return next;
        });
    };

    // Select all patients (respects filter)
    const handleSelectAll = () => {
        const allCompositeIds = filteredStudyData.map(row => getCompositeId(row));
        setSelectedRowIds(allCompositeIds);
        setTimeout(() => onSelectionChange?.(allCompositeIds), 0);
    };

    // Clear selection
    const handleClearSelection = () => {
        setSelectedRowIds([]);
        setTimeout(() => onSelectionChange?.([]), 0);
    };

    // Select only non-annotated patients (respects filter)
    const handleSelectNonAnnotated = () => {
        const nonAnnotatedCompositeIds = [];
        filteredStudyData.forEach(row => {
            const patientId = row.patient_id ?? "Unknown Patient";
            if (!annotatedPatients.includes(patientId)) {
                nonAnnotatedCompositeIds.push(getCompositeId(row));
            }
        });
        setSelectedRowIds(nonAnnotatedCompositeIds);
        setTimeout(() => onSelectionChange?.(nonAnnotatedCompositeIds), 0);
    };

    // Helper function to normalize series name by removing file extensions
    const normalizeSeriesName = (name) => {
        if (!name) return "Unknown Series";
        // Remove common file extensions to group NIfTI and DICOM versions together
        return name
            .replace(/\.nii\.gz$/i, '')
            .replace(/\.nii$/i, '')
            .replace(/\.dcm$/i, '');
    };

    // Helper function to get the format from a data item
    const getDataFormat = (item) => {
        // Check if this is a virtual converted DICOM row
        if (item.isConvertedDicom) {
            return 'DICOM';
        }
        // For MedicalImage records, check original_format first
        // This indicates the original format before conversion to DICOM for viewing
        if (item.original_format) {
            if (item.original_format.toLowerCase() === 'nifti') return 'DICOM (NIfTI)';
            if (item.original_format.toLowerCase() === 'numpy') return 'DICOM (NumPy)';
            if (item.original_format.toLowerCase() === 'dicom') return 'DICOM';
        }
        // For PatientData records, check original_file_type
        if (item.original_file_type) {
            if (item.original_file_type.toLowerCase() === 'nifti') return 'NIfTI';
            if (item.original_file_type.toLowerCase() === 'dicom') return 'DICOM';
            return item.original_file_type.toUpperCase();
        }
        // Fall back to checking the series name or filename
        const name = item.series_name || item.series_id || item.filename || '';
        if (name.includes('.nii')) return 'NIfTI';
        if (name.includes('.dcm') || item.data_type === 'dicom') return 'DICOM';
        return 'Unknown';
    };

    // Helper function to get the CSS class for a format badge
    const getFormatBadgeClass = (item) => {
        // Virtual converted DICOM rows get special styling
        if (item.isConvertedDicom) return 'dicom-converted';
        const format = getDataFormat(item);
        if (format.includes('NIfTI') && format.includes('DICOM')) return 'dicom-converted';
        if (format.includes('NumPy') && format.includes('DICOM')) return 'dicom-converted';
        if (format === 'NIfTI') return 'nifti';
        if (format === 'DICOM') return 'dicom';
        return 'unknown';
    };

    // Helper function to check if an item was recently converted (within 1 hour)
    // Only shows NEW badge on the converted DICOM row, not the original NIfTI
    const isRecentlyConverted = (item) => {
        // Only show NEW badge on the virtual DICOM rows, not the original NIfTI
        if (!item.isConvertedDicom) return false;

        // Check converted_at (directly on virtual row or in metadata)
        const convertedAt = item.converted_at || item.metadata?.converted_at;
        if (!convertedAt) return false;

        const convertedTime = new Date(convertedAt).getTime();
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        return convertedTime > oneHourAgo;
    };

    // Build hierarchy: patient -> study -> type -> seriesId -> rows (scans)
    // This matches the Dataset page structure
    // Series names are normalized to group NIfTI and DICOM versions together
    const grouped = React.useMemo(() => {
        const tree = new Map();
        for (const r of filteredStudyData) {
            const patientId = r.patient_id ?? "Unknown Patient";
            const studyId = r.study_id ?? "Unknown Study";
            // If is_annotation or is_map is true, group under their own type
            const type = r.is_annotation ? "annotations" : r.is_map ? "maps" : (r.type ?? r.dataset_type ?? r.modality ?? r.data_type ?? "Unknown Type");
            // Normalize series name to group different formats together
            const rawSeriesName = r.series_name ?? r.series_id ?? "Unknown Series";
            const seriesId = normalizeSeriesName(rawSeriesName);

            if (!tree.has(patientId)) tree.set(patientId, new Map());
            const studies = tree.get(patientId);

            if (!studies.has(studyId)) studies.set(studyId, new Map());
            const types = studies.get(studyId);

            if (!types.has(type)) types.set(type, new Map());
            const series = types.get(type);

            if (!series.has(seriesId)) series.set(seriesId, []);
            series.get(seriesId).push(r);
        }
        return tree;
    }, [filteredStudyData]);

    const togglePatient = (p) =>
        setOpenPatients((s) => ({ ...s, [p]: !s[p] }));

    const toggleStudy = (p, st) => {
        const key = `${p}::${st}`;
        setOpenStudies((s) => ({ ...s, [key]: !s[key] }));
    };

    const toggleType = (p, st, t) => {
        const key = `${p}::${st}::${t}`;
        setOpenTypes((s) => ({ ...s, [key]: !s[key] }));
    };

    const toggleSeries = (p, st, t, sId) => {
        const key = `${p}::${st}::${t}::${sId}`;
        setOpenSeries((s) => ({ ...s, [key]: !s[key] }));
    };

    const totalPatients = grouped.size;
    const totalImages = filteredStudyData.filter(d => d.data_type === 'image' && !d.is_annotation && !d.is_map).length;
    const totalDocuments = filteredStudyData.filter(d => d.data_type === 'document' && !d.is_annotation && !d.is_map).length;
    const totalAnnotations = filteredStudyData.filter(d => d.is_annotation).length;
    const totalMaps = filteredStudyData.filter(d => d.is_map).length;

    return (
        <div className="study-data-card">
            <div className="study-data-header">
                <div className="study-data-title">
                    <FileText className="study-data-icon" />
                    <div>
                        <h3>Study Data</h3>
                        <p className="study-data-subtitle">
                            {totalPatients} patient{totalPatients !== 1 ? 's' : ''} · {totalImages} image{totalImages !== 1 ? 's' : ''} · {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}{totalAnnotations > 0 ? ` · ${totalAnnotations} annotation${totalAnnotations !== 1 ? 's' : ''}` : ''}{totalMaps > 0 ? ` · ${totalMaps} map${totalMaps !== 1 ? 's' : ''}` : ''}
                        </p>
                    </div>
                </div>
                <div className="study-data-actions">
                    {uniqueStudyIds.length > 1 && (
                        <div className="study-id-filter">
                            <label htmlFor="study-id-select" className="filter-label">Study ID:</label>
                            <select
                                id="study-id-select"
                                className="study-id-select"
                                value={studyIdFilter}
                                onChange={(e) => setStudyIdFilter(e.target.value)}
                            >
                                <option value="">All Studies</option>
                                {uniqueStudyIds.map(id => (
                                    <option key={id} value={id}>
                                        {id.length > 30 ? `${id.substring(0, 15)}...${id.substring(id.length - 10)}` : id}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="selection-buttons">
                        <button
                            type="button"
                            className="selection-btn"
                            onClick={handleSelectAll}
                            title="Select all patients"
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            className="selection-btn non-annotated"
                            onClick={handleSelectNonAnnotated}
                            title="Select only non-annotated patients"
                        >
                            Select Non-Annotated
                        </button>
                        {selectedRowIds.length > 0 && (
                            <button
                                type="button"
                                className="selection-btn clear"
                                onClick={handleClearSelection}
                                title="Clear selection"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {selectedRowIds.length > 0 && (
                        <span className="selected-count">
                            {selectedRowIds.length} selected
                        </span>
                    )}
                </div>
            </div>

            {/* Bulk-assign action bar — shown when admin has picked a reviewer
                and selected at least one patient via the patient-row checkboxes. */}
            {canManage && selectedReviewerId && (() => {
                const selectedPatients = Array.from(new Set(
                    filteredStudyData
                        .filter(r => selectedRowIds.includes(getCompositeId(r)))
                        .map(r => r.patient_id)
                        .filter(Boolean)
                ));
                if (selectedPatients.length === 0) return null;
                const toAssign = selectedPatients.filter(pid => !assignedSet.has(pid));
                const toRemove = selectedPatients.filter(pid => assignedSet.has(pid));
                return (
                    <div className="bulk-reviewer-bar" style={{
                        display: 'flex', gap: 8, alignItems: 'center',
                        background: '#f1f7ff', border: '1px solid #cfe2ff',
                        padding: '8px 12px', borderRadius: 6, margin: '0 0 10px 0',
                        fontSize: 13,
                    }}>
                        <UserCheck size={16} style={{ color: '#0d6efd' }} />
                        <span><strong>{selectedPatients.length}</strong> patient{selectedPatients.length !== 1 ? 's' : ''} selected</span>
                        {toAssign.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => onAssignPatientsToReviewer?.(toAssign)}
                            >
                                Assign {toAssign.length} to reviewer
                            </button>
                        )}
                        {toRemove.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={async () => {
                                    if (!window.confirm(
                                        `Remove ${toRemove.length} patient${toRemove.length !== 1 ? 's' : ''} from this reviewer? This deletes their annotations and uploaded segmentations for these patients.`
                                    )) return;
                                    for (const pid of toRemove) {
                                        // eslint-disable-next-line no-await-in-loop
                                        await onRemoveCaseFromReviewer?.(pid, /* skipConfirm */ true);
                                    }
                                }}
                            >
                                Remove {toRemove.length} from reviewer
                            </button>
                        )}
                    </div>
                );
            })()}

            <div className="study-data-content">
                {studyData.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No data in this study yet</p>
                    </div>
                ) : filteredStudyData.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No data matches the selected Study ID filter</p>
                        <button
                            type="button"
                            className="selection-btn"
                            onClick={() => setStudyIdFilter("")}
                            style={{ marginTop: '1rem' }}
                        >
                            Clear Filter
                        </button>
                    </div>
                ) : (
                    <table className="study-table">
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>Select</th>
                                <th>Patient</th>
                                <th>Series</th>
                                <th>Format</th>
                                <th>Annotated</th>
                                <th>Dataset</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from(grouped.entries()).map(([patientId, studiesMap]) => {
                                const isOpenP = !!openPatients[patientId];
                                const allCompositeIds = [];
                                studiesMap.forEach((typesMap) => {
                                    typesMap.forEach((seriesMap) => {
                                        seriesMap.forEach((rows) => {
                                            rows.forEach((row) => {
                                                allCompositeIds.push(getCompositeId(row));
                                            });
                                        });
                                    });
                                });
                                const isPatientSelected = allCompositeIds.every((id) => selectedRowIds.includes(id));

                                const isAnnotated = annotatedPatients.includes(patientId);

                                // Count studies and series for display
                                const studyCount = studiesMap.size;
                                let totalSeriesCount = 0;
                                studiesMap.forEach((typesMap) => {
                                    typesMap.forEach((seriesMap) => {
                                        totalSeriesCount += seriesMap.size;
                                    });
                                });

                                const isAssignedToReviewer = !!selectedReviewerId && assignedSet.has(patientId);
                                const showReviewerControls = canManage && !!selectedReviewerId;

                                return (
                                    <React.Fragment key={`p-${patientId}`}>
                                        <tr className={`group-row patient-row ${isAnnotated ? 'annotated' : ''}`}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-patient"
                                                    checked={isPatientSelected}
                                                    onChange={() => handlePatientSelect(patientId, studiesMap)}
                                                />
                                            </td>
                                            <td colSpan={5}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <button
                                                        className="collapse-btn"
                                                        onClick={() => togglePatient(patientId)}
                                                        type="button"
                                                        style={{ flex: 1, textAlign: 'left' }}
                                                    >
                                                        {isOpenP ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                        <span className="group-label">Patient: {patientId}</span>
                                                        <span className="group-count">
                                                            {studyCount} stud{studyCount !== 1 ? "ies" : "y"}, {totalSeriesCount} series
                                                        </span>
                                                        {isAnnotated && (
                                                            <span className="annotated-badge" title="Patient has been annotated">
                                                                <i className="fa fa-check-circle"></i>
                                                                Annotated
                                                            </span>
                                                        )}
                                                        {showReviewerControls && isAssignedToReviewer && (
                                                            <span
                                                                className="annotated-badge"
                                                                style={{ background: '#cfe2ff', color: '#0a58ca', marginLeft: 6 }}
                                                                title="Assigned to selected reviewer"
                                                            >
                                                                <UserCheck size={12} style={{ marginRight: 4 }} />
                                                                Assigned
                                                            </span>
                                                        )}
                                                    </button>
                                                    {showReviewerControls && (
                                                        isAssignedToReviewer ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Remove this case from the selected reviewer (deletes their annotations + segmentations)"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onRemoveCaseFromReviewer?.(patientId);
                                                                }}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                title="Assign this patient to the selected reviewer"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onAssignPatientsToReviewer?.([patientId]);
                                                                }}
                                                            >
                                                                <Plus size={14} />
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {isOpenP &&
                                            Array.from(studiesMap.entries()).map(([studyId, typesMap]) => {
                                                const studyKey = `${patientId}::${studyId}`;
                                                const isOpenSt = !!openStudies[studyKey];

                                                // Count series in this study and collect all IDs for selection check
                                                let studySeriesCount = 0;
                                                const studyCompositeIds = [];
                                                typesMap.forEach((seriesMap) => {
                                                    studySeriesCount += seriesMap.size;
                                                    seriesMap.forEach((rows) => {
                                                        rows.forEach((row) => {
                                                            studyCompositeIds.push(getCompositeId(row));
                                                        });
                                                    });
                                                });
                                                const isStudySelected = studyCompositeIds.length > 0 && studyCompositeIds.every((id) => selectedRowIds.includes(id));

                                                return (
                                                    <React.Fragment key={`st-${studyKey}`}>
                                                        <tr className="group-row study-row">
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="checkbox-study"
                                                                    checked={isStudySelected}
                                                                    onChange={() => handleStudySelect(typesMap)}
                                                                />
                                                            </td>
                                                            <td className="indent-1" colSpan={5}>
                                                                <button
                                                                    className="collapse-btn"
                                                                    onClick={() => toggleStudy(patientId, studyId)}
                                                                    type="button"
                                                                >
                                                                    {isOpenSt ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                                    <i className="fa fa-folder-open me-1" style={{ color: '#ffc107' }}></i>
                                                                    <span className="group-label">Study: {studyId}</span>
                                                                    <span className="group-count">{studySeriesCount} series</span>
                                                                </button>
                                                            </td>
                                                        </tr>

                                                        {isOpenSt &&
                                                            Array.from(typesMap.entries()).map(([type, seriesMap]) => {
                                                                const typeKey = `${patientId}::${studyId}::${type}`;
                                                                const isOpenT = !!openTypes[typeKey];

                                                                // Collect all IDs for this type for selection check
                                                                const typeCompositeIds = [];
                                                                seriesMap.forEach((rows) => {
                                                                    rows.forEach((row) => {
                                                                        typeCompositeIds.push(getCompositeId(row));
                                                                    });
                                                                });
                                                                const isTypeSelected = typeCompositeIds.length > 0 && typeCompositeIds.every((id) => selectedRowIds.includes(id));

                                                                return (
                                                                    <React.Fragment key={`t-${typeKey}`}>
                                                                        <tr className="group-row type-row">
                                                                            <td>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="checkbox-type"
                                                                                    checked={isTypeSelected}
                                                                                    onChange={() => handleTypeSelect(seriesMap)}
                                                                                />
                                                                            </td>
                                                                            <td />
                                                                            <td className="indent-2" colSpan={4}>
                                                                                <button
                                                                                    className="collapse-btn"
                                                                                    onClick={() => toggleType(patientId, studyId, type)}
                                                                                    type="button"
                                                                                >
                                                                                    {isOpenT ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                                                    {getTypeIcon(type, type === 'annotations')}
                                                                                    <span className="group-label">Type: {type}</span>
                                                                                    <span className="group-count">{seriesMap.size} series</span>
                                                                                </button>
                                                                            </td>
                                                                        </tr>

                                                                        {isOpenT &&
                                                                            Array.from(seriesMap.entries()).map(([seriesId, rows]) => {
                                                                                const sKey = `${patientId}::${studyId}::${type}::${seriesId}`;
                                                                                const isOpenS = !!openSeries[sKey];

                                                                                // Check if all items in this series are selected
                                                                                const seriesCompositeIds = rows.map((row) => getCompositeId(row));
                                                                                const isSeriesSelected = seriesCompositeIds.length > 0 && seriesCompositeIds.every((id) => selectedRowIds.includes(id));

                                                                                return (
                                                                                    <React.Fragment key={`s-${sKey}`}>
                                                                                        <tr className="group-row series-row">
                                                                                            <td>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    className="checkbox-series"
                                                                                                    checked={isSeriesSelected}
                                                                                                    onChange={() => handleSeriesSelect(rows)}
                                                                                                />
                                                                                            </td>
                                                                                            <td />
                                                                                            <td />
                                                                                            <td className="indent-3" colSpan={3}>
                                                                                                <button
                                                                                                    className="collapse-btn"
                                                                                                    onClick={() => toggleSeries(patientId, studyId, type, seriesId)}
                                                                                                    type="button"
                                                                                                >
                                                                                                    {isOpenS ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                                                                    <span className="group-label">Series: {seriesId}</span>
                                                                                                    <span className="group-count">
                                                                                                        {rows.length} format{rows.length !== 1 ? "s" : ""}
                                                                                                    </span>
                                                                                                </button>
                                                                                            </td>
                                                                                        </tr>

                                                                                        {isOpenS &&
                                                                                            rows.map((row) => (
                                                                                                <tr
                                                                                                    key={getCompositeId(row)}
                                                                                                    className={`data-row ${row.data_type === 'document' ? 'document-row' : 'image-row'} ${selectedRowIds.includes(getCompositeId(row)) ? 'selected' : ''}`}
                                                                                                >
                                                                                                    <td>
                                                                                                        <input
                                                                                                            type="checkbox"
                                                                                                            className="checkbox-item"
                                                                                                            checked={selectedRowIds.includes(getCompositeId(row))}
                                                                                                            onChange={() => handleRowSelect(row)}
                                                                                                        />
                                                                                                    </td>
                                                                                                    <td>{row.patient_id}</td>
                                                                                                    <td>{seriesId}</td>
                                                                                                    <td>
                                                                                                        <span className={`format-badge ${getFormatBadgeClass(row)}`}>
                                                                                                            {getDataFormat(row)}
                                                                                                        </span>
                                                                                                        {isRecentlyConverted(row) && (
                                                                                                            <span className="new-badge">NEW</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>{row.annotated?.toString?.() ?? row.annotation_status ?? "—"}</td>
                                                                                                    <td>{row.dataset_name || "—"}</td>
                                                                                                </tr>
                                                                                            ))}
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                    </React.Fragment>
                                                );
                                            })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};


const ViewerCard = ({ selectedDataIds = [], studyId, studyData = [], customViewers = [], allowedViewerTypes = [], canEdit = false, onViewerTypesChange, onRefreshData, viewAsUserId = null }) => {
    const [viewer, setViewer] = React.useState("");
    const [showConfigModal, setShowConfigModal] = React.useState(false);
    const [isLaunching, setIsLaunching] = React.useState(false);

    // All available system viewer types
    const systemViewers = [
        { value: "dv2", label: "DV2 (DICOM + Image + Document)", category: "Multimodal Viewers" },
        { value: "discovery-viewer-test", label: "Discovery Viewer Test (Image + Text)", category: "Multimodal Viewers" },
        { value: "image-viewer", label: "Image Viewer (JPG/PNG)", category: "Standard Images" },
        { value: "ohif", label: "OHIF Basic Viewer", category: "DICOM Viewers" },
        { value: "ohif-segmentation", label: "OHIF Segmentation", category: "DICOM Viewers" },
        { value: "ohif-3d", label: "OHIF 3D", category: "DICOM Viewers" },
        { value: "ohif-microscopy", label: "OHIF Microscopy", category: "DICOM Viewers" },
        { value: "trame-slicer", label: "3D Slicer", category: "DICOM Viewers" },
        { value: "ehr-viewer", label: "EHR Viewer (JSON/CSV)", category: "Clinical Data" },
        { value: "signal-viewer", label: "Signal Viewer (ECG/EEG)", category: "Clinical Data" },
        { value: "document-viewer", label: "Document Viewer (PDF/Text)", category: "Documents" },
        { value: "pme", label: "PME Pathology Viewer (WSI)", category: "Pathology" },
        { value: "labelstudio", label: "Label Studio (Annotation)", category: "Annotation Tools" },
        { value: "cvat", label: "CVAT (Video Annotation)", category: "Annotation Tools" },
    ];

    // Check if a viewer type is allowed (empty array means all allowed)
    const isViewerAllowed = (viewerType) => {
        if (!allowedViewerTypes || allowedViewerTypes.length === 0) return true;
        return allowedViewerTypes.includes(viewerType);
    };

    // Group allowed system viewers by category
    const getFilteredViewersByCategory = () => {
        const filtered = systemViewers.filter(v => isViewerAllowed(v.value));
        const grouped = {};
        filtered.forEach(v => {
            if (!grouped[v.category]) grouped[v.category] = [];
            grouped[v.category].push(v);
        });
        return grouped;
    };

    const handleLaunchViewer = async () => {
        if (!viewer) return;

        // Prevent double-clicking
        if (isLaunching) {
            console.log('[StudyDashboard] Launch already in progress, ignoring');
            return;
        }

        // Check if any items are selected - applies to ALL viewer types
        if (selectedDataIds.length === 0) {
            toast.warning("Please select at least one patient to visualize.");
            return;
        }

        setIsLaunching(true);

        // Check if this is a custom viewer (starts with "custom-")
        if (viewer.startsWith("custom-")) {

            const viewerId = parseInt(viewer.replace("custom-", ""));
            const customViewer = customViewers.find(v => v.id === viewerId);

            if (!customViewer) {
                toast.error("Custom viewer not found.");
                setIsLaunching(false);
                return;
            }

            // Scrollable-layout custom viewers (built in the Viewer Creation Lab)
            // use the schema-driven runtime at /DV_Viewer, not the legacy
            // /Custom_Viewer page. Route there and skip the legacy URL building.
            if (customViewer.config?.layout_mode === "scrollable") {
                const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));
                const patientIds = [...new Set(selectedItems.map(item => item.patient_id || 'Unknown'))];
                const patientId = patientIds[0] || '';
                if (!patientId) {
                    toast.warning("Please select a patient to open in this viewer.");
                    setIsLaunching(false);
                    return;
                }
                const params = new URLSearchParams();
                params.append('custom_viewer_id', viewerId);
                params.append('study_id', studyId);
                params.append('patient_id', patientId);
                window.location.href = `/DV_Viewer?${params.toString()}`;
                setIsLaunching(false);
                return;
            }

            // Get selected items (using composite IDs: id_sourceModel)
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Get all unique patient IDs from selection (preserve selection order)
            const patientIds = [...new Set(selectedItems.map(item => item.patient_id || 'Unknown'))];
            const patientId = patientIds.length > 0 ? patientIds[0] : '';

            // Build URL parameters for the Custom Viewer
            const params = new URLSearchParams();
            params.append('study_id', studyId);
            params.append('patient_id', patientId);
            // Add all patient IDs for multi-patient navigation
            if (patientIds.length > 1) {
                params.append('patient_ids', patientIds.join(','));
            }
            params.append('custom_viewer_id', viewerId);
            params.append('custom_viewer_config', JSON.stringify(customViewer.config || {}));
            // Pass view_as_user_id for admin viewing as another user
            if (viewAsUserId) {
                params.append('view_as_user_id', viewAsUserId);
            }

            // Group data by patient AND by type for multi-patient navigation
            const patientDataMap = {};

            selectedItems.forEach(item => {
                const pid = item.patient_id || 'Unknown';
                if (!patientDataMap[pid]) {
                    patientDataMap[pid] = { dicom: [], image: [], document: [], signal: [], video: [], ehr: [] };
                }

                if (item.data_type === 'ehr') {
                    patientDataMap[pid].ehr.push(`${item.id}:${item.source_model || 'PatientData'}`);
                } else if (item.data_type === 'video') {
                    patientDataMap[pid].video.push(`${item.id}:${item.source_model || 'PatientData'}`);
                } else if (item.data_type === 'document') {
                    patientDataMap[pid].document.push(`${item.id}:${item.source_model || 'MedicalDocument'}`);
                } else if (item.data_type === 'signal') {
                    patientDataMap[pid].signal.push(`${item.id}:${item.source_model || 'PatientData'}`);
                } else if (item.data_type === 'image' || item.data_type === 'imaging') {
                    const filePath = (item.image || item.file || item.location || '').toLowerCase();
                    const regularImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
                    const isRegularImage = regularImageExtensions.some(ext => filePath.endsWith(ext));
                    const isDicom = !isRegularImage && (item.data_type === 'imaging' || filePath.endsWith('.dcm'));

                    if (isDicom) {
                        patientDataMap[pid].dicom.push(`${item.id}:${item.source_model || 'MedicalImage'}`);
                    } else {
                        patientDataMap[pid].image.push(`${item.id}:${item.source_model || 'MedicalImage'}`);
                    }
                }
            });

            // Auto-include documents for selected patients even if not explicitly selected.
            // Documents often lack a study_id so they appear under "Unknown Study" in the
            // hierarchy and won't be selected when clicking a study-level checkbox.
            // The custom viewer is designed to show all data types for a patient together.
            const selectedDocIds = new Set(selectedItems.filter(i => i.data_type === 'document').map(i => i.id));
            const selectedEhrIds = new Set(selectedItems.filter(i => i.data_type === 'ehr').map(i => i.id));
            patientIds.forEach(pid => {
                if (!patientDataMap[pid]) {
                    patientDataMap[pid] = { dicom: [], image: [], document: [], signal: [], video: [], ehr: [] };
                }
                // Auto-include documents
                const patientDocs = studyData.filter(item =>
                    (item.patient_id || 'Unknown') === pid &&
                    item.data_type === 'document' &&
                    !selectedDocIds.has(item.id)
                );
                if (patientDocs.length > 0) {
                    patientDocs.forEach(doc => {
                        patientDataMap[pid].document.push(`${doc.id}:${doc.source_model || 'MedicalDocument'}`);
                    });
                }
                // Auto-include EHR data
                const patientEhr = studyData.filter(item =>
                    (item.patient_id || 'Unknown') === pid &&
                    item.data_type === 'ehr' &&
                    !selectedEhrIds.has(item.id)
                );
                if (patientEhr.length > 0) {
                    patientEhr.forEach(ehr => {
                        patientDataMap[pid].ehr.push(`${ehr.id}:${ehr.source_model || 'PatientData'}`);
                    });
                }
            });

            // Add current patient's data params (for initial load)
            const currentPatientData = patientDataMap[patientId] || { dicom: [], image: [], document: [], signal: [], video: [], ehr: [] };
            if (currentPatientData.dicom.length > 0) {
                params.append('dicom_data', currentPatientData.dicom.join(','));
            }
            if (currentPatientData.image.length > 0) {
                params.append('image_data', currentPatientData.image.join(','));
            }
            if (currentPatientData.document.length > 0) {
                params.append('document_data', currentPatientData.document.join(','));
            }
            if (currentPatientData.signal.length > 0) {
                params.append('signal_data', currentPatientData.signal.join(','));
            }
            if (currentPatientData.video.length > 0) {
                params.append('video_data', currentPatientData.video.join(','));
            }
            if (currentPatientData.ehr.length > 0) {
                params.append('ehr_data', currentPatientData.ehr.join(','));
            }

            // Store patient data map in sessionStorage to avoid URI too large error
            // Use a unique key based on study ID
            if (patientIds.length > 1) {
                const storageKey = `patient_data_map_${studyId}`;
                sessionStorage.setItem(storageKey, JSON.stringify(patientDataMap));
                params.append('patient_data_storage_key', storageKey);
            }

            // Open the Custom Viewer with all data
            window.location.href = `/Custom_Viewer?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle Image Viewer separately (no backend call needed)
        if (viewer === "image-viewer") {
            if (selectedDataIds.length === 0) {
                toast.warning("Please select at least one image to view.");
                setIsLaunching(false);
                return;
            }

            // Get selected items and filter for images only (exclude DICOM and documents)
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));
            const imageItems = selectedItems.filter(item => {
                // Only include regular images (JPG/PNG), not DICOM or documents
                const isDocument = item.data_type === 'document';
                const isDicom = item.data_type === 'imaging' && item.original_file_type === 'dicom';
                const isImage = item.data_type === 'image' ||
                    (item.data_type === 'imaging' && item.original_file_type !== 'dicom');
                return !isDocument && !isDicom && isImage;
            });

            if (imageItems.length === 0) {
                toast.warning("No viewable images selected. Please select JPG/PNG images.");
                setIsLaunching(false);
                return;
            }

            // Build URL with source model info
            const imageData = imageItems.map(item =>
                `${item.id}:${item.source_model || 'MedicalImage'}`
            ).join(',');
            window.location.href = `/Image_Viewer?image_data=${imageData}&study_id=${studyId}`;
            setIsLaunching(false);
            return;
        }

        // Handle Discovery Viewer Test - requires both image and document for selected patient
        if (viewer === "discovery-viewer-test") {
            if (selectedDataIds.length === 0) {
                toast.warning("Please select at least one item to view.");
                setIsLaunching(false);
                return;
            }

            // Get the selected items from studyData
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Group by patient, separating by data type and source model
            // Discovery Viewer Test is for regular images (JPG/PNG) + documents, NOT DICOM
            const patientData = {};
            selectedItems.forEach(item => {
                const patientId = item.patient_id || 'Unknown';
                if (!patientData[patientId]) {
                    patientData[patientId] = { images: [], documents: [] };
                }
                // Check if it's a document type (including PatientData documents)
                const isDocument = item.data_type === 'document';

                // Check if it's a regular image (JPG/PNG) - exclude DICOM
                const filePath = (item.image || item.file || item.location || '').toLowerCase();
                const regularImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
                const isRegularImage = regularImageExtensions.some(ext => filePath.endsWith(ext));
                // Also accept 'image' data_type that's not DICOM
                const isImageDataType = item.data_type === 'image' && !item.original_file_type?.includes('dicom');

                if (isDocument) {
                    patientData[patientId].documents.push(item);
                } else if (isRegularImage || isImageDataType) {
                    patientData[patientId].images.push(item);
                }
            });

            // Find a patient with both images and documents
            const patientWithBoth = Object.entries(patientData).find(
                ([, data]) => data.images.length > 0 && data.documents.length > 0
            );

            if (!patientWithBoth) {
                toast.warning("Please select a patient that has both images and documents.");
                setIsLaunching(false);
                return;
            }

            const [patientId, data] = patientWithBoth;

            // Build URL params with source model info
            const params = new URLSearchParams();
            params.append('patient_id', patientId);
            params.append('study_id', studyId);

            // Add image info with source model (deduplicate by id:source combination)
            const seenImages = new Set();
            const imageInfos = data.images
                .map(img => `${img.id}:${img.source_model || 'MedicalImage'}`)
                .filter(info => {
                    if (seenImages.has(info)) return false;
                    seenImages.add(info);
                    return true;
                });
            params.append('image_data', imageInfos.join(','));

            // Add document info with source model (deduplicate by id:source combination)
            const seenDocs = new Set();
            const documentInfos = data.documents
                .map(doc => `${doc.id}:${doc.source_model || 'MedicalDocument'}`)
                .filter(info => {
                    if (seenDocs.has(info)) return false;
                    seenDocs.add(info);
                    return true;
                });
            params.append('document_data', documentInfos.join(','));

            window.location.href = `/Discovery_Viewer_Test?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle DV2 Viewer - requires DICOM, image, and document for selected patient
        if (viewer === "dv2") {
            if (selectedDataIds.length === 0) {
                toast.warning("Please select at least one item to view.");
                setIsLaunching(false);
                return;
            }

            // Get the selected items from studyData
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Group by patient, separating DICOM from regular images
            const patientData = {};
            selectedItems.forEach(item => {
                const patientId = item.patient_id || 'Unknown';
                if (!patientData[patientId]) {
                    patientData[patientId] = { dicomImages: [], regularImages: [], documents: [], signals: [] };
                }
                if (item.data_type === 'document') {
                    patientData[patientId].documents.push(item);
                } else if (item.data_type === 'signal') {
                    patientData[patientId].signals.push(item);
                } else if (item.data_type === 'image' || item.data_type === 'imaging') {
                    // Check if it's a regular image file (JPG, PNG, etc.) first
                    const filePath = (item.image || item.file || item.location || '').toLowerCase();
                    const regularImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
                    const isRegularImage = regularImageExtensions.some(ext => filePath.endsWith(ext));

                    // Check if it's DICOM based on:
                    // 1. data_type is 'imaging' with original_file_type 'dicom'
                    // 2. File extension (.dcm)
                    // 3. Path contains DICOM-related patterns
                    // 4. Type explicitly contains 'dicom'
                    // 5. Modality is a known DICOM modality
                    const isImagingDicom = item.data_type === 'imaging' && item.original_file_type === 'dicom';
                    const isDcmExtension = filePath.endsWith('.dcm');
                    const isDicomPath = filePath.includes('/dicom') || filePath.includes('nifti_to_dicom') || filePath.includes('rotated_');
                    const typeContainsDicom = item.type?.toLowerCase().includes('dicom');
                    const modality = (item.modality || '').toUpperCase();
                    const dicomModalities = ['CT', 'MR', 'MRI', 'X-RAY', 'PET', 'ULTRASOUND', 'US', 'NM', 'CR', 'DX', 'MG', 'PT', 'SEG', 'SC'];
                    const hasDicomModality = dicomModalities.includes(modality);

                    // Regular images take priority - if it has a known image extension, treat as regular
                    // DICOM files are identified by imaging type, .dcm extension, path, explicit type, or modality
                    const isDicom = !isRegularImage && (isImagingDicom || isDcmExtension || isDicomPath || typeContainsDicom || hasDicomModality);

                    if (isDicom) {
                        patientData[patientId].dicomImages.push(item);
                    } else {
                        patientData[patientId].regularImages.push(item);
                    }
                }
            });

            // Find first patient with data
            const patientEntry = Object.entries(patientData)[0];
            if (!patientEntry) {
                toast.warning("No valid patient data selected.");
                setIsLaunching(false);
                return;
            }

            const [patientId, data] = patientEntry;

            // Build URL parameters
            const params = new URLSearchParams();
            params.append('patient_id', patientId);
            params.append('study_id', studyId);

            if (data.dicomImages.length > 0) {
                // Include source model info for DICOM images: "id:source_model"
                const dicomData = data.dicomImages.map(img =>
                    `${img.id}:${img.source_model || 'MedicalImage'}`
                ).join(',');
                params.append('dicom_data', dicomData);
            }
            if (data.regularImages.length > 0) {
                // Include source model info for regular images: "id:source_model"
                const imageData = data.regularImages.map(img =>
                    `${img.id}:${img.source_model || 'MedicalImage'}`
                ).join(',');
                params.append('image_data', imageData);
            }
            if (data.documents.length > 0) {
                // Include source model info for documents: "id:source_model"
                const docData = data.documents.map(doc =>
                    `${doc.id}:${doc.source_model || 'MedicalDocument'}`
                ).join(',');
                params.append('document_data', docData);
            }
            if (data.signals.length > 0) {
                // Include source model info for signals: "id:source_model"
                const signalData = data.signals.map(sig =>
                    `${sig.id}:${sig.source_model || 'PatientData'}`
                ).join(',');
                params.append('signal_data', signalData);
            }

            window.location.href = `/DV2?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle EHR Viewer - displays JSON/CSV clinical data
        if (viewer === "ehr-viewer") {
            if (selectedDataIds.length === 0) {
                toast.warning("Please select at least one patient to view EHR data.");
                setIsLaunching(false);
                return;
            }

            // Get the selected items from studyData
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Filter for EHR data items
            const ehrItems = selectedItems.filter(item => item.data_type === 'ehr');

            if (ehrItems.length === 0) {
                toast.warning("No EHR data found in selection. Please select patients with EHR records.");
                setIsLaunching(false);
                return;
            }

            // Get unique patient IDs
            const patientIds = [...new Set(ehrItems.map(item => item.patient_id || 'Unknown'))];

            // Build URL parameters
            const params = new URLSearchParams();
            params.append('study_id', studyId);
            params.append('patient_ids', patientIds.join(','));

            // Add EHR data IDs with source model info
            const ehrData = ehrItems.map(item =>
                `${item.id}:${item.source_model || 'PatientData'}`
            ).join(',');
            params.append('ehr_data', ehrData);

            window.location.href = `/EHR_Viewer?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle Signal Viewer - displays ECG/EEG waveform data
        if (viewer === "signal-viewer") {
            if (selectedDataIds.length === 0) {
                toast.warning("Please select at least one patient to view signal data.");
                setIsLaunching(false);
                return;
            }

            // Get the selected items from studyData
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Filter for signal data items
            const signalItems = selectedItems.filter(item => item.data_type === 'signal');

            if (signalItems.length === 0) {
                toast.warning("No signal data found in selection. Please select patients with signal recordings.");
                setIsLaunching(false);
                return;
            }

            // Get unique patient IDs
            const patientIds = [...new Set(signalItems.map(item => item.patient_id || 'Unknown'))];

            // Build URL parameters
            const params = new URLSearchParams();
            params.append('study_id', studyId);
            params.append('patient_ids', patientIds.join(','));

            // Add signal data IDs with source model info
            const signalData = signalItems.map(item =>
                `${item.id}:${item.source_model || 'PatientData'}`
            ).join(',');
            params.append('signal_data', signalData);

            window.location.href = `/Signal_Viewer?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle Document Viewer - dedicated multi-panel viewer with a source
        // sidebar (documents grouped by inferred category) and drag-and-drop
        // placement into 1-4 panels.
        if (viewer === "document-viewer") {
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));
            const patientIdsList = [...new Set(selectedItems.map(item => item.patient_id || 'Unknown'))];
            const patientId = patientIdsList[0] || 'Unknown';

            // Collect documents across all selected patients, auto-including any
            // documents attached to those patients that aren't explicitly selected
            // (documents commonly live under "Unknown Study" and get missed).
            const patientDocsMap = {};
            patientIdsList.forEach(pid => {
                const docs = studyData.filter(item =>
                    (item.patient_id || 'Unknown') === pid &&
                    item.data_type === 'document'
                );
                if (docs.length > 0) {
                    patientDocsMap[pid] = docs.map(d => `${d.id}:${d.source_model || 'MedicalDocument'}`);
                }
            });

            const currentPatientDocs = patientDocsMap[patientId] || [];
            if (currentPatientDocs.length === 0 && Object.keys(patientDocsMap).length === 0) {
                toast.warning("No documents found for the selected patients.");
                setIsLaunching(false);
                return;
            }

            const params = new URLSearchParams();
            params.append('study_id', studyId);
            params.append('patient_id', patientId);
            if (patientIdsList.length > 1) {
                params.append('patient_ids', patientIdsList.join(','));
            }
            if (currentPatientDocs.length > 0) {
                params.append('document_data', currentPatientDocs.join(','));
            }
            if (patientIdsList.length > 1) {
                const storageKey = `patient_data_map_${studyId}_docviewer`;
                const dataMap = {};
                patientIdsList.forEach(pid => {
                    dataMap[pid] = {
                        dicom: [], image: [], signal: [], video: [], ehr: [],
                        document: patientDocsMap[pid] || []
                    };
                });
                sessionStorage.setItem(storageKey, JSON.stringify(dataMap));
                params.append('patient_data_storage_key', storageKey);
            }
            if (viewAsUserId) {
                params.append('view_as_user_id', viewAsUserId);
            }

            window.location.href = `/Document_Viewer?${params.toString()}`;
            setIsLaunching(false);
            return;
        }

        // Handle PME Pathology Viewer - opens WSI slide for selected patient
        // Route through DV_Viewer to get the standard ViewerNavbar (labels, annotations, etc.)
        if (viewer === "pme") {
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));
            // Only WSI slide formats are openable in PME — exclude maps/overlays like .geojson, .csv
            const wsiExtensions = ['.svs', '.tiff', '.tif', '.ndpi'];
            const pathologyItems = selectedItems.filter(item => {
                if (item.data_type !== 'pathology') return false;
                if (item.is_map || item.is_annotation) return false;
                const fp = (item.file || item.filename || '').toLowerCase();
                return wsiExtensions.some(ext => fp.endsWith(ext));
            });

            if (pathologyItems.length === 0) {
                toast.warning("No pathology slides found in selection. Please select patients with WSI files (.svs, .tiff).");
                setIsLaunching(false);
                return;
            }

            // Helper: map serialized file URL to PME container path
            // DRF serializes FileField as URL: /media/private/users/.../file.svs
            // nginx alias: /media/X → /data/private/X (so /media/private/... → /data/private/private/...)
            // PME volume: ./data → /discovery_data
            // Therefore: strip /media/, prepend /discovery_data/private/
            const toContainerPath = (fp) => {
                const clean = fp.replace(/^\/?media\//, '');
                return `/discovery_data/private/${clean}`;
            };

            // Find all WSI slides for the same patient to populate the slide list
            const patientId = pathologyItems[0].patient_id || 'Unknown';
            const allPatientPathology = studyData.filter(item => {
                if (item.data_type !== 'pathology' || item.patient_id !== patientId) return false;
                if (item.is_map || item.is_annotation) return false;
                const fp = (item.file || item.filename || '').toLowerCase();
                return wsiExtensions.some(ext => fp.endsWith(ext));
            });

            // Build slides array for the left panel
            const slides = allPatientPathology.map(item => {
                const fp = item.file || item.location || '';
                const containerPath = toContainerPath(fp);
                // Extract filename without extension for display name
                const fileName = fp.split('/').pop() || '';
                const name = fileName.replace(/\.[^.]+$/, '') || `Slide`;
                return {
                    name,
                    file: containerPath,
                    stain: item.stain || item.metadata?.stain || '',
                };
            });

            // Find maps associated with pathology items for this patient
            const patientMaps = studyData.filter(
                item => item.is_map && item.patient_id === patientId
            );
            const maps = patientMaps.map(item => {
                const fp = item.file || item.location || '';
                const containerPath = toContainerPath(fp);
                const fileName = fp.split('/').pop() || '';
                const name = fileName.replace(/\.[^.]+$/, '') || 'Map';
                return {
                    name,
                    file: containerPath,
                    parent_data_id: item.metadata?.parent_data_id || null,
                    parent_filename: item.metadata?.parent_data_filename || '',
                };
            });

            const firstFile = pathologyItems[0].file || pathologyItems[0].location;
            const containerPath = toContainerPath(firstFile);
            const slidesParam = encodeURIComponent(JSON.stringify(slides));
            const mapsParam = maps.length > 0 ? encodeURIComponent(JSON.stringify(maps)) : '';
            let pmeUrl = `/pme/view_slide?file=${encodeURIComponent(containerPath)}&slides=${slidesParam}`;
            if (mapsParam) pmeUrl += `&maps=${mapsParam}`;

            // Open through DV_Viewer wrapper to get the standard DV ViewerNavbar
            const viewerPageUrl = `/DV_Viewer?viewer_url=${encodeURIComponent(pmeUrl)}&study_id=${studyId}&patient_id=${encodeURIComponent(patientId)}`;
            window.location.href = viewerPageUrl;
            setIsLaunching(false);
            return;
        }

        // Label Studio viewer — auto-provision, create project, sync data, and open
        if (viewer === "labelstudio") {
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            // Build data_ids payload from selected items
            const dataIds = selectedItems.map(item => ({
                id: item.id,
                source_model: item.source_model || 'PatientData',
                data_type: item.data_type,
            }));

            // Detect dominant data type for labeling config
            const typeCounts = {};
            selectedItems.forEach(item => {
                const dt = item.data_type || 'image';
                typeCounts[dt] = (typeCounts[dt] || 0) + 1;
            });
            const dominantType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'image';
            // Map to LS-supported types
            const lsTypeMap = { 'imaging': 'image', 'pathology': 'image' };
            const lsDataType = lsTypeMap[dominantType] || dominantType;

            const loadingToastId = toast.info('Preparing Label Studio...', { autoClose: false });

            try {
                const resp = await API.post('/labelstudio/launch/', {
                    study_id: parseInt(studyId),
                    data_ids: dataIds,
                    data_type: lsDataType,
                });

                toast.dismiss(loadingToastId);

                const lsProjectId = resp.data.ls_project_id;
                const taskCount = resp.data.task_count;
                const lsBaseUrl = `${window.location.origin}/labelstudio`;

                if (taskCount > 0) {
                    window.open(`${lsBaseUrl}/projects/${lsProjectId}/`, '_blank');
                    toast.success(`Label Studio opened with ${taskCount} task(s)`);
                } else {
                    toast.warning('No compatible data items found for Label Studio. Only non-DICOM images, signals, documents, and videos are supported.');
                }
            } catch (err) {
                toast.dismiss(loadingToastId);
                const errorMessage = err.response?.data?.error || 'Failed to launch Label Studio';
                toast.error(errorMessage);
            }

            setIsLaunching(false);
            return;
        }

        // CVAT viewer — auto-provision, create project, sync video data, and open
        if (viewer === "cvat") {
            const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

            const dataIds = selectedItems.map(item => ({
                id: item.id,
                source_model: item.source_model || 'PatientData',
                data_type: item.data_type,
            }));

            const loadingToastId = toast.info('Preparing CVAT...', { autoClose: false });

            try {
                const resp = await API.post('/cvat/launch/', {
                    study_id: parseInt(studyId),
                    data_ids: dataIds,
                    data_type: 'video',
                });

                toast.dismiss(loadingToastId);

                const taskId = resp.data.cvat_task_id;
                const jobId = resp.data.cvat_job_id;
                const taskCount = resp.data.task_count;
                const cvatPort = '19444';
                const cvatBaseUrl = `https://${window.location.hostname}:${cvatPort}`;

                if (taskCount > 0 && taskId) {
                    const targetPath = jobId
                        ? `/tasks/${taskId}/jobs/${jobId}`
                        : `/tasks/${taskId}`;
                    // Auto-login: redirect through Discovery auth endpoint on CVAT port
                    // Sets CVAT session cookies using the orthanc_auth JWT cookie
                    const cvatUrl = `${cvatBaseUrl}/discovery-auth/?next=${encodeURIComponent(targetPath)}`;
                    window.open(cvatUrl, '_blank');
                    toast.success(`CVAT opened with ${taskCount} task(s)`);
                } else {
                    toast.warning('No compatible data items found for CVAT.');
                }
            } catch (err) {
                toast.dismiss(loadingToastId);
                const errorMessage = err.response?.data?.error || 'Failed to launch CVAT';
                toast.error(errorMessage);
            }

            setIsLaunching(false);
            return;
        }

        // For standard DICOM viewers (ohif, ohif-segmentation, etc.)
        // Filter selected items to only include DICOM data
        if (selectedDataIds.length === 0) {
            toast.warning("Please select at least one DICOM image to view.");
            setIsLaunching(false);
            return;
        }

        const selectedItems = studyData.filter(item => selectedDataIds.includes(getCompositeId(item)));

        // Filter for DICOM-compatible images (includes NIfTI that was converted to DICOM)
        const dicomItems = selectedItems.filter(item => {
            if (item.data_type === 'document') return false;

            const filePath = (item.image || item.file || item.location || '').toLowerCase();
            const regularImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
            const isRegularImage = regularImageExtensions.some(ext => filePath.endsWith(ext));

            // DICOM detection logic - includes native DICOM and NIfTI converted to DICOM
            const isImagingDicom = item.data_type === 'imaging' && item.original_file_type === 'dicom';
            const isNiftiConverted = item.data_type === 'imaging' && item.original_file_type === 'nifti';
            // Image-slice datasets (jpg/png inside imaging/) are converted to DICOM at
            // launch time by the backend, so let them through here.
            const isImagingJpgPng = item.data_type === 'imaging' && ['jpg', 'jpeg', 'png'].includes(item.original_file_type);
            const isDcmExtension = filePath.endsWith('.dcm');
            const isDicomPath = filePath.includes('/dicom') || filePath.includes('nifti_to_dicom') || filePath.includes('rotated_') || filePath.includes('/nifti/');
            const typeContainsDicom = item.type?.toLowerCase().includes('dicom');
            const modality = (item.modality || '').toUpperCase();
            const dicomModalities = ['CT', 'MR', 'MRI', 'X-RAY', 'PET', 'ULTRASOUND', 'US', 'NM', 'CR', 'DX', 'MG', 'PT', 'SEG', 'SC'];
            const hasDicomModality = dicomModalities.includes(modality);
            // Check if metadata contains medical_image_id (NIfTI uploads have this)
            const hasMedicalImageId = item.metadata?.medical_image_id;

            // Imaging jpg/png bypass the regular-image extension filter (they get converted server-side).
            return isImagingJpgPng || (!isRegularImage && (isImagingDicom || isNiftiConverted || isDcmExtension || isDicomPath || typeContainsDicom || hasDicomModality || hasMedicalImageId));
        });

        if (dicomItems.length === 0) {
            toast.error("No DICOM images found in selection. Please select DICOM data to use this viewer.");
            return;
        }

        // Get all unique patient IDs from the selected DICOM items (preserve order)
        const allPatientIds = [...new Set(dicomItems.map(item => item.patient_id).filter(Boolean))];
        const patientId = allPatientIds[0] || 'Unknown';

        // Declare loadingToast outside try block so it's accessible in finally
        let loadingToast = null;
        let needsDataRefresh = false;

        try {
            // Check if any NIfTI files need conversion first
            const niftiItems = dicomItems.filter(item =>
                item.original_file_type === 'nifti' ||
                item.filename?.endsWith('.nii') ||
                item.filename?.endsWith('.nii.gz')
            );

            if (niftiItems.length > 0) {
                // Check conversion status first
                loadingToast = toast.loading(
                    `Checking ${niftiItems.length} NIfTI file(s)...`,
                    { autoClose: false }
                );

                const statusResponse = await API.post("/viewers/check_conversion_status/", {
                    data_ids: niftiItems.map(item => item.id),
                    trigger_conversion: false
                });

                const summary = statusResponse.data.summary || {};
                const readyInOrthanc = summary.ready_in_orthanc || 0;
                const needsConversion = summary.needs_conversion || 0;
                const converted = summary.converted || 0;

                // Show status summary
                if (readyInOrthanc === niftiItems.length) {
                    // All files already in Orthanc - fast path!
                    // Dismiss the checking toast and show a brief success message
                    toast.dismiss(loadingToast);
                    toast.success(`All ${readyInOrthanc} file(s) ready in Orthanc`, { autoClose: 1500 });
                    loadingToast = null;
                } else if (statusResponse.data.needs_conversion) {
                    // Show converting toast with count
                    toast.update(loadingToast, {
                        render: `Converting ${needsConversion} NIfTI file(s) to DICOM... This may take a moment for large files.`,
                        type: "info",
                        isLoading: true
                    });
                    needsDataRefresh = true;

                    try {
                        // Kick off async conversion. The endpoint returns a task_id immediately;
                        // the heavy work runs in a background thread and progress is polled below.
                        const startResponse = await API.post("/viewers/batch_convert_nifti/", {
                            study_id: studyId,
                            data_ids: niftiItems.map(item => item.id),
                        });

                        if (startResponse.data.status === 'completed') {
                            // Nothing actually needed conversion (e.g. another tab already did it).
                            toast.update(loadingToast, {
                                render: startResponse.data.message || 'Conversion complete! Preparing viewer...',
                                type: "info",
                                isLoading: true
                            });
                        } else {
                            const conversionTaskId = startResponse.data.task_id;
                            if (!conversionTaskId) {
                                throw new Error('Conversion start returned no task_id');
                            }

                            // Poll until the worker thread reports completion / error / stopped.
                            const pollIntervalMs = 1000;
                            const maxAttempts = 60 * 30;  // 30 minutes ceiling
                            let firstError = null;
                            let finalStatus = null;
                            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                                let progressResp;
                                try {
                                    progressResp = await API.get(
                                        `/viewers/get_batch_conversion_progress/?task_id=${conversionTaskId}`
                                    );
                                } catch (pollErr) {
                                    if (pollErr.response?.status === 404) {
                                        // Task evicted from cache after completion - treat as done.
                                        finalStatus = 'completed';
                                        break;
                                    }
                                    throw pollErr;
                                }

                                const progress = progressResp.data || {};
                                const done = progress.converted || 0;
                                const total = progress.total || needsConversion;
                                const currentFile = progress.current_file || '';
                                toast.update(loadingToast, {
                                    render: `Converting ${done}/${total}${currentFile ? ` — ${currentFile}` : ''}`,
                                    type: "info",
                                    isLoading: true
                                });

                                if (progress.status === 'completed') {
                                    finalStatus = 'completed';
                                    if (progress.errors && progress.errors.length > 0) {
                                        firstError = progress.errors[0];
                                    }
                                    break;
                                }
                                if (progress.status === 'error' || progress.status === 'stopped') {
                                    finalStatus = progress.status;
                                    if (progress.errors && progress.errors.length > 0) {
                                        firstError = progress.errors[0];
                                    }
                                    break;
                                }

                                await new Promise(r => setTimeout(r, pollIntervalMs));
                            }

                            if (finalStatus !== 'completed') {
                                toast.update(loadingToast, {
                                    render: firstError
                                        ? `Conversion failed: ${firstError.error || firstError}`
                                        : `Conversion did not complete (status: ${finalStatus || 'timeout'})`,
                                    type: "error",
                                    isLoading: false,
                                    autoClose: 5000
                                });
                                return;
                            }

                            if (firstError) {
                                toast.update(loadingToast, {
                                    render: `Conversion finished with errors: ${firstError.error || firstError}`,
                                    type: "warning",
                                    isLoading: false,
                                    autoClose: 5000
                                });
                                return;
                            }
                        }

                        // Update toast - conversion done
                        toast.update(loadingToast, {
                            render: "Conversion complete! Preparing viewer...",
                            type: "info",
                            isLoading: true
                        });
                    } catch (convErr) {
                        toast.update(loadingToast, {
                            render: `Conversion error: ${convErr.message}`,
                            type: "error",
                            isLoading: false,
                            autoClose: 5000
                        });
                        return;
                    }
                } else if (converted > 0) {
                    // Files converted but need upload to Orthanc
                    toast.update(loadingToast, {
                        render: `Uploading ${converted} file(s) to Orthanc...`,
                        type: "info",
                        isLoading: true
                    });
                }
            }

            // Show loading toast if not already showing (for already converted files)
            if (!loadingToast) {
                loadingToast = toast.loading(
                    `Preparing viewer...`,
                    { autoClose: false }
                );
            }

            // Now launch the viewer
            // Send data_items with id and source_model to prevent ID collisions
            // Group items by source_model to send separate lists for MedicalImage and PatientData
            // IMPORTANT: For grouped DICOM series, expand grouped_item_ids (can be on either MedicalImage or PatientData)
            // MedicalImage items may have grouped_item_ids when deduplicated from PatientData
            const medicalImageIds = dicomItems
                .filter(item => item.source_model === 'MedicalImage')
                .map(item => item.id);

            // For PatientData items, check for grouped_item_ids and expand them
            // Also check MedicalImage items for grouped_item_ids (copied during deduplication)
            const patientDataIds = dicomItems
                .flatMap(item => {
                    // If this is a grouped DICOM series (from CLI upload), use all the individual file IDs
                    // This can be on PatientData entries or MedicalImage entries that were merged during deduplication
                    if (item.grouped_item_ids && Array.isArray(item.grouped_item_ids)) {
                        console.log(`[StudyDashboard] Expanding grouped series ${item.series_id}: ${item.grouped_item_ids.length} files`);
                        return item.grouped_item_ids;
                    }
                    // For PatientData without grouped_item_ids, include the single ID
                    // For MedicalImage, the ID is already in medicalImageIds, so skip
                    if (item.source_model === 'PatientData') {
                        return [item.id];
                    }
                    return [];
                });

            // Debug: Log exactly what we're sending to the backend
            console.log('[StudyDashboard] Launching viewer with:', {
                selectedDataIds: selectedDataIds,
                selectedItemsCount: selectedItems.length,
                dicomItemsCount: dicomItems.length,
                medicalImageIds: medicalImageIds,
                patientDataIds: patientDataIds,
                dicomItemPatients: [...new Set(dicomItems.map(item => item.patient_id))],
            });

            // Show/update loading toast for viewer launch
            if (!loadingToast) {
                loadingToast = toast.loading(
                    `Launching viewer for ${allPatientIds.length} patient(s)...`,
                    { autoClose: false }
                );
            } else {
                toast.update(loadingToast, {
                    render: `Launching viewer for ${allPatientIds.length} patient(s)...`,
                    type: "info",
                    isLoading: true
                });
            }

            const requestData = {
                viewer_name: viewer,
                data_ids: medicalImageIds,  // MedicalImage IDs
                patient_data_ids: patientDataIds,  // PatientData IDs
                study_id: studyId,  // Pass study_id for annotation isolation (per-user DICOM copies when share_annotations=False)
            };
            // If viewing as another user, pass their user ID to show their segmentations
            if (viewAsUserId) {
                requestData.view_as_user_id = viewAsUserId;
            }
            const response = await API.post("/viewers/launch_viewer/", requestData);

            // Debug: Log backend response to verify patient_ids and study_uids match
            console.log('[StudyDashboard] Backend response:', {
                patient_ids: response.data.patient_ids,
                study_uids: response.data.study_uids,
                frontend_allPatientIds: allPatientIds
            });

            // Use patient_id from selected items (more reliable than backend response)
            const patientIdForViewer = encodeURIComponent(patientId);
            const imageIdForViewer = response.data.image_id || dicomItems[0].id || '';

            // Build URL with multi-patient support
            const viewerUrl = encodeURIComponent(response.data.viewer_url);
            let viewerPageUrl = `/DV_Viewer?viewer_url=${viewerUrl}&study_id=${studyId}&patient_id=${patientIdForViewer}&image_id=${imageIdForViewer}`;

            // IMPORTANT: Use patient_ids from backend response, NOT from frontend selection
            // This ensures perfect 1:1 correspondence between patient_ids and study_uids
            // The backend builds both arrays in the same order from the same images list
            const backendPatientIds = response.data.patient_ids || [];
            if (backendPatientIds.length > 1) {
                viewerPageUrl += `&patient_ids=${encodeURIComponent(backendPatientIds.join(','))}`;
            }

            // Add study UIDs if returned by backend (must match patient_ids order)
            if (response.data.study_uids && response.data.study_uids.length > 0) {
                viewerPageUrl += `&study_uids=${encodeURIComponent(response.data.study_uids.join(','))}`;
            }

            // Trame-slicer multi-patient: each patient has a distinct URL (different --data
            // path). Store the URL list in sessionStorage so DvViewer can swap the iframe
            // src on navigation without blowing out the URL length limit.
            const trameUrls = response.data.trame_urls || [];
            if (trameUrls.length > 1) {
                const trameSessionKey = `trame_${Date.now()}`;
                sessionStorage.setItem(trameSessionKey, JSON.stringify(trameUrls));
                viewerPageUrl += `&trame_session_key=${encodeURIComponent(trameSessionKey)}`;
            }

            // Ensure orthanc_auth cookie is fresh before opening viewer
            // This prevents 401 errors when OHIF tries to load resources
            try {
                await ensureOrthancAuthCookie();
            } catch (authErr) {
                console.warn('[StudyDashboard] Could not refresh auth cookie:', authErr);
                // Continue anyway - the viewer might still work if the cookie is valid
            }

            // Dismiss the loading toast BEFORE opening the viewer window
            if (loadingToast) {
                toast.dismiss(loadingToast);
                loadingToast = null;
            }

            // Small delay to let React process the toast dismiss before tab switch
            await new Promise(r => setTimeout(r, 100));

            // Navigate to the viewer in the same window (preserves auth state)
            window.location.href = viewerPageUrl;

            // Show warning if some patients' data couldn't be loaded
            if (response.data.warning || response.data.missing_patients) {
                const missingCount = response.data.missing_patients?.length || 0;
                const includedCount = response.data.patient_ids?.length || 0;
                toast.warn(
                    `${includedCount} patient(s) loaded. ${missingCount} patient(s) excluded due to data upload issues.`,
                    { autoClose: 5000 }
                );
            }

            // Refresh study data if conversion happened to show new DICOM series
            if (needsDataRefresh && onRefreshData) {
                onRefreshData();
            }
        } catch (err) {
            console.error("Error launching viewer:", err);
            const errorMessage = err.response?.data?.error || "Failed to launch viewer.";
            toast.error(errorMessage);
        } finally {
            // Always ensure loading toast is dismissed and reset launching state
            if (loadingToast) {
                toast.dismiss(loadingToast);
            }
            setIsLaunching(false);
        }
    };

    // All viewer values including custom viewers
    const allViewerValues = [
        ...systemViewers.map(v => v.value),
        ...customViewers.map(v => `custom-${v.id}`)
    ];

    // Handle toggling viewer type in configuration
    const handleToggleViewerType = async (viewerType) => {
        if (!onViewerTypesChange) return;

        let newAllowedTypes;
        if (allowedViewerTypes.length === 0) {
            // If currently all are allowed, switch to all except this one
            newAllowedTypes = allViewerValues.filter(v => v !== viewerType);
        } else if (allowedViewerTypes.includes(viewerType)) {
            // Remove this viewer type
            newAllowedTypes = allowedViewerTypes.filter(v => v !== viewerType);
        } else {
            // Add this viewer type
            newAllowedTypes = [...allowedViewerTypes, viewerType];
        }

        // If all are selected, clear the array (means all allowed)
        if (newAllowedTypes.length === allViewerValues.length) {
            newAllowedTypes = [];
        }

        onViewerTypesChange(newAllowedTypes);
    };

    // Get filtered viewers by category for dropdown
    const filteredViewersByCategory = getFilteredViewersByCategory();

    return (
        <div className="viewer-card">
            <div className="viewer-header">
                <Monitor className="viewer-icon" />
                <div>
                    <h3>Launch Viewer</h3>
                    <p className="viewer-subtitle">Select a viewer to visualize your data</p>
                </div>
                {canEdit && (
                    <button
                        className="btn btn-sm btn-outline-secondary ms-auto"
                        onClick={() => setShowConfigModal(true)}
                        title="Configure allowed viewers"
                    >
                        <i className="fa fa-cog"></i>
                    </button>
                )}
            </div>
            <div className="viewer-content">
                <select
                    className="viewer-select"
                    value={viewer}
                    onChange={(e) => setViewer(e.target.value)}
                >
                    <option value="" disabled>Choose a viewer</option>
                    {customViewers.filter(v => isViewerAllowed(`custom-${v.id}`)).length > 0 && (
                        <optgroup label="Custom Viewers">
                            {customViewers.filter(v => isViewerAllowed(`custom-${v.id}`)).map(v => (
                                <option key={`custom-${v.id}`} value={`custom-${v.id}`}>
                                    {v.name}
                                </option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Multimodal Viewers"]?.length > 0 && (
                        <optgroup label="Multimodal Viewers">
                            {filteredViewersByCategory["Multimodal Viewers"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Standard Images"]?.length > 0 && (
                        <optgroup label="Standard Images">
                            {filteredViewersByCategory["Standard Images"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["DICOM Viewers"]?.length > 0 && (
                        <optgroup label="DICOM Viewers">
                            {filteredViewersByCategory["DICOM Viewers"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Clinical Data"]?.length > 0 && (
                        <optgroup label="Clinical Data">
                            {filteredViewersByCategory["Clinical Data"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Documents"]?.length > 0 && (
                        <optgroup label="Documents">
                            {filteredViewersByCategory["Documents"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Pathology"]?.length > 0 && (
                        <optgroup label="Pathology">
                            {filteredViewersByCategory["Pathology"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {filteredViewersByCategory["Annotation Tools"]?.length > 0 && (
                        <optgroup label="Annotation Tools">
                            {filteredViewersByCategory["Annotation Tools"].map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </optgroup>
                    )}
                </select>
                <button
                    disabled={!viewer || isLaunching}
                    onClick={handleLaunchViewer}
                    className={`viewer-launch-btn ${viewer && !isLaunching ? 'active' : ''}`}
                >
                    {isLaunching ? "Launching..." : (viewer ? "Launch Viewer" : "Select a Viewer")}
                </button>
            </div>

            {/* Viewer Configuration Modal - rendered via portal to avoid stacking context issues */}
            {showConfigModal && ReactDOM.createPortal(
                <>
                    <div className="modal-backdrop show" style={{ zIndex: 1050 }} onClick={() => setShowConfigModal(false)}></div>
                    <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="fa fa-desktop me-2"></i>
                                        Configure Allowed Viewers
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setShowConfigModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted mb-3">
                                        Select which viewers will be available for annotators in this study.
                                        {allowedViewerTypes.length === 0 && " Currently all viewers are enabled."}
                                    </p>

                                    <div className="viewer-config-list">
                                        {customViewers.length > 0 && (
                                            <>
                                                <h6 className="text-muted mb-2">Custom Viewers</h6>
                                                {customViewers.map(v => (
                                                    <div key={`custom-${v.id}`} className="form-check mb-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`viewer-custom-${v.id}`}
                                                            checked={isViewerAllowed(`custom-${v.id}`)}
                                                            onChange={() => handleToggleViewerType(`custom-${v.id}`)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`viewer-custom-${v.id}`}>
                                                            {v.name}
                                                        </label>
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        <h6 className="text-muted mb-2 mt-3">Multimodal Viewers</h6>
                                        {systemViewers.filter(v => v.category === "Multimodal Viewers").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">Standard Images</h6>
                                        {systemViewers.filter(v => v.category === "Standard Images").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">DICOM Viewers</h6>
                                        {systemViewers.filter(v => v.category === "DICOM Viewers").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">Clinical Data</h6>
                                        {systemViewers.filter(v => v.category === "Clinical Data").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">Documents</h6>
                                        {systemViewers.filter(v => v.category === "Documents").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">Pathology</h6>
                                        {systemViewers.filter(v => v.category === "Pathology").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}

                                        <h6 className="text-muted mb-2 mt-3">Annotation Tools</h6>
                                        {systemViewers.filter(v => v.category === "Annotation Tools").map(v => (
                                            <div key={v.value} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`viewer-${v.value}`}
                                                    checked={isViewerAllowed(v.value)}
                                                    onChange={() => handleToggleViewerType(v.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`viewer-${v.value}`}>
                                                    {v.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowConfigModal(false)}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};


// Labels Configuration Card
const LabelsCard = ({ labels = [], onLabelsChange, studyId, canEdit = false }) => {
    const [showModal, setShowModal] = useState(false);
    const [newLabel, setNewLabel] = useState({ name: "", color: "#007bff", shortcut: "", annotation_level: "patient", allow_multiple: false });

    const predefinedColors = [
        "#007bff", "#28a745", "#dc3545", "#ffc107",
        "#17a2b8", "#6f42c1", "#fd7e14", "#20c997"
    ];

    const annotationLevels = [
        { value: "patient", label: "Patient Level", description: "One annotation per patient" },
        { value: "data_type", label: "Data Type Level", description: "Separate per data type (imaging, document)" },
        { value: "series", label: "Series Level", description: "Separate per individual series" }
    ];

    const handleAddLabel = async () => {
        if (!newLabel.name.trim()) return;

        const label = {
            id: Date.now(),
            name: newLabel.name.trim(),
            color: newLabel.color,
            shortcut: newLabel.shortcut.trim().toUpperCase(),
            allow_multiple: newLabel.allow_multiple || false,
            annotation_level: newLabel.annotation_level || "patient"
        };

        const updatedLabels = [...labels, label];

        try {
            await API.patch(`/studies/${studyId}/`, { labels: updatedLabels });
            onLabelsChange(updatedLabels);
            setNewLabel({ name: "", color: "#007bff", shortcut: "", annotation_level: "patient" });
            toast.success("Label added successfully");
        } catch (err) {
            console.error("Error saving label:", err);
            toast.error("Failed to save label");
        }
    };

    const handleRemoveLabel = async (labelId) => {
        const updatedLabels = labels.filter((l) => l.id !== labelId);

        try {
            await API.patch(`/studies/${studyId}/`, { labels: updatedLabels });
            onLabelsChange(updatedLabels);
            toast.success("Label removed");
        } catch (err) {
            console.error("Error removing label:", err);
            toast.error("Failed to remove label");
        }
    };

    return (
        <>
            <div className="labels-card">
                <div className="labels-header">
                    <Tags className="labels-icon" />
                    <div>
                        <h3>Study Labels</h3>
                        <p className="labels-subtitle">
                            {canEdit ? "Configure labels for data annotation" : "Labels for data annotation"}
                        </p>
                    </div>
                </div>
                <div className="labels-content">
                    {labels.length === 0 ? (
                        <p className="no-labels-text">
                            {canEdit ? "No labels configured yet" : "No labels configured for this study"}
                        </p>
                    ) : (
                        <div className="labels-list">
                            {labels.map((label) => (
                                <div key={label.id} className="label-item">
                                    <span
                                        className="label-color"
                                        style={{ backgroundColor: label.color }}
                                    />
                                    <span className="label-name">{label.name}</span>
                                    {label.shortcut && (
                                        <span className="label-shortcut">{label.shortcut}</span>
                                    )}
                                    {label.allow_multiple && (
                                        <span className="annotation-level-badge" style={{ backgroundColor: '#6f42c1' }}>Multi</span>
                                    )}
                                    {label.annotation_level && label.annotation_level !== 'patient' && (
                                        <span className={`annotation-level-badge level-${label.annotation_level}`}>
                                            {label.annotation_level === 'data_type' ? 'Data Type' : 'Series'}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {canEdit && (
                        <button
                            className="configure-labels-btn"
                            onClick={() => setShowModal(true)}
                        >
                            <Plus size={16} />
                            Configure Labels
                        </button>
                    )}
                </div>
            </div>

            {/* Labels Modal - Only shown for users with edit permission */}
            {showModal && canEdit && (
                <div className="labels-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="labels-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="labels-modal-header">
                            <h4>Configure Labels</h4>
                            <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="labels-modal-body">
                            {/* Add New Label Form */}
                            <div className="add-label-form">
                                <div className="form-row">
                                    <input
                                        type="text"
                                        className="label-input"
                                        placeholder="Label name"
                                        value={newLabel.name}
                                        onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                                        onKeyPress={(e) => e.key === "Enter" && handleAddLabel()}
                                    />
                                    <input
                                        type="text"
                                        className="label-input shortcut-input"
                                        placeholder="Key"
                                        maxLength={1}
                                        value={newLabel.shortcut}
                                        onChange={(e) => setNewLabel({ ...newLabel, shortcut: e.target.value })}
                                    />
                                </div>
                                <div className="color-picker-row">
                                    {predefinedColors.map((color) => (
                                        <button
                                            key={color}
                                            className={`color-btn ${newLabel.color === color ? "selected" : ""}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => setNewLabel({ ...newLabel, color })}
                                        />
                                    ))}
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>Selection Type</div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <div
                                            onClick={() => setNewLabel({ ...newLabel, allow_multiple: false })}
                                            style={{
                                                flex: 1, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                                fontSize: '12px', fontWeight: 500, border: '2px solid',
                                                borderColor: !newLabel.allow_multiple ? '#007bff' : '#dee2e6',
                                                backgroundColor: !newLabel.allow_multiple ? '#007bff' : '#fff',
                                                color: !newLabel.allow_multiple ? '#fff' : '#666'
                                            }}
                                        >
                                            Single Select
                                        </div>
                                        <div
                                            onClick={() => setNewLabel({ ...newLabel, allow_multiple: true })}
                                            style={{
                                                flex: 1, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                                fontSize: '12px', fontWeight: 500, border: '2px solid',
                                                borderColor: newLabel.allow_multiple ? '#6f42c1' : '#dee2e6',
                                                backgroundColor: newLabel.allow_multiple ? '#6f42c1' : '#fff',
                                                color: newLabel.allow_multiple ? '#fff' : '#666'
                                            }}
                                        >
                                            Multi Select
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                        {newLabel.allow_multiple ? "Multiple labels can be applied per patient" : "Only one label can be applied per patient"}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Annotation Level</label>
                                    <select
                                        className="annotation-level-select"
                                        value={newLabel.annotation_level}
                                        onChange={(e) => setNewLabel({ ...newLabel, annotation_level: e.target.value })}
                                    >
                                        {annotationLevels.map((level) => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="annotation-level-hint">
                                        {annotationLevels.find(l => l.value === newLabel.annotation_level)?.description}
                                    </small>
                                </div>
                                <button className="add-label-btn" onClick={handleAddLabel}>
                                    <Plus size={16} /> Add Label
                                </button>
                            </div>

                            {/* Existing Labels */}
                            <div className="existing-labels">
                                <h5>Existing Labels</h5>
                                {labels.length === 0 ? (
                                    <p className="no-labels-msg">No labels configured yet</p>
                                ) : (
                                    <div className="labels-list-modal">
                                        {labels.map((label) => (
                                            <div key={label.id} className="label-item-modal">
                                                <span
                                                    className="label-color"
                                                    style={{ backgroundColor: label.color }}
                                                />
                                                <span className="label-name">{label.name}</span>
                                                {label.shortcut && (
                                                    <span className="label-shortcut-badge">{label.shortcut}</span>
                                                )}
                                                <span className={`annotation-level-badge-small level-${label.annotation_level || 'patient'}`}>
                                                    {label.annotation_level === 'data_type' ? 'DT' : label.annotation_level === 'series' ? 'S' : 'P'}
                                                </span>
                                                <span className={`selection-type-badge ${label.allow_multiple ? 'multi' : 'single'}`}>
                                                    {label.allow_multiple ? 'M' : '1'}
                                                </span>
                                                <button
                                                    className="remove-label-btn"
                                                    onClick={() => handleRemoveLabel(label.id)}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="labels-modal-footer">
                            <p className="labels-hint">
                                Use keyboard shortcuts to quickly apply labels while viewing
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};


// Questions Configuration Card
const QuestionsCard = ({ questions = [], onQuestionsChange, studyId, canEdit = false }) => {
    const [showModal, setShowModal] = useState(false);
    const [newQuestion, setNewQuestion] = useState({
        question: "",
        type: "multiple_choice",
        options: [""],
        required: false,
        annotation_level: "patient"
    });

    const questionTypes = [
        { value: "multiple_choice", label: "Multiple Choice (Single)", icon: List },
        { value: "multiple_select", label: "Multiple Choice (Multi)", icon: List },
        { value: "text", label: "Open Text", icon: Type }
    ];

    const annotationLevels = [
        { value: "patient", label: "Patient Level", description: "One annotation per patient" },
        { value: "data_type", label: "Data Type Level", description: "Separate per data type (imaging, document)" },
        { value: "series", label: "Series Level", description: "Separate per individual series" }
    ];

    const handleAddOption = () => {
        setNewQuestion({
            ...newQuestion,
            options: [...newQuestion.options, ""]
        });
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...newQuestion.options];
        newOptions[index] = value;
        setNewQuestion({ ...newQuestion, options: newOptions });
    };

    const handleRemoveOption = (index) => {
        if (newQuestion.options.length > 1) {
            const newOptions = newQuestion.options.filter((_, i) => i !== index);
            setNewQuestion({ ...newQuestion, options: newOptions });
        }
    };

    const handleAddQuestion = async () => {
        if (!newQuestion.question.trim()) return;

        // For multiple choice, filter out empty options
        const filteredOptions = newQuestion.type !== "text"
            ? newQuestion.options.filter(opt => opt.trim() !== "")
            : [];

        if (newQuestion.type !== "text" && filteredOptions.length < 2) {
            toast.error("Please add at least 2 options for multiple choice questions");
            return;
        }

        const question = {
            id: Date.now(),
            question: newQuestion.question.trim(),
            type: newQuestion.type,
            options: filteredOptions,
            required: newQuestion.required,
            annotation_level: newQuestion.annotation_level || "patient"
        };

        const updatedQuestions = [...questions, question];

        try {
            await API.patch(`/studies/${studyId}/`, { questions: updatedQuestions });
            onQuestionsChange(updatedQuestions);
            setNewQuestion({
                question: "",
                type: "multiple_choice",
                options: [""],
                required: false,
                annotation_level: "patient"
            });
            toast.success("Question added successfully");
        } catch (err) {
            console.error("Error saving question:", err);
            toast.error("Failed to save question");
        }
    };

    const handleRemoveQuestion = async (questionId) => {
        const updatedQuestions = questions.filter((q) => q.id !== questionId);

        try {
            await API.patch(`/studies/${studyId}/`, { questions: updatedQuestions });
            onQuestionsChange(updatedQuestions);
            toast.success("Question removed");
        } catch (err) {
            console.error("Error removing question:", err);
            toast.error("Failed to remove question");
        }
    };

    const getQuestionTypeLabel = (type) => {
        const typeInfo = questionTypes.find(t => t.value === type);
        return typeInfo ? typeInfo.label : type;
    };

    return (
        <>
            <div className="questions-card">
                <div className="questions-header">
                    <HelpCircle className="questions-icon" />
                    <div>
                        <h3>Study Questions</h3>
                        <p className="questions-subtitle">
                            {canEdit ? "Configure questions for data annotation" : "Questions for data annotation"}
                        </p>
                    </div>
                </div>
                <div className="questions-content">
                    {questions.length === 0 ? (
                        <p className="no-questions-text">
                            {canEdit ? "No questions configured yet" : "No questions configured for this study"}
                        </p>
                    ) : (
                        <div className="questions-list">
                            {questions.map((q, index) => (
                                <div key={q.id} className="question-item">
                                    <div className="question-number">{index + 1}</div>
                                    <div className="question-info">
                                        <span className="question-text">{q.question}</span>
                                        <span className="question-type-badge">{getQuestionTypeLabel(q.type)}</span>
                                        {q.required && <span className="question-required">Required</span>}
                                        {q.annotation_level && q.annotation_level !== 'patient' && (
                                            <span className={`annotation-level-badge level-${q.annotation_level}`}>
                                                {q.annotation_level === 'data_type' ? 'Data Type' : 'Series'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {canEdit && (
                        <button
                            className="configure-questions-btn"
                            onClick={() => setShowModal(true)}
                        >
                            <Plus size={16} />
                            Configure Questions
                        </button>
                    )}
                </div>
            </div>

            {/* Questions Modal - Only shown for users with edit permission */}
            {showModal && canEdit && (
                <div className="questions-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="questions-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="questions-modal-header">
                            <h4>Configure Questions</h4>
                            <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="questions-modal-body">
                            {/* Add New Question Form */}
                            <div className="add-question-form">
                                <div className="form-group">
                                    <label>Question</label>
                                    <input
                                        type="text"
                                        className="question-input"
                                        placeholder="Enter your question..."
                                        value={newQuestion.question}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Question Type</label>
                                    <div className="question-type-selector">
                                        {questionTypes.map((type) => (
                                            <button
                                                key={type.value}
                                                className={`type-btn ${newQuestion.type === type.value ? "selected" : ""}`}
                                                onClick={() => setNewQuestion({ ...newQuestion, type: type.value })}
                                            >
                                                <type.icon size={16} />
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {newQuestion.type !== "text" && (
                                    <div className="form-group">
                                        <label>Options</label>
                                        <div className="options-list">
                                            {newQuestion.options.map((option, index) => (
                                                <div key={index} className="option-row">
                                                    <input
                                                        type="text"
                                                        className="option-input"
                                                        placeholder={`Option ${index + 1}`}
                                                        value={option}
                                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                                    />
                                                    {newQuestion.options.length > 1 && (
                                                        <button
                                                            className="remove-option-btn"
                                                            onClick={() => handleRemoveOption(index)}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button className="add-option-btn" onClick={handleAddOption}>
                                                <Plus size={14} /> Add Option
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Annotation Level</label>
                                    <select
                                        className="annotation-level-select"
                                        value={newQuestion.annotation_level}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, annotation_level: e.target.value })}
                                    >
                                        {annotationLevels.map((level) => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="annotation-level-hint">
                                        {annotationLevels.find(l => l.value === newQuestion.annotation_level)?.description}
                                    </small>
                                </div>

                                <div className="form-group-inline">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={newQuestion.required}
                                            onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                                        />
                                        Required question
                                    </label>
                                </div>

                                <button className="add-question-btn" onClick={handleAddQuestion}>
                                    <Plus size={16} /> Add Question
                                </button>
                            </div>

                            {/* Existing Questions */}
                            <div className="existing-questions">
                                <h5>Existing Questions</h5>
                                {questions.length === 0 ? (
                                    <p className="no-questions-msg">No questions configured yet</p>
                                ) : (
                                    <div className="questions-list-modal">
                                        {questions.map((q, index) => (
                                            <div key={q.id} className="question-item-modal">
                                                <div className="question-item-header">
                                                    <span className="question-number">{index + 1}</span>
                                                    <span className="question-text-modal">{q.question}</span>
                                                    <button
                                                        className="remove-question-btn"
                                                        onClick={() => handleRemoveQuestion(q.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <div className="question-item-meta">
                                                    <span className="question-type-badge">{getQuestionTypeLabel(q.type)}</span>
                                                    {q.required && <span className="question-required">Required</span>}
                                                    {q.type !== "text" && q.options && (
                                                        <span className="question-options-count">{q.options.length} options</span>
                                                    )}
                                                    <span className={`annotation-level-badge-small level-${q.annotation_level || 'patient'}`}>
                                                        {q.annotation_level === 'data_type' ? 'DT' : q.annotation_level === 'series' ? 'S' : 'P'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="questions-modal-footer">
                            <p className="questions-hint">
                                Questions will be displayed in the viewer for annotators to answer
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};


// Scores Configuration Card
const ScoresCard = ({ scores = [], onScoresChange, studyId, canEdit = false }) => {
    const [showModal, setShowModal] = useState(false);
    const [newScore, setNewScore] = useState({
        name: "",
        description: "",
        scale: "1-5",
        required: false,
        annotation_level: "patient"
    });

    const scaleOptions = [
        { value: "1-5", label: "1 to 5", min: 1, max: 5 },
        { value: "1-10", label: "1 to 10", min: 1, max: 10 },
        { value: "0-100", label: "0 to 100", min: 0, max: 100 },
        { value: "1-3", label: "1 to 3 (Low/Med/High)", min: 1, max: 3 },
    ];

    const annotationLevels = [
        { value: "patient", label: "Patient Level", description: "One annotation per patient" },
        { value: "data_type", label: "Data Type Level", description: "Separate per data type (imaging, document)" },
        { value: "series", label: "Series Level", description: "Separate per individual series" }
    ];

    const handleAddScore = async () => {
        if (!newScore.name.trim()) {
            toast.error("Please enter a score name");
            return;
        }

        const scaleInfo = scaleOptions.find(s => s.value === newScore.scale);
        const score = {
            id: Date.now(),
            name: newScore.name.trim(),
            description: newScore.description.trim(),
            scale: newScore.scale,
            min: scaleInfo?.min || 1,
            max: scaleInfo?.max || 5,
            required: newScore.required,
            annotation_level: newScore.annotation_level || "patient"
        };

        const updatedScores = [...scores, score];

        try {
            await API.patch(`/studies/${studyId}/`, { scores: updatedScores });
            onScoresChange(updatedScores);
            setNewScore({
                name: "",
                description: "",
                scale: "1-5",
                required: false,
                annotation_level: "patient"
            });
            toast.success("Score metric added successfully");
        } catch (err) {
            console.error("Error saving score:", err);
            toast.error("Failed to save score metric");
        }
    };

    const handleRemoveScore = async (scoreId) => {
        const updatedScores = scores.filter((s) => s.id !== scoreId);

        try {
            await API.patch(`/studies/${studyId}/`, { scores: updatedScores });
            onScoresChange(updatedScores);
            toast.success("Score metric removed");
        } catch (err) {
            console.error("Error removing score:", err);
            toast.error("Failed to remove score metric");
        }
    };

    const getScaleLabel = (scale) => {
        const scaleInfo = scaleOptions.find(s => s.value === scale);
        return scaleInfo ? scaleInfo.label : scale;
    };

    return (
        <>
            <div className="scores-card">
                <div className="scores-header">
                    <Star className="scores-icon" />
                    <div>
                        <h3>Study Scores</h3>
                        <p className="scores-subtitle">
                            {canEdit ? "Configure scoring metrics for data annotation" : "Scoring metrics for data annotation"}
                        </p>
                    </div>
                </div>
                <div className="scores-content">
                    {scores.length === 0 ? (
                        <p className="no-scores-text">
                            {canEdit ? "No scoring metrics configured yet" : "No scoring metrics configured for this study"}
                        </p>
                    ) : (
                        <div className="scores-list">
                            {scores.map((score, index) => (
                                <div key={score.id} className="score-item">
                                    <div className="score-number">{index + 1}</div>
                                    <div className="score-info">
                                        <span className="score-name">{score.name}</span>
                                        <span className="score-scale-badge">{getScaleLabel(score.scale)}</span>
                                        {score.required && <span className="score-required">Required</span>}
                                        {score.annotation_level && score.annotation_level !== 'patient' && (
                                            <span className={`annotation-level-badge level-${score.annotation_level}`}>
                                                {score.annotation_level === 'data_type' ? 'Data Type' : 'Series'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {canEdit && (
                        <button
                            className="configure-scores-btn"
                            onClick={() => setShowModal(true)}
                        >
                            <Plus size={16} />
                            Configure Scores
                        </button>
                    )}
                </div>
            </div>

            {/* Scores Modal - Only shown for users with edit permission */}
            {showModal && canEdit && (
                <div className="scores-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="scores-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="scores-modal-header">
                            <h4>Configure Scoring Metrics</h4>
                            <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="scores-modal-body">
                            {/* Add New Score Form */}
                            <div className="add-score-form">
                                <div className="form-group">
                                    <label>Score Name</label>
                                    <input
                                        type="text"
                                        className="score-input"
                                        placeholder="e.g., Image Quality"
                                        value={newScore.name}
                                        onChange={(e) => setNewScore({ ...newScore, name: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Description (optional)</label>
                                    <input
                                        type="text"
                                        className="score-input"
                                        placeholder="e.g., Rate the overall image quality"
                                        value={newScore.description}
                                        onChange={(e) => setNewScore({ ...newScore, description: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Scale</label>
                                    <div className="scale-selector">
                                        {scaleOptions.map((scale) => (
                                            <button
                                                key={scale.value}
                                                className={`scale-btn ${newScore.scale === scale.value ? "selected" : ""}`}
                                                onClick={() => setNewScore({ ...newScore, scale: scale.value })}
                                            >
                                                {scale.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Annotation Level</label>
                                    <select
                                        className="annotation-level-select"
                                        value={newScore.annotation_level}
                                        onChange={(e) => setNewScore({ ...newScore, annotation_level: e.target.value })}
                                    >
                                        {annotationLevels.map((level) => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="annotation-level-hint">
                                        {annotationLevels.find(l => l.value === newScore.annotation_level)?.description}
                                    </small>
                                </div>

                                <div className="form-group-inline">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={newScore.required}
                                            onChange={(e) => setNewScore({ ...newScore, required: e.target.checked })}
                                        />
                                        Required score
                                    </label>
                                </div>

                                <button className="add-score-btn" onClick={handleAddScore}>
                                    <Plus size={16} /> Add Score Metric
                                </button>
                            </div>

                            {/* Existing Scores */}
                            <div className="existing-scores">
                                <h5>Existing Score Metrics</h5>
                                {scores.length === 0 ? (
                                    <p className="no-scores-msg">No scoring metrics configured yet</p>
                                ) : (
                                    <div className="scores-list-modal">
                                        {scores.map((score, index) => (
                                            <div key={score.id} className="score-item-modal">
                                                <div className="score-item-header">
                                                    <span className="score-number">{index + 1}</span>
                                                    <span className="score-name-modal">{score.name}</span>
                                                    <button
                                                        className="remove-score-btn"
                                                        onClick={() => handleRemoveScore(score.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <div className="score-item-meta">
                                                    <span className="score-scale-badge">{getScaleLabel(score.scale)}</span>
                                                    {score.required && <span className="score-required">Required</span>}
                                                    <span className={`annotation-level-badge-small level-${score.annotation_level || 'patient'}`}>
                                                        {score.annotation_level === 'data_type' ? 'DT' : score.annotation_level === 'series' ? 'S' : 'P'}
                                                    </span>
                                                    {score.description && (
                                                        <span className="score-description">{score.description}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="scores-modal-footer">
                            <p className="scores-hint">
                                Score metrics will be displayed in the viewer for annotators to rate
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};


// Public Sharing Card
const PublicSharingCard = ({ isPublic, publicationUrl, onPublicChange, onPublicationUrlChange, studyId, canEdit = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempUrl, setTempUrl] = useState(publicationUrl || "");
    const [saving, setSaving] = useState(false);

    const handleTogglePublic = async () => {
        if (!canEdit) return;
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { is_public: !isPublic });
            onPublicChange(!isPublic);
            toast.success(isPublic ? "Study is now private" : "Study is now public!");
        } catch (err) {
            console.error("Error updating public status:", err);
            toast.error("Failed to update public status");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveUrl = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { publication_url: tempUrl || null });
            onPublicationUrlChange(tempUrl || null);
            setIsEditing(false);
            toast.success("Publication URL saved");
        } catch (err) {
            console.error("Error saving publication URL:", err);
            toast.error("Failed to save publication URL");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="public-sharing-card">
            <div className="public-sharing-header">
                <Globe className="public-sharing-icon" />
                <div>
                    <h3>Public Sharing</h3>
                    <p className="public-sharing-subtitle">
                        Share your research with the community
                    </p>
                </div>
            </div>
            <div className="public-sharing-content">
                {/* Public Toggle */}
                <div className="public-toggle-row">
                    <div className="public-toggle-info">
                        <span className="public-toggle-label">Make study public</span>
                        <span className="public-toggle-hint">
                            Visible in Open Research Studies
                        </span>
                    </div>
                    {canEdit ? (
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={handleTogglePublic}
                                disabled={saving}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    ) : (
                        <span className={`public-status-badge ${isPublic ? 'public' : 'private'}`}>
                            {isPublic ? 'Public' : 'Private'}
                        </span>
                    )}
                </div>

                {/* Publication URL */}
                <div className="publication-url-section">
                    <label className="publication-url-label">
                        <ExternalLink size={14} />
                        Publication URL
                    </label>
                    {isEditing && canEdit ? (
                        <div className="publication-url-edit">
                            <input
                                type="url"
                                className="publication-url-input"
                                placeholder="https://doi.org/..."
                                value={tempUrl}
                                onChange={(e) => setTempUrl(e.target.value)}
                            />
                            <div className="publication-url-actions">
                                <button
                                    className="btn-save-url"
                                    onClick={handleSaveUrl}
                                    disabled={saving}
                                >
                                    Save
                                </button>
                                <button
                                    className="btn-cancel-url"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setTempUrl(publicationUrl || "");
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="publication-url-display">
                            {publicationUrl ? (
                                <a
                                    href={publicationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="publication-link"
                                >
                                    {publicationUrl.length > 40
                                        ? publicationUrl.substring(0, 40) + "..."
                                        : publicationUrl}
                                    <ExternalLink size={12} />
                                </a>
                            ) : (
                                <span className="no-publication">No publication linked</span>
                            )}
                            {canEdit && (
                                <button
                                    className="btn-edit-url"
                                    onClick={() => setIsEditing(true)}
                                >
                                    {publicationUrl ? "Edit" : "Add"}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Info about public studies */}
                {isPublic && (
                    <div className="public-info-box">
                        <i className="fa fa-info-circle"></i>
                        <span>
                            Your study is visible on the{" "}
                            <a href="/OpenStudies" target="_blank" rel="noopener noreferrer">
                                Research Constellations
                            </a>{" "}
                            page
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};


// Study Command Bar - Horizontal configuration bar for study owner
const StudyCommandBar = ({
    labels = [],
    onLabelsChange,
    labelDisplayMode = 'dropdown',
    onLabelDisplayModeChange,
    questions = [],
    onQuestionsChange,
    scores = [],
    onScoresChange,
    isPublic,
    publicationUrl,
    onPublicChange,
    onPublicationUrlChange,
    shareAnnotations = false,
    onShareAnnotationsChange,
    studyId,
    study,
    canEdit = false,
    onRefreshData
}) => {
    const [activeModal, setActiveModal] = useState(null);
    const [togglingShare, setTogglingShare] = useState(false);

    const handleToggleShareAnnotations = async () => {
        if (togglingShare) return;
        setTogglingShare(true);
        try {
            const next = !shareAnnotations;
            await API.patch(`/studies/${studyId}/`, { share_annotations: next });
            onShareAnnotationsChange?.(next);
            onRefreshData?.();
            toast.success(
                next
                    ? 'Annotations are now shared between all reviewers.'
                    : 'Annotations are now private to each reviewer.'
            );
        } catch (err) {
            console.error('Error updating share_annotations:', err);
            toast.error('Failed to update annotation sharing.');
        } finally {
            setTogglingShare(false);
        }
    };

    // Persist conversion task ID in localStorage so it survives page navigation
    const conversionStorageKey = `conversion_task_${studyId}`;
    const uploadStorageKey = `upload_task_${studyId}`;

    const [conversionTaskId, setConversionTaskId] = useState(() => {
        // Load from localStorage on initial render
        const stored = localStorage.getItem(conversionStorageKey);
        if (stored) {
            try {
                const data = JSON.parse(stored);
                // Check if the stored task is not too old (max 1 hour)
                if (data.timestamp && Date.now() - data.timestamp < 3600000) {
                    return data.taskId;
                } else {
                    // Expired, clean up
                    localStorage.removeItem(conversionStorageKey);
                }
            } catch (e) {
                localStorage.removeItem(conversionStorageKey);
            }
        }
        return null;
    });

    // Upload tasks: object mapping targetUserId (or 'default') to { taskId, username, timestamp }
    // This allows multiple concurrent upload jobs for different users
    const [uploadTasks, setUploadTasks] = useState(() => {
        // Load from localStorage on initial render
        const stored = localStorage.getItem(uploadStorageKey);
        if (stored) {
            try {
                const data = JSON.parse(stored);
                // Handle both old format (single taskId) and new format (tasks object)
                if (data.taskId && !data.tasks) {
                    // Old format - migrate to new format
                    if (data.timestamp && Date.now() - data.timestamp < 3600000) {
                        return { default: { taskId: data.taskId, username: null, timestamp: data.timestamp } };
                    }
                } else if (data.tasks) {
                    // New format - filter out expired tasks
                    const validTasks = {};
                    for (const [key, task] of Object.entries(data.tasks)) {
                        if (task.timestamp && Date.now() - task.timestamp < 3600000) {
                            validTasks[key] = task;
                        }
                    }
                    if (Object.keys(validTasks).length > 0) {
                        return validTasks;
                    }
                }
                localStorage.removeItem(uploadStorageKey);
            } catch (e) {
                localStorage.removeItem(uploadStorageKey);
            }
        }
        return {};
    });

    // Save to localStorage whenever conversionTaskId changes
    useEffect(() => {
        if (conversionTaskId) {
            localStorage.setItem(conversionStorageKey, JSON.stringify({
                taskId: conversionTaskId,
                timestamp: Date.now()
            }));
        } else {
            localStorage.removeItem(conversionStorageKey);
        }
    }, [conversionTaskId, conversionStorageKey]);

    // Save to localStorage whenever uploadTasks changes
    useEffect(() => {
        if (Object.keys(uploadTasks).length > 0) {
            localStorage.setItem(uploadStorageKey, JSON.stringify({
                tasks: uploadTasks
            }));
        } else {
            localStorage.removeItem(uploadStorageKey);
        }
    }, [uploadTasks, uploadStorageKey]);

    // Workflow state for DV Agents - persisted to localStorage
    const workflowStorageKey = `workflow_task_${studyId}`;
    const [workflowState, setWorkflowState] = useState(() => {
        const stored = localStorage.getItem(workflowStorageKey);
        if (stored) {
            try {
                const data = JSON.parse(stored);
                // Check if the stored workflow is not too old (max 1 hour)
                if (data.timestamp && Date.now() - data.timestamp < 3600000) {
                    return data.workflow;
                } else {
                    localStorage.removeItem(workflowStorageKey);
                }
            } catch (e) {
                localStorage.removeItem(workflowStorageKey);
            }
        }
        return null;
    });

    // Save workflow to localStorage whenever it changes
    useEffect(() => {
        if (workflowState) {
            localStorage.setItem(workflowStorageKey, JSON.stringify({
                workflow: workflowState,
                timestamp: Date.now()
            }));
        } else {
            localStorage.removeItem(workflowStorageKey);
        }
    }, [workflowState, workflowStorageKey]);

    // Handle when conversion starts from the tools modal
    const handleConversionStart = (taskId) => {
        setConversionTaskId(taskId);
        setActiveModal(null); // Close the modal
    };

    // Handle when conversion completes
    const handleConversionComplete = (result) => {
        toast.success(`Converted ${result.converted} of ${result.total} files to DICOM`);
        if (onRefreshData) {
            onRefreshData();
        }
    };

    // Handle clearing the conversion tracker
    const handleConversionClear = () => {
        setConversionTaskId(null);
    };

    // Handle stopping the conversion
    const handleConversionStop = async () => {
        if (!conversionTaskId) return;

        try {
            await API.post('/viewers/stop_batch_conversion/', {
                task_id: conversionTaskId
            });
            toast.info('Conversion stopped');
            setConversionTaskId(null);
            if (onRefreshData) {
                onRefreshData();
            }
        } catch (err) {
            console.error('Error stopping conversion:', err);
            toast.error('Failed to stop conversion');
        }
    };

    // Handle when upload starts from the tools modal
    // taskKey is either a userId string or 'default' for non-user-specific uploads
    // NOTE: Modal stays open so users can start multiple uploads for different users
    const handleUploadStart = (taskId, targetUserId, username) => {
        const taskKey = targetUserId ? String(targetUserId) : 'default';
        setUploadTasks(prev => ({
            ...prev,
            [taskKey]: { taskId, username, timestamp: Date.now() }
        }));
        // Don't close modal - allow starting multiple uploads for different users
    };

    // Handle when upload completes for a specific task
    const handleUploadComplete = (taskKey) => (result) => {
        const task = uploadTasks[taskKey];
        const userInfo = task?.username ? ` for ${task.username}` : '';
        toast.success(`Uploaded ${result.uploaded} of ${result.total} files to Orthanc${userInfo}`);
        if (onRefreshData) {
            onRefreshData();
        }
    };

    // Handle clearing the upload tracker for a specific task
    const handleUploadClear = (taskKey) => () => {
        setUploadTasks(prev => {
            const newTasks = { ...prev };
            delete newTasks[taskKey];
            return newTasks;
        });
    };

    // Handle stopping the upload for a specific task
    const handleUploadStop = (taskKey) => async () => {
        const task = uploadTasks[taskKey];
        if (!task?.taskId) return;

        try {
            await API.post('/viewers/stop_batch_upload/', {
                task_id: task.taskId
            });
            const userInfo = task.username ? ` for ${task.username}` : '';
            toast.info(`Upload stopped${userInfo}`);
            setUploadTasks(prev => {
                const newTasks = { ...prev };
                delete newTasks[taskKey];
                return newTasks;
            });
            if (onRefreshData) {
                onRefreshData();
            }
        } catch (err) {
            console.error('Error stopping upload:', err);
            toast.error('Failed to stop upload');
        }
    };

    // Handle workflow state updates from DVAgentsConfigModal
    const handleWorkflowStart = (workflow) => {
        setWorkflowState(workflow);
    };

    const handleWorkflowUpdate = (updates) => {
        setWorkflowState(prev => prev ? { ...prev, ...updates } : null);
    };

    const handleWorkflowClear = () => {
        setWorkflowState(null);
    };

    const handleWorkflowStop = () => {
        setWorkflowState(prev => prev ? { ...prev, status: 'stopped' } : null);
    };

    if (!canEdit) return null;

    const configItems = [
        {
            id: 'labels',
            icon: Tags,
            label: 'Labels',
            count: labels.length,
            color: '#6f42c1',
            description: 'Configure annotation labels'
        },
        {
            id: 'questions',
            icon: HelpCircle,
            label: 'Questions',
            count: questions.length,
            color: '#17a2b8',
            description: 'Configure study questions'
        },
        {
            id: 'scores',
            icon: Star,
            label: 'Scores',
            count: scores.length,
            color: '#ffc107',
            description: 'Configure scoring metrics'
        },
        {
            id: 'sharing',
            icon: Globe,
            label: isPublic ? 'Public' : 'Private',
            count: null,
            color: isPublic ? '#28a745' : '#6c757d',
            description: 'Public sharing settings'
        },
        {
            id: 'tools',
            icon: Wrench,
            label: 'Tools',
            count: study?.tools_count || null,
            color: '#fd7e14',
            description: 'Processing tools assigned to this study'
        },
        {
            id: 'models',
            icon: Cpu,
            label: 'Models',
            count: study?.models?.length || 0,
            color: '#e83e8c',
            description: 'AI models assigned to this study'
        },
        {
            id: 'data_ops',
            icon: RefreshCw,
            label: 'Data Ops',
            count: null,
            color: '#6c757d',
            description: 'Built-in data operations (convert, upload)'
        },
        {
            id: 'agents',
            icon: GitBranch,
            label: 'Workflows',
            count: study?.workflows?.length || 0,
            color: '#20c997',
            description: 'Workflow pipelines with parallel execution'
        },
        {
            id: 'viewer_navbar',
            icon: SlidersHorizontal,
            label: 'Viewer Navbar',
            count: null,
            color: '#0d6efd',
            description: 'Toggle which buttons appear in the viewer navbar'
        },
        {
            id: 'segmentation_preset',
            icon: Layers,
            label: 'Segmentation Preset',
            count: study?.segmentation_preset?.segments?.length || null,
            color: '#9b59b6',
            description: 'Auto-load a named segmentation scheme in the OHIF segmentation viewer'
        },
        {
            id: 'viewer_layout',
            icon: LayoutGrid,
            label: 'Viewer Layout',
            count: study?.viewer_layout
                ? (VIEWER_LAYOUT_LABELS[study.viewer_layout] || 'Custom')
                : null,
            color: '#0d9488',
            description: 'Default OHIF layout (MPR, 3D, grid, ...) when this study opens'
        },
        {
            id: 'distribution',
            icon: Shuffle,
            label: 'Distribution',
            count: study?.distribution_mode && study.distribution_mode !== 'shared'
                ? (study.distribution_mode === 'randomized' ? 'R' : 'M')
                : null,
            color: '#0dcaf0',
            description: 'How patients are distributed among assigned reviewers'
        },
        {
            id: 'reviewer_view',
            icon: Eye,
            label: study?.study_type === 'randomized' ? "Reviewer's View: Flow" : "Reviewer's View: Standard",
            count: null,
            color: study?.study_type === 'randomized' ? '#0dcaf0' : '#6c757d',
            description: 'How reviewers see and step through cases: full table (Standard) or queued "Start next case" dashboard (Flow)',
        },
        {
            id: 'share_annotations',
            icon: Users,
            label: shareAnnotations ? 'Annotations: Shared' : 'Annotations: Private',
            count: null,
            color: shareAnnotations ? '#198754' : '#dc3545',
            description: 'Click to toggle: shared means every reviewer sees the same annotations; private means each reviewer only sees their own',
            onClick: handleToggleShareAnnotations,
            disabled: togglingShare,
        },
        {
            id: 'study_type',
            icon: Shuffle,
            label: study?.study_type === 'randomized' ? 'Randomized' : 'Standard',
            count: null,
            color: study?.study_type === 'randomized' ? '#0dcaf0' : '#6c757d',
            description: 'Choose how reviewers progress through cases'
        },
    ];

    return (
        <>
            <div className="study-command-bar">
                <div className="command-bar-title">
                    <span>Study Configuration</span>
                </div>
                <div className="command-bar-items">
                    {configItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.id}
                                className={`command-bar-btn ${activeModal === item.id ? 'active' : ''}`}
                                onClick={item.onClick || (() => setActiveModal(item.id))}
                                title={item.description}
                                disabled={item.disabled}
                            >
                                <Icon size={18} style={{ color: item.color }} />
                                <span className="command-btn-label">{item.label}</span>
                                {item.count !== null && (
                                    <span className="command-btn-count" style={{ backgroundColor: item.color }}>
                                        {item.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Task Trackers - shown below command bar when tasks are active */}
            {(conversionTaskId || Object.keys(uploadTasks).length > 0 || workflowState) && (
                <div className="task-trackers-container">
                    {conversionTaskId && (
                        <TaskTracker
                            taskId={conversionTaskId}
                            taskType="convert"
                            onComplete={handleConversionComplete}
                            onClear={handleConversionClear}
                            onStop={handleConversionStop}
                        />
                    )}
                    {Object.entries(uploadTasks).map(([taskKey, task]) => {
                        const taskType = taskKey.startsWith('ann_') ? 'preupload_annotations'
                            : taskKey.startsWith('map_') ? 'preupload_maps'
                            : 'upload';
                        return (
                            <TaskTracker
                                key={taskKey}
                                taskId={task.taskId}
                                taskType={taskType}
                                label={task.username || undefined}
                                onComplete={handleUploadComplete(taskKey)}
                                onClear={handleUploadClear(taskKey)}
                                onStop={handleUploadStop(taskKey)}
                            />
                        );
                    })}
                    {workflowState && (
                        <WorkflowTracker
                            workflow={workflowState}
                            onClear={handleWorkflowClear}
                            onStop={handleWorkflowStop}
                        />
                    )}
                </div>
            )}

            {/* Labels Modal */}
            {activeModal === 'labels' && (
                <LabelsConfigModal
                    labels={labels}
                    onLabelsChange={onLabelsChange}
                    labelDisplayMode={labelDisplayMode}
                    onLabelDisplayModeChange={onLabelDisplayModeChange}
                    studyId={studyId}
                    onClose={() => setActiveModal(null)}
                />
            )}

            {/* Questions Modal */}
            {activeModal === 'questions' && (
                <QuestionsConfigModal
                    questions={questions}
                    onQuestionsChange={onQuestionsChange}
                    studyId={studyId}
                    onClose={() => setActiveModal(null)}
                />
            )}

            {/* Scores Modal */}
            {activeModal === 'scores' && (
                <ScoresConfigModal
                    scores={scores}
                    onScoresChange={onScoresChange}
                    studyId={studyId}
                    onClose={() => setActiveModal(null)}
                />
            )}

            {/* Sharing Modal */}
            {activeModal === 'sharing' && (
                <SharingConfigModal
                    isPublic={isPublic}
                    publicationUrl={publicationUrl}
                    onPublicChange={onPublicChange}
                    onPublicationUrlChange={onPublicationUrlChange}
                    shareAnnotations={shareAnnotations}
                    onShareAnnotationsChange={onShareAnnotationsChange}
                    studyId={studyId}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Assigned Tools Modal */}
            {activeModal === 'tools' && (
                <AssignedToolsModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                />
            )}

            {/* Data Operations Modal (Convert NIfTI, Upload to Orthanc, etc.) */}
            {activeModal === 'data_ops' && (
                <ToolsConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onConversionStart={handleConversionStart}
                    onUploadStart={handleUploadStart}
                    activeUploadTasks={uploadTasks}
                />
            )}

            {/* Models Modal */}
            {activeModal === 'models' && (
                <ModelsConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* DV Agents Modal */}
            {activeModal === 'agents' && (
                <DVAgentsConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                    onWorkflowStart={handleWorkflowStart}
                    onWorkflowUpdate={handleWorkflowUpdate}
                    workflowState={workflowState}
                />
            )}

            {/* Viewer Navbar Configuration Modal */}
            {activeModal === 'viewer_navbar' && (
                <ViewerNavbarConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Segmentation Preset Modal */}
            {activeModal === 'segmentation_preset' && (
                <SegmentationPresetConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Viewer Layout Modal */}
            {activeModal === 'viewer_layout' && (
                <ViewerLayoutConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Distribution Mode Modal */}
            {activeModal === 'distribution' && (
                <DistributionConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Study Type Modal (Standard vs Randomized) */}
            {activeModal === 'study_type' && (
                <StudyTypeConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

            {/* Reviewer's View Modal (Standard vs Flow) */}
            {activeModal === 'reviewer_view' && (
                <ReviewerViewConfigModal
                    studyId={studyId}
                    study={study}
                    onClose={() => setActiveModal(null)}
                    onRefreshData={onRefreshData}
                />
            )}

        </>
    );
};

const DistributionConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const [mode, setMode] = useState(study?.distribution_mode || 'shared');
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);

    const reviewers = (study?.assignments || []).map(a => a.user).filter(Boolean);
    const counts = study?.patient_assignment_counts || {};

    const handleSaveMode = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/distribution_mode/`, { mode });
            toast.success(`Distribution mode set to "${mode}".`);
            onRefreshData?.();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || 'Failed to update distribution mode.');
        } finally {
            setSaving(false);
        }
    };

    const handleRandomize = async () => {
        if (reviewers.length === 0) {
            toast.error('Assign at least one reviewer to the study first.');
            return;
        }
        if (!window.confirm('Randomize patient assignments across all reviewers? This will replace any existing per-patient assignments.')) return;
        setRunning(true);
        try {
            const resp = await API.post(`/studies/${studyId}/distribute_patients/`, {
                mode: 'randomized',
                replace: true,
            });
            toast.success(`Distributed ${resp.data.patients_distributed} patients across ${reviewers.length} reviewers.`);
            setMode('randomized');
            onRefreshData?.();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || 'Failed to randomize distribution.');
        } finally {
            setRunning(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><Shuffle size={20} style={{ marginRight: 8 }} /> Patient Distribution</h3>
                    <button className="modal-close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="modal-body">
                    <p className="text-muted" style={{ marginBottom: 12 }}>
                        Controls which patients each reviewer sees in this study.
                    </p>

                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label>
                            <input type="radio" name="dist-mode" value="shared" checked={mode === 'shared'} onChange={() => setMode('shared')} />
                            &nbsp;<strong>Shared</strong> — every reviewer sees every patient (default).
                        </label>
                        <label>
                            <input type="radio" name="dist-mode" value="manual" checked={mode === 'manual'} onChange={() => setMode('manual')} />
                            &nbsp;<strong>Manual</strong> — admin picks which patients each reviewer sees. Use the reviewer picker on the study page.
                        </label>
                        <label>
                            <input type="radio" name="dist-mode" value="randomized" checked={mode === 'randomized'} onChange={() => setMode('randomized')} />
                            &nbsp;<strong>Randomized</strong> — distribute patients evenly at random across all reviewers.
                        </label>
                    </div>

                    {reviewers.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <h5 style={{ marginBottom: 8 }}>Reviewers ({reviewers.length})</h5>
                            <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                                {reviewers.map(u => (
                                    <li key={u.id}>
                                        {u.username} — <span className="text-muted">{counts[u.id] || 0} patient{(counts[u.id] || 0) !== 1 ? 's' : ''} assigned</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-outline-primary" disabled={running || reviewers.length === 0} onClick={handleRandomize}>
                        {running ? 'Randomizing…' : 'Randomize now'}
                    </button>
                    <button className="btn btn-primary" disabled={saving || mode === study?.distribution_mode} onClick={handleSaveMode}>
                        {saving ? 'Saving…' : 'Save mode'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ReaderDashboardView — shown to reviewers on randomized studies. Mirrors the
// rag-platform reader UX: progress card + "Start next case" that resumes at the
// first uncompleted patient in the reviewer's queue. Admins always see the
// full StudyDashboard regardless of study_type.
const ReaderDashboardView = ({ study, studyId, studyData }) => {
    const navigate = useNavigate();
    const [queue, setQueue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [launching, setLaunching] = useState(false);

    const fetchQueue = useCallback(async () => {
        try {
            const res = await API.get(`/studies/${studyId}/my-queue/`);
            setQueue(res.data);
        } catch (err) {
            console.error('Failed to load reader queue:', err);
            toast.error('Failed to load your case queue.');
        } finally {
            setLoading(false);
        }
    }, [studyId]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    // Mirrors api/dv_viewers/views.py::_extract_original_patient_id. Items in
    // studyData may have renamed patient IDs (e.g. `case_10_kartagener_study1`
    // or `case_10_kartagener_s1_user`) when datasets are isolated per study/user,
    // but the randomized queue stores the bare original ID — strip those
    // suffixes before comparing.
    const extractOriginalPatientId = (pid) => {
        if (!pid) return pid;
        const userMatch = pid.match(/^(.+?)_s(\d+)_([a-zA-Z0-9_]+)$/);
        if (userMatch) return userMatch[1];
        const studyMatch = pid.match(/^(.+?)_study(\d+)$/);
        if (studyMatch) return studyMatch[1];
        return pid;
    };

    // Items in studyData that belong to this patient (after extracting the
    // original ID from any study/user-isolated copy).
    const itemsForPatient = (patientId) => (studyData || []).filter(
        (item) => extractOriginalPatientId(item.patient_id) === patientId
    );

    const isDicomItem = (item) => {
        if (item.source_model === 'MedicalImage') return true;
        if (item.source_model === 'PatientData') {
            return item.data_type === 'imaging' && item.original_file_type === 'dicom';
        }
        return false;
    };

    // Split a patient's items by what each viewer category consumes. Mirrors
    // the filtering used by the standard handleLaunchViewer in ViewerCard.
    const splitItems = (items) => {
        const dicom = { medicalImageIds: [], patientDataIds: [] };
        const regularImages = [];
        const documents = [];
        const signals = [];
        const ehr = [];
        const pathology = [];

        const regularImageExt = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];

        for (const item of items) {
            if (item.data_type === 'document') {
                documents.push(item);
                continue;
            }
            if (item.data_type === 'signal') {
                signals.push(item);
                continue;
            }
            if (item.data_type === 'ehr') {
                ehr.push(item);
                continue;
            }
            if (item.data_type === 'pathology') {
                pathology.push(item);
                continue;
            }
            if (isDicomItem(item)) {
                if (item.source_model === 'MedicalImage') {
                    dicom.medicalImageIds.push(item.id);
                } else {
                    dicom.patientDataIds.push(item.id);
                }
                continue;
            }
            const fp = (item.image || item.file || item.location || '').toLowerCase();
            const isRegularImage = regularImageExt.some((ext) => fp.endsWith(ext));
            const isImageDataType =
                item.data_type === 'image' ||
                (item.data_type === 'imaging' && item.original_file_type !== 'dicom');
            if (isRegularImage || isImageDataType) regularImages.push(item);
        }
        return { dicom, regularImages, documents, signals, ehr, pathology };
    };

    const launchOhif = async (viewerName, patientId, dicom) => {
        const response = await API.post('/viewers/launch_viewer/', {
            viewer_name: viewerName,
            data_ids: dicom.medicalImageIds,
            patient_data_ids: dicom.patientDataIds,
            study_id: studyId,
        });
        const viewerUrl = encodeURIComponent(response.data.viewer_url);
        const imageId = response.data.image_id || dicom.medicalImageIds[0] || dicom.patientDataIds[0] || '';
        let url = `/DV_Viewer?viewer_url=${viewerUrl}&study_id=${studyId}&patient_id=${encodeURIComponent(patientId)}&image_id=${imageId}&from_queue=1`;
        if (response.data.study_uids && response.data.study_uids.length > 0) {
            url += `&study_uids=${encodeURIComponent(response.data.study_uids.join(','))}`;
        }
        try { await ensureOrthancAuthCookie(); } catch (e) { /* viewer surfaces auth issues itself */ }
        return url;
    };

    const buildDv2Url = (patientId, split) => {
        const params = new URLSearchParams();
        params.append('patient_id', patientId);
        params.append('study_id', studyId);
        params.append('from_queue', '1');
        // For DV2 we send DICOM as MedicalImage ids — the standard launcher does
        // the same. PatientData DICOM rows are referenced via dicom_data too.
        const dicomItems = (studyData || []).filter(
            (i) => extractOriginalPatientId(i.patient_id) === patientId && isDicomItem(i)
        );
        if (dicomItems.length) {
            params.append(
                'dicom_data',
                dicomItems.map((i) => `${i.id}:${i.source_model || 'MedicalImage'}`).join(',')
            );
        }
        if (split.regularImages.length) {
            params.append(
                'image_data',
                split.regularImages.map((i) => `${i.id}:${i.source_model || 'MedicalImage'}`).join(',')
            );
        }
        if (split.documents.length) {
            params.append(
                'document_data',
                split.documents.map((i) => `${i.id}:${i.source_model || 'MedicalDocument'}`).join(',')
            );
        }
        if (split.signals.length) {
            params.append(
                'signal_data',
                split.signals.map((i) => `${i.id}:${i.source_model || 'PatientData'}`).join(',')
            );
        }
        return `/DV2?${params.toString()}`;
    };

    const buildImageViewerUrl = (split) => {
        if (split.regularImages.length === 0) return null;
        const imageData = split.regularImages
            .map((i) => `${i.id}:${i.source_model || 'MedicalImage'}`)
            .join(',');
        return `/Image_Viewer?image_data=${imageData}&study_id=${studyId}`;
    };

    const buildEhrUrl = (patientId, split) => {
        if (split.ehr.length === 0) return null;
        const params = new URLSearchParams();
        params.append('study_id', studyId);
        params.append('patient_ids', patientId);
        params.append(
            'ehr_data',
            split.ehr.map((i) => `${i.id}:${i.source_model || 'PatientData'}`).join(',')
        );
        return `/EHR_Viewer?${params.toString()}`;
    };

    const buildSignalUrl = (patientId, split) => {
        if (split.signals.length === 0) return null;
        const params = new URLSearchParams();
        params.append('study_id', studyId);
        params.append('patient_ids', patientId);
        params.append(
            'signal_data',
            split.signals.map((i) => `${i.id}:${i.source_model || 'PatientData'}`).join(',')
        );
        return `/Signal_Viewer?${params.toString()}`;
    };

    const buildDiscoveryViewerTestUrl = (patientId, split) => {
        if (split.regularImages.length === 0 || split.documents.length === 0) return null;
        const params = new URLSearchParams();
        params.append('patient_id', patientId);
        params.append('study_id', studyId);
        params.append(
            'image_data',
            split.regularImages.map((i) => `${i.id}:${i.source_model || 'MedicalImage'}`).join(',')
        );
        params.append(
            'document_data',
            split.documents.map((i) => `${i.id}:${i.source_model || 'MedicalDocument'}`).join(',')
        );
        return `/Discovery_Viewer_Test?${params.toString()}`;
    };

    const launchPme = async (patientId, split) => {
        // Mirrors the WSI filter from the standard launcher.
        const wsiExt = ['.svs', '.tiff', '.tif', '.ndpi'];
        const wsiItems = split.pathology.filter((item) => {
            if (item.is_map || item.is_annotation) return false;
            const fp = (item.file || item.filename || '').toLowerCase();
            return wsiExt.some((ext) => fp.endsWith(ext));
        });
        if (wsiItems.length === 0) return null;

        const toContainerPath = (fp) => `/discovery_data/private/${fp.replace(/^\/?media\//, '')}`;
        const slides = wsiItems.map((item) => {
            const fp = item.file || item.location || '';
            const fileName = fp.split('/').pop() || '';
            return {
                name: fileName.replace(/\.[^.]+$/, '') || 'Slide',
                file: toContainerPath(fp),
                stain: item.stain || item.metadata?.stain || '',
            };
        });
        const firstFile = wsiItems[0].file || wsiItems[0].location;
        const pmeUrl = `/pme/view_slide?file=${encodeURIComponent(toContainerPath(firstFile))}&slides=${encodeURIComponent(JSON.stringify(slides))}`;
        return `/DV_Viewer?viewer_url=${encodeURIComponent(pmeUrl)}&study_id=${studyId}&patient_id=${encodeURIComponent(patientId)}&from_queue=1`;
    };

    const handleStartNextCase = async () => {
        if (!queue?.next_patient_id) return;
        const patientId = queue.next_patient_id;
        const viewerType = study?.randomization_config?.viewer_type || 'ohif';

        const items = itemsForPatient(patientId);
        if (items.length === 0) {
            toast.error(`No data found for ${patientId} in this study.`);
            return;
        }
        const split = splitItems(items);

        setLaunching(true);
        try {
            await API.post(`/studies/${studyId}/case-started/${encodeURIComponent(patientId)}/`);

            let navUrl = null;
            if (viewerType.startsWith('custom-')) {
                // Custom viewer built in the Viewer Creation Lab. The renderer
                // (DvViewerScrollable) fetches studyData + queue itself, so
                // just pass the IDs through.
                const cid = viewerType.slice('custom-'.length);
                navUrl = `/DV_Viewer?custom_viewer_id=${encodeURIComponent(cid)}&study_id=${studyId}&patient_id=${encodeURIComponent(patientId)}&from_queue=1`;
            } else if (viewerType.startsWith('ohif')) {
                if (split.dicom.medicalImageIds.length === 0 && split.dicom.patientDataIds.length === 0) {
                    toast.error(`No DICOM imaging found for ${patientId}.`);
                    return;
                }
                navUrl = await launchOhif(viewerType, patientId, split.dicom);
            } else if (viewerType === 'dv2') {
                navUrl = buildDv2Url(patientId, split);
            } else if (viewerType === 'image-viewer') {
                navUrl = buildImageViewerUrl(split);
                if (!navUrl) {
                    toast.error(`No viewable images (JPG/PNG) found for ${patientId}.`);
                    return;
                }
            } else if (viewerType === 'ehr-viewer') {
                navUrl = buildEhrUrl(patientId, split);
                if (!navUrl) {
                    toast.error(`No EHR data found for ${patientId}.`);
                    return;
                }
            } else if (viewerType === 'signal-viewer') {
                navUrl = buildSignalUrl(patientId, split);
                if (!navUrl) {
                    toast.error(`No signal data found for ${patientId}.`);
                    return;
                }
            } else if (viewerType === 'discovery-viewer-test') {
                navUrl = buildDiscoveryViewerTestUrl(patientId, split);
                if (!navUrl) {
                    toast.error(`${patientId} needs both an image and a document for this viewer.`);
                    return;
                }
            } else if (viewerType === 'pme') {
                navUrl = await launchPme(patientId, split);
                if (!navUrl) {
                    toast.error(`No WSI slides found for ${patientId}.`);
                    return;
                }
            } else {
                toast.error(`Viewer "${viewerType}" is not supported for reader queues yet.`);
                return;
            }

            window.location.href = navUrl;
        } catch (err) {
            console.error('Failed to launch viewer:', err);
            toast.error('Failed to launch viewer for next case.');
        } finally {
            setLaunching(false);
        }
    };

    if (loading || !queue) {
        return (
            <div className="loading-card">
                <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
        );
    }

    const done = queue.completed;
    const total = queue.total;
    const allDone = total > 0 && done >= total;
    const inProgressCount = queue.queue.filter(q => q.started_at && !q.completed_at).length;

    return (
        <div className="container py-4">
            <button className="back-button mb-3" onClick={() => navigate(-1)}>
                <ArrowLeft size={18} /> Back
            </button>

            <div className="study-header-card">
                <div className="study-header-content">
                    <div className="study-info">
                        <p className="text-info mb-1 small">Randomized reader study</p>
                        <h1 className="study-title">{study?.name || ''}</h1>
                        <p className="study-description">
                            Continue where you left off. Cases are presented in a randomized order assigned to you.
                        </p>
                    </div>
                </div>
            </div>

            <div className="row g-3 mt-3">
                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="text-muted small">Cases completed</div>
                            <div className="display-5 fw-bold mt-2">{done} / {total}</div>
                            <div className="progress mt-3" style={{ height: 6 }}>
                                <div
                                    className="progress-bar bg-info"
                                    style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center gap-3">
                            <Clock size={32} className="text-warning" />
                            <div>
                                <div className="text-muted small">In progress</div>
                                <div className="h3 mb-0">{inProgressCount}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center gap-3">
                            <CheckCircle size={32} className="text-success" />
                            <div>
                                <div className="text-muted small">Remaining</div>
                                <div className="h3 mb-0">{Math.max(0, total - done)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-4">
                <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="mb-1">{allDone ? 'All cases completed' : 'Next assigned case'}</h3>
                        <p className="text-muted mb-0">
                            {allDone
                                ? 'You have finished every case in your queue. Thank you!'
                                : queue.next_patient_id
                                    ? `Patient ${queue.next_patient_id} — click to open the viewer.`
                                    : 'No cases are assigned to you yet.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary btn-lg"
                        onClick={handleStartNextCase}
                        disabled={allDone || launching || !queue.next_patient_id}
                    >
                        {launching ? 'Launching…' : 'Start next case'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Study Type Configuration Modal — Standard vs Randomized
// When randomized, admins can set an optional subset_size (cap per reviewer)
// and trigger a queue rebuild for already-assigned reviewers.
const StudyTypeConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const initialType = study?.study_type || 'standard';
    const initialSubsetSize = study?.randomization_config?.subset_size ?? '';
    const initialViewerType = study?.randomization_config?.viewer_type || 'ohif';

    const [studyType, setStudyType] = useState(initialType);
    const [subsetSize, setSubsetSize] = useState(initialSubsetSize);
    const [viewerType, setViewerType] = useState(initialViewerType);
    const [saving, setSaving] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [overview, setOverview] = useState(null);
    const [expandedUserId, setExpandedUserId] = useState(null);

    const loadOverview = useCallback(async () => {
        try {
            const res = await API.get(`/studies/${studyId}/queue-overview/`);
            setOverview(res.data);
        } catch (e) {
            // 403 for non-admins; that's fine — just skip the overview.
        }
    }, [studyId]);

    // Load admin overview when modal opens (so it shows up after switching from
    // standard → randomized too — the queues are built server-side on save).
    useEffect(() => {
        if (studyType === 'randomized') loadOverview();
    }, [loadOverview, studyType]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { study_type: studyType };
            if (studyType === 'randomized') {
                const parsed = parseInt(subsetSize, 10);
                payload.randomization_config = {
                    ...(study?.randomization_config || {}),
                    subset_size: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
                    viewer_type: viewerType,
                };
            }
            await API.patch(`/studies/${studyId}/`, payload);
            toast.success('Study type updated');
            if (studyType === 'randomized') await loadOverview();
            if (onRefreshData) await onRefreshData();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error('Failed to update study type');
        } finally {
            setSaving(false);
        }
    };

    const handleRebuild = async () => {
        if (!window.confirm("Rebuild every reviewer's queue with the new randomization config? Progress is preserved only for cases that remain in each new queue.")) return;
        setRebuilding(true);
        try {
            await API.post(`/studies/${studyId}/rebuild-queues/`);
            toast.success('Reviewer queues rebuilt');
            const res = await API.get(`/studies/${studyId}/queue-overview/`);
            setOverview(res.data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to rebuild queues');
        } finally {
            setRebuilding(false);
        }
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <Shuffle size={18} style={{ marginRight: 8, color: '#0dcaf0' }} />
                            Study Type
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted">
                            Choose how reviewers progress through cases in this study.
                        </p>

                        <div className="d-flex flex-column gap-2 mb-4">
                            <label className={`p-3 border rounded ${studyType === 'standard' ? 'border-primary bg-light' : ''}`} style={{ cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="study_type"
                                    value="standard"
                                    checked={studyType === 'standard'}
                                    onChange={() => setStudyType('standard')}
                                    className="me-2"
                                />
                                <strong>Standard study</strong>
                                <div className="text-muted small mt-1">
                                    Reviewers see the full case table and annotate cases in any order.
                                </div>
                            </label>

                            <label className={`p-3 border rounded ${studyType === 'randomized' ? 'border-primary bg-light' : ''}`} style={{ cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="study_type"
                                    value="randomized"
                                    checked={studyType === 'randomized'}
                                    onChange={() => setStudyType('randomized')}
                                    className="me-2"
                                />
                                <strong>Randomized study</strong>
                                <div className="text-muted small mt-1">
                                    Each reviewer gets a per-reviewer randomized queue and a "Start next case" dashboard that resumes where they left off.
                                </div>
                            </label>
                        </div>

                        {studyType === 'randomized' && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label">Subset size per reviewer (optional)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        placeholder="Leave blank to assign all cases"
                                        value={subsetSize}
                                        onChange={(e) => setSubsetSize(e.target.value)}
                                    />
                                    <div className="form-text">
                                        If set, each reviewer's queue is capped to this many randomly-selected cases. Leave blank to assign every case to every reviewer in a different shuffled order.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Reader viewer</label>
                                    <select
                                        className="form-select"
                                        value={viewerType}
                                        onChange={(e) => setViewerType(e.target.value)}
                                    >
                                        {(() => {
                                            // Filter by study's allowed_viewer_types (empty list = all allowed).
                                            const allowed = study?.allowed_viewer_types || [];
                                            const baseOptions = allowed.length === 0
                                                ? READER_VIEWER_OPTIONS
                                                : READER_VIEWER_OPTIONS.filter((v) => allowed.includes(v.value));
                                            // Custom viewers (created in the Viewer Creation Lab) live on the
                                            // study as viewers_detail. They're addressed as 'custom-{id}'.
                                            const customViewers = study?.viewers_detail || [];
                                            const customOptions = customViewers
                                                .filter((cv) => allowed.length === 0 || allowed.includes(`custom-${cv.id}`))
                                                .map((cv) => ({
                                                    value: `custom-${cv.id}`,
                                                    label: cv.name || `Custom #${cv.id}`,
                                                    category: "Custom",
                                                }));
                                            const options = [...baseOptions, ...customOptions];
                                            const grouped = options.reduce((acc, v) => {
                                                (acc[v.category] = acc[v.category] || []).push(v);
                                                return acc;
                                            }, {});
                                            return Object.entries(grouped).map(([category, vs]) => (
                                                <optgroup key={category} label={category}>
                                                    {vs.map((v) => (
                                                        <option key={v.value} value={v.value}>{v.label}</option>
                                                    ))}
                                                </optgroup>
                                            ));
                                        })()}
                                    </select>
                                    <div className="form-text">
                                        The viewer launched when a reviewer clicks "Start next case". Limited to the viewers enabled in this study's "Configure Allowed Viewers" settings.
                                    </div>
                                </div>

                                {overview !== null && (
                                    <div className="mt-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0">Reviewer queues</h6>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleRebuild}
                                                disabled={rebuilding}
                                            >
                                                {rebuilding ? 'Rebuilding…' : 'Rebuild queues'}
                                            </button>
                                        </div>
                                        {overview.length === 0 ? (
                                            <p className="text-muted small mb-0">No reviewers assigned yet.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-sm align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 30 }}></th>
                                                            <th>Reviewer</th>
                                                            <th>Progress</th>
                                                            <th>In progress</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {overview.map((row) => {
                                                            const isExpanded = expandedUserId === row.user.id;
                                                            return (
                                                                <React.Fragment key={row.user.id}>
                                                                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedUserId(isExpanded ? null : row.user.id)}>
                                                                        <td>{isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                                                                        <td>{row.user.username}</td>
                                                                        <td>{row.completed} / {row.total}</td>
                                                                        <td>{row.in_progress}</td>
                                                                    </tr>
                                                                    {isExpanded && (
                                                                        <tr>
                                                                            <td></td>
                                                                            <td colSpan={3}>
                                                                                <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                                                                                    {row.queue.length === 0 ? (
                                                                                        <span className="text-muted small">No cases in queue.</span>
                                                                                    ) : (
                                                                                        <ol className="mb-0 small" style={{ paddingLeft: 20 }}>
                                                                                            {row.queue.map((c) => {
                                                                                                const status = c.completed_at
                                                                                                    ? 'done'
                                                                                                    : c.started_at
                                                                                                        ? 'in progress'
                                                                                                        : 'pending';
                                                                                                const color = c.completed_at ? '#198754' : c.started_at ? '#fd7e14' : '#6c757d';
                                                                                                return (
                                                                                                    <li key={c.order_index} className="d-flex justify-content-between">
                                                                                                        <span>{c.patient_id}</span>
                                                                                                        <span style={{ color }} className="small">{status}</span>
                                                                                                    </li>
                                                                                                );
                                                                                            })}
                                                                                        </ol>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reviewer's View — Standard (full table) vs Flow (queue-based "Start next case").
// Internally still maps to study_type 'standard' | 'randomized' so the existing
// ReaderDashboardView trigger keeps working. When Flow is on, admins pick which
// viewer launches for "Start next case" and can see a per-reviewer progress table.
const ReviewerViewConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const initialType = study?.study_type || 'standard';
    // Pick the initial dropdown value. The trick is the saved `viewer_type`
    // may not be present in the study's allowed_viewer_types list — when an
    // admin has narrowed Configure Allowed Viewers down to only custom-N and
    // the saved viewer is still the pre-restriction default ("ohif"), the
    // <select> shows custom-N visually (it's the only option) but the React
    // state remains "ohif" until the user actively changes it. Saving then
    // re-persists the stale "ohif" and the reader queue routes to the wrong
    // viewer. So: if the saved value isn't in the option set, fall back to
    // the first allowed/custom option and let the user save through.
    const initialViewerType = (() => {
        const allowed = study?.allowed_viewer_types || [];
        const customViewers = (study?.viewers_detail || []).filter(
            (v) => v && v.viewer_type === 'custom'
        );
        const isAllowed = (val) => {
            if (allowed.length === 0) return true;
            return allowed.includes(val);
        };
        const firstCustom = customViewers.find((cv) => isAllowed(`custom-${cv.id}`));

        const explicit = study?.randomization_config?.viewer_type;
        // If the saved value is still in the allowed set, honor it as-is.
        if (explicit && isAllowed(explicit)) return explicit;
        // Otherwise prefer a custom viewer when one exists, else fall back to
        // the first allowed base viewer, else OHIF (legacy default).
        if (firstCustom) return `custom-${firstCustom.id}`;
        const firstBase = (READER_VIEWER_OPTIONS || []).find((v) => isAllowed(v.value));
        if (firstBase) return firstBase.value;
        return 'ohif';
    })();

    const [reviewerView, setReviewerView] = useState(initialType);
    const [viewerType, setViewerType] = useState(initialViewerType);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { study_type: reviewerView };
            if (reviewerView === 'randomized') {
                payload.randomization_config = {
                    ...(study?.randomization_config || {}),
                    viewer_type: viewerType,
                };
            }
            await API.patch(`/studies/${studyId}/`, payload);
            toast.success("Reviewer's view updated");
            if (onRefreshData) await onRefreshData();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update reviewer's view");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <Eye size={18} style={{ marginRight: 8, color: '#0dcaf0' }} />
                            Reviewer's View
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted">
                            Choose how reviewers see and step through cases in this study.
                        </p>

                        <div className="d-flex flex-column gap-2 mb-4">
                            <label className={`p-3 border rounded ${reviewerView === 'standard' ? 'border-primary bg-light' : ''}`} style={{ cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="reviewer_view"
                                    value="standard"
                                    checked={reviewerView === 'standard'}
                                    onChange={() => setReviewerView('standard')}
                                    className="me-2"
                                />
                                <strong>Standard</strong>
                                <div className="text-muted small mt-1">
                                    Reviewers see the full case table and pick any patient/viewer at will.
                                </div>
                            </label>

                            <label className={`p-3 border rounded ${reviewerView === 'randomized' ? 'border-primary bg-light' : ''}`} style={{ cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="reviewer_view"
                                    value="randomized"
                                    checked={reviewerView === 'randomized'}
                                    onChange={() => setReviewerView('randomized')}
                                    className="me-2"
                                />
                                <strong>Flow</strong>
                                <div className="text-muted small mt-1">
                                    Reviewers see only a progress dashboard with a "Start next case" button. The viewer opens automatically on the next pending patient.
                                </div>
                            </label>
                        </div>

                        {reviewerView === 'randomized' && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label">Viewer launched for "Start next case"</label>
                                    <select
                                        className="form-select"
                                        value={viewerType}
                                        onChange={(e) => setViewerType(e.target.value)}
                                    >
                                        {(() => {
                                            const allowed = study?.allowed_viewer_types || [];
                                            const baseOptions = allowed.length === 0
                                                ? READER_VIEWER_OPTIONS
                                                : READER_VIEWER_OPTIONS.filter((v) => allowed.includes(v.value));
                                            const customViewers = study?.viewers_detail || [];
                                            const customOptions = customViewers
                                                .filter((cv) => allowed.length === 0 || allowed.includes(`custom-${cv.id}`))
                                                .map((cv) => ({
                                                    value: `custom-${cv.id}`,
                                                    label: cv.name || `Custom #${cv.id}`,
                                                    category: "Custom",
                                                }));
                                            const options = [...baseOptions, ...customOptions];
                                            const grouped = options.reduce((acc, v) => {
                                                (acc[v.category] = acc[v.category] || []).push(v);
                                                return acc;
                                            }, {});
                                            return Object.entries(grouped).map(([category, vs]) => (
                                                <optgroup key={category} label={category}>
                                                    {vs.map((v) => (
                                                        <option key={v.value} value={v.value}>{v.label}</option>
                                                    ))}
                                                </optgroup>
                                            ));
                                        })()}
                                    </select>
                                    <div className="form-text">
                                        Limited to viewers enabled in this study's "Configure Allowed Viewers" settings. To control how many patients each reviewer reviews, use the Distribution modal.
                                    </div>
                                </div>

                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Label Studio Modal - Create/manage Label Studio projects for spatial/temporal annotation
const LabelStudioModal = ({ studyId, study, onClose }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [provisioning, setProvisioning] = useState(false);
    const [accountLinked, setAccountLinked] = useState(null); // null=checking, true/false
    const [dataType, setDataType] = useState('image');
    const [customLabels, setCustomLabels] = useState('');

    const lsBaseUrl = `${window.location.origin}/labelstudio`;

    useEffect(() => {
        checkAccountAndFetch();
    }, []);

    const checkAccountAndFetch = async () => {
        try {
            const statusResp = await API.get('/labelstudio/account/status/');
            setAccountLinked(statusResp.data.has_token);
        } catch {
            setAccountLinked(false);
        }
        fetchProjects();
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await API.get('/labelstudio/projects/');
            const studyProjects = response.data.filter(p => p.study === parseInt(studyId));
            setProjects(studyProjects);
        } catch (error) {
            console.error('Failed to fetch LS projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProvision = async () => {
        try {
            setProvisioning(true);
            const resp = await API.post('/labelstudio/account/provision/');
            setAccountLinked(true);
            toast.success(resp.data.message);
        } catch (error) {
            const msg = error.response?.data?.error || 'Failed to link account';
            toast.error(msg);
        } finally {
            setProvisioning(false);
        }
    };

    const handleCreate = async () => {
        try {
            setCreating(true);
            const payload = {
                study_id: parseInt(studyId),
                data_type: dataType,
            };
            if (customLabels.trim()) {
                payload.labels = customLabels.split(',').map(l => l.trim()).filter(Boolean);
            }
            await API.post('/labelstudio/projects/create/', payload);
            toast.success('Label Studio project created!');
            fetchProjects();
        } catch (error) {
            const msg = error.response?.data?.error || 'Failed to create project';
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleSync = async (projectId) => {
        try {
            await API.post(`/labelstudio/projects/${projectId}/sync/`);
            toast.info('Data sync started...');
            fetchProjects();
        } catch (error) {
            toast.error('Failed to start sync');
        }
    };

    return ReactDOM.createPortal(
        <div className="modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
                <div className="modal-header" style={{ borderBottom: '3px solid #ff6b35' }}>
                    <h4><Layers size={20} style={{ marginRight: 8, color: '#ff6b35' }} /> Label Studio</h4>
                    <button className="modal-close-btn" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body" style={{ padding: '20px' }}>

                    {/* Account linking section */}
                    {accountLinked === false && (
                        <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(255,193,7,0.08)', borderRadius: '8px', border: '1px solid rgba(255,193,7,0.3)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h5 style={{ margin: '0 0 4px 0', color: '#ffc107' }}>Link Your Account</h5>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#aaa' }}>
                                        Auto-create a Label Studio account linked to your Discovery login.
                                    </p>
                                </div>
                                <button onClick={handleProvision} disabled={provisioning}
                                    style={{ padding: '8px 20px', borderRadius: '6px', background: '#ffc107', color: '#000', border: 'none', cursor: provisioning ? 'wait' : 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                    {provisioning ? 'Linking...' : 'Link Account'}
                                </button>
                            </div>
                        </div>
                    )}

                    {accountLinked === true && (
                        <div style={{ marginBottom: '20px', padding: '10px 16px', background: 'rgba(40,167,69,0.08)', borderRadius: '8px', border: '1px solid rgba(40,167,69,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#28a745', fontSize: '0.9rem' }}>Account linked</span>
                            <a href={lsBaseUrl} target="_blank" rel="noopener noreferrer"
                                style={{ padding: '6px 16px', borderRadius: '6px', background: '#ff6b35', color: '#fff', textDecoration: 'none', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center' }}>
                                <ExternalLink size={14} style={{ marginRight: 6 }} /> Open Label Studio
                            </a>
                        </div>
                    )}

                    {/* Create new project */}
                    <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(255,107,53,0.05)', borderRadius: '8px', border: '1px solid rgba(255,107,53,0.2)' }}>
                        <h5 style={{ margin: '0 0 12px 0', color: '#ff6b35' }}>Create Label Studio Project</h5>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'end', flexWrap: 'wrap' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '4px' }}>Data Type</label>
                                <select value={dataType} onChange={e => setDataType(e.target.value)}
                                    style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #444', background: '#1a1a2e', color: '#fff' }}>
                                    <option value="image">Images (JPG/PNG)</option>
                                    <option value="signal">Signals (CSV/EDF)</option>
                                    <option value="document">Documents (Text/PDF)</option>
                                    <option value="video">Video (MP4)</option>
                                    <option value="table">Tables (CSV)</option>
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '4px' }}>Custom Labels (comma-separated, optional)</label>
                                <input type="text" value={customLabels} onChange={e => setCustomLabels(e.target.value)}
                                    placeholder="e.g., Tumor, Normal, Artifact"
                                    style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #444', background: '#1a1a2e', color: '#fff' }} />
                            </div>
                            <button onClick={handleCreate} disabled={creating}
                                style={{ padding: '6px 16px', borderRadius: '6px', background: '#ff6b35', color: '#fff', border: 'none', cursor: creating ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                                {creating ? 'Creating...' : 'Create Project'}
                            </button>
                        </div>
                    </div>

                    {/* Existing projects */}
                    <h5 style={{ margin: '0 0 12px 0' }}>Linked Projects</h5>
                    {loading ? (
                        <p style={{ color: '#888' }}>Loading...</p>
                    ) : projects.length === 0 ? (
                        <p style={{ color: '#888' }}>No Label Studio projects linked to this study yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {projects.map(p => (
                                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <div>
                                        <strong>LS Project #{p.ls_project_id}</strong>
                                        <span style={{ marginLeft: '12px', fontSize: '0.85rem', color: p.sync_status === 'synced' ? '#28a745' : p.sync_status === 'error' ? '#dc3545' : '#ffc107' }}>
                                            {p.sync_status}
                                        </span>
                                        <span style={{ marginLeft: '12px', fontSize: '0.85rem', color: '#888' }}>
                                            {p.task_count} tasks
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => handleSync(p.id)}
                                            style={{ padding: '4px 12px', borderRadius: '4px', background: '#17a2b8', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>
                                            <RefreshCw size={14} style={{ marginRight: 4 }} /> Sync Data
                                        </button>
                                        <a href={`${lsBaseUrl}/projects/${p.ls_project_id}/`} target="_blank" rel="noopener noreferrer"
                                            style={{ padding: '4px 12px', borderRadius: '4px', background: '#ff6b35', color: '#fff', textDecoration: 'none', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>
                                            <ExternalLink size={14} style={{ marginRight: 4 }} /> Open
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// Assigned Tools Modal - Shows containerized tools assigned to this study via project
const AssignedToolsModal = ({ studyId, study, onClose }) => {
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTools();
    }, [studyId]);

    const fetchTools = async () => {
        try {
            setLoading(true);
            const response = await API.get('/ai-models/models/', {
                params: { study: studyId, entity_type: 'tool' }
            });
            setTools(response.data);
        } catch (err) {
            console.error('Error fetching tools:', err);
        } finally {
            setLoading(false);
        }
    };

    const getBuildStatusBadge = (buildStatus) => {
        const statusConfig = {
            pending: { class: 'bg-secondary', label: 'Pending' },
            building: { class: 'bg-info', label: 'Building' },
            ready: { class: 'bg-success', label: 'Ready' },
            failed: { class: 'bg-danger', label: 'Failed' },
        };
        const config = statusConfig[buildStatus] || statusConfig.pending;
        return <span className={`badge ${config.class}`}>{config.label}</span>;
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal tools-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header">
                    <h3><Wrench size={20} /> Tools</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="config-modal-body">
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : tools.length === 0 ? (
                        <div className="text-center py-4" style={{ color: '#adb5bd' }}>
                            <Wrench size={40} style={{ opacity: 0.4, marginBottom: '12px' }} />
                            <p style={{ margin: 0 }}>No tools assigned to this study yet.</p>
                            <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>
                                Assign tools from the project's Tools tab.
                            </p>
                        </div>
                    ) : (
                        <div className="tool-section">
                            {tools.map((tool) => (
                                <div key={tool.id} className="tool-item" style={{ cursor: 'default' }}>
                                    <div className="tool-info">
                                        <div className="tool-title">
                                            <i
                                                className={`fa ${tool.icon || 'fa-wrench'}`}
                                                style={{ color: tool.color || '#6c757d', fontSize: '1.1rem' }}
                                            ></i>
                                            <span>{tool.name}</span>
                                            <span style={{ marginLeft: '8px' }}>{getBuildStatusBadge(tool.build_status)}</span>
                                        </div>
                                        {tool.description && (
                                            <p className="tool-description">{tool.description}</p>
                                        )}
                                        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                                            Type: {tool.model_type_display || tool.model_type || 'Tool'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Data Operations Modal - Convert NIfTI, Upload to Orthanc, etc.
const ToolsConfigModal = ({ studyId, study, onClose, onConversionStart, onUploadStart, activeUploadTasks = {} }) => {
    const [startingConvert, setStartingConvert] = useState(false);
    const [startingUpload, setStartingUpload] = useState(false);
    const [startingAnnotationUpload, setStartingAnnotationUpload] = useState(false);
    const [startingMapUpload, setStartingMapUpload] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedAnnotationUserId, setSelectedAnnotationUserId] = useState('');
    const [selectedMapUserId, setSelectedMapUserId] = useState('');
    const [forceReconvert, setForceReconvert] = useState(false);

    // Check if the selected user already has an active upload task
    const selectedUserHasActiveTask = selectedUserId && activeUploadTasks[selectedUserId];
    const defaultUploadActive = !selectedUserId && activeUploadTasks['default'];
    const annUserHasActiveTask = selectedAnnotationUserId && activeUploadTasks[`ann_${selectedAnnotationUserId}`];
    const annDefaultActive = !selectedAnnotationUserId && activeUploadTasks['ann_default'];
    const mapUserHasActiveTask = selectedMapUserId && activeUploadTasks[`map_${selectedMapUserId}`];
    const mapDefaultActive = !selectedMapUserId && activeUploadTasks['map_default'];

    // Get assigned users from study (reviewers who can have isolated data)
    const assignedUsers = React.useMemo(() => {
        if (!study?.assignments) return [];
        return study.assignments.map(a => ({
            id: a.user.id,
            username: a.user.username,
            role: a.role
        }));
    }, [study]);

    const handleConvertAll = async () => {
        setStartingConvert(true);
        setError(null);

        try {
            const response = await API.post('/viewers/batch_convert_nifti/', {
                study_id: studyId,
                force: forceReconvert
            });

            if (response.data.status === 'completed') {
                toast.info(response.data.message || 'No NIfTI files need conversion');
                onClose();
            } else if (response.data.task_id) {
                onConversionStart(response.data.task_id);
            }
        } catch (err) {
            setStartingConvert(false);
            setError(err.response?.data?.error || 'Failed to start conversion');
            toast.error('Failed to start conversion');
        }
    };

    const handleUploadToOrthanc = async () => {
        setStartingUpload(true);
        setError(null);

        try {
            const requestBody = {
                study_id: studyId
            };

            // If a user is selected, include target_user_id for isolated upload
            if (selectedUserId) {
                requestBody.target_user_id = parseInt(selectedUserId, 10);
            }

            const response = await API.post('/viewers/batch_upload_to_orthanc/', requestBody);

            if (response.data.status === 'completed') {
                toast.info(response.data.message || 'No files need uploading to Orthanc');
                setStartingUpload(false);
                setSelectedUserId(''); // Reset for next upload
            } else if (response.data.task_id) {
                // Pass task ID, target user ID, and username for display
                const selectedUser = assignedUsers.find(u => u.id === parseInt(selectedUserId, 10));
                onUploadStart(response.data.task_id, selectedUserId || null, selectedUser?.username || null);
                setStartingUpload(false);
                setSelectedUserId(''); // Reset for next upload - allows starting another upload for different user
            }
        } catch (err) {
            setStartingUpload(false);
            setError(err.response?.data?.error || 'Failed to start upload');
            toast.error('Failed to start upload');
        }
    };

    const handlePreuploadAnnotations = async () => {
        setStartingAnnotationUpload(true);
        setError(null);

        try {
            const requestBody = { study_id: studyId };
            if (selectedAnnotationUserId) {
                requestBody.target_user_id = parseInt(selectedAnnotationUserId, 10);
            }

            const response = await API.post('/viewers/batch_preupload_annotations/', requestBody);

            if (response.data.status === 'completed') {
                toast.info(response.data.message || 'No annotations need uploading');
                setStartingAnnotationUpload(false);
                setSelectedAnnotationUserId('');
            } else if (response.data.task_id) {
                const taskKey = selectedAnnotationUserId ? `ann_${selectedAnnotationUserId}` : 'ann_default';
                const selectedUser = assignedUsers.find(u => u.id === parseInt(selectedAnnotationUserId, 10));
                onUploadStart(response.data.task_id, taskKey, selectedUser?.username ? `Annotations (${selectedUser.username})` : 'Annotations');
                setStartingAnnotationUpload(false);
                setSelectedAnnotationUserId('');
            }
        } catch (err) {
            setStartingAnnotationUpload(false);
            setError(err.response?.data?.error || 'Failed to start annotation upload');
            toast.error('Failed to start annotation upload');
        }
    };

    const handlePreuploadMaps = async () => {
        setStartingMapUpload(true);
        setError(null);

        try {
            const requestBody = { study_id: studyId };
            if (selectedMapUserId) {
                requestBody.target_user_id = parseInt(selectedMapUserId, 10);
            }

            const response = await API.post('/viewers/batch_preupload_maps/', requestBody);

            if (response.data.status === 'completed') {
                toast.info(response.data.message || 'No maps need uploading');
                setStartingMapUpload(false);
                setSelectedMapUserId('');
            } else if (response.data.task_id) {
                const taskKey = selectedMapUserId ? `map_${selectedMapUserId}` : 'map_default';
                const selectedUser = assignedUsers.find(u => u.id === parseInt(selectedMapUserId, 10));
                onUploadStart(response.data.task_id, taskKey, selectedUser?.username ? `Maps (${selectedUser.username})` : 'Maps');
                setStartingMapUpload(false);
                setSelectedMapUserId('');
            }
        } catch (err) {
            setStartingMapUpload(false);
            setError(err.response?.data?.error || 'Failed to start map upload');
            toast.error('Failed to start map upload');
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal tools-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header">
                    <h3><Wrench size={20} /> Data Ops</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="config-modal-body">
                    <div className="tool-section">
                        {/* Tool 1: Convert NIfTI to DICOM */}
                        <div className="tool-item">
                            <div className="tool-info">
                                <div className="tool-title">
                                    <RefreshCw size={18} />
                                    <span>Convert NIfTI to DICOM</span>
                                </div>
                                <p className="tool-description">
                                    Convert all NIfTI files in this study's datasets to DICOM format.
                                    This prepares the files for viewing but does not upload them to Orthanc.
                                </p>

                                {/* Force re-convert option */}
                                <div className="tool-option">
                                    <label className="tool-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={forceReconvert}
                                            onChange={(e) => setForceReconvert(e.target.checked)}
                                            disabled={startingConvert}
                                        />
                                        <span>Force re-convert</span>
                                    </label>
                                    {forceReconvert && (
                                        <small className="tool-hint" style={{ color: '#dc3545' }}>
                                            Warning: This will delete existing DICOM files and re-convert from NIfTI.
                                            Use this to fix DICOM metadata issues.
                                        </small>
                                    )}
                                </div>
                            </div>

                            <button
                                className="tool-action-btn"
                                onClick={handleConvertAll}
                                disabled={startingConvert || startingUpload}
                            >
                                {startingConvert ? 'Starting...' : forceReconvert ? 'Re-convert' : 'Convert'}
                            </button>
                        </div>

                        {/* Tool 2: Upload to Orthanc */}
                        <div className="tool-item">
                            <div className="tool-info">
                                <div className="tool-title">
                                    <Layers size={18} />
                                    <span>Upload to Orthanc</span>
                                </div>
                                <p className="tool-description">
                                    Upload converted DICOM files to Orthanc server for viewing in OHIF.
                                    Select a reviewer to create isolated copies for that user.
                                </p>

                                {/* User selector for isolated upload */}
                                {assignedUsers.length > 0 && (
                                    <div className="tool-option">
                                        <label htmlFor="target-user">
                                            <Users size={14} />
                                            Upload for reviewer:
                                        </label>
                                        <select
                                            id="target-user"
                                            value={selectedUserId}
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                            disabled={startingUpload}
                                            className="tool-select"
                                        >
                                            <option value="">-- All (raw DICOM) --{activeUploadTasks['default'] ? ' (uploading...)' : ''}</option>
                                            {assignedUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.username} ({user.role}){activeUploadTasks[String(user.id)] ? ' (uploading...)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedUserId && !selectedUserHasActiveTask && (
                                            <small className="tool-hint">
                                                Creates isolated DICOM copies visible only to this reviewer in OHIF.
                                            </small>
                                        )}
                                        {selectedUserHasActiveTask && (
                                            <small className="tool-hint" style={{ color: '#007bff' }}>
                                                Upload already in progress for this user. Select a different user or wait for completion.
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                className="tool-action-btn"
                                onClick={handleUploadToOrthanc}
                                disabled={startingConvert || startingUpload || selectedUserHasActiveTask || defaultUploadActive}
                                title={selectedUserHasActiveTask ? 'Upload already in progress for this user' : defaultUploadActive ? 'Default upload already in progress' : ''}
                            >
                                {startingUpload ? 'Starting...' : selectedUserHasActiveTask ? 'In Progress' : 'Upload'}
                            </button>
                        </div>

                        {/* Tool 3: Pre-upload Annotations */}
                        <div className="tool-item">
                            <div className="tool-info">
                                <div className="tool-title">
                                    <Layers size={18} style={{ color: '#fd7e14' }} />
                                    <span>Pre-upload Annotations</span>
                                </div>
                                <p className="tool-description">
                                    Convert and upload all annotations (segmentation masks) to Orthanc for the selected reviewer.
                                    Equivalent to clicking "Load Mask" for every patient in the study.
                                </p>

                                {assignedUsers.length > 0 && (
                                    <div className="tool-option">
                                        <label htmlFor="ann-target-user">
                                            <Users size={14} />
                                            Upload for reviewer:
                                        </label>
                                        <select
                                            id="ann-target-user"
                                            value={selectedAnnotationUserId}
                                            onChange={(e) => setSelectedAnnotationUserId(e.target.value)}
                                            disabled={startingAnnotationUpload}
                                            className="tool-select"
                                        >
                                            <option value="">-- Study copies --{activeUploadTasks['ann_default'] ? ' (uploading...)' : ''}</option>
                                            {assignedUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.username} ({user.role}){activeUploadTasks[`ann_${user.id}`] ? ' (uploading...)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {annUserHasActiveTask && (
                                            <small className="tool-hint" style={{ color: '#007bff' }}>
                                                Annotation upload already in progress for this user.
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                className="tool-action-btn"
                                onClick={handlePreuploadAnnotations}
                                disabled={startingAnnotationUpload || annUserHasActiveTask || annDefaultActive}
                                title={annUserHasActiveTask ? 'Upload already in progress' : ''}
                            >
                                {startingAnnotationUpload ? 'Starting...' : annUserHasActiveTask ? 'In Progress' : 'Upload'}
                            </button>
                        </div>

                        {/* Tool 4: Pre-upload Maps */}
                        <div className="tool-item">
                            <div className="tool-info">
                                <div className="tool-title">
                                    <MapIcon size={18} style={{ color: '#20c997' }} />
                                    <span>Pre-upload Maps</span>
                                </div>
                                <p className="tool-description">
                                    Convert and upload all probability maps to Orthanc as RGB DICOM series for the selected reviewer.
                                    Equivalent to clicking "Load Map" for every patient in the study.
                                </p>

                                {assignedUsers.length > 0 && (
                                    <div className="tool-option">
                                        <label htmlFor="map-target-user">
                                            <Users size={14} />
                                            Upload for reviewer:
                                        </label>
                                        <select
                                            id="map-target-user"
                                            value={selectedMapUserId}
                                            onChange={(e) => setSelectedMapUserId(e.target.value)}
                                            disabled={startingMapUpload}
                                            className="tool-select"
                                        >
                                            <option value="">-- Study copies --{activeUploadTasks['map_default'] ? ' (uploading...)' : ''}</option>
                                            {assignedUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.username} ({user.role}){activeUploadTasks[`map_${user.id}`] ? ' (uploading...)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {mapUserHasActiveTask && (
                                            <small className="tool-hint" style={{ color: '#007bff' }}>
                                                Map upload already in progress for this user.
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                className="tool-action-btn"
                                onClick={handlePreuploadMaps}
                                disabled={startingMapUpload || mapUserHasActiveTask || mapDefaultActive}
                                title={mapUserHasActiveTask ? 'Upload already in progress' : ''}
                            >
                                {startingMapUpload ? 'Starting...' : mapUserHasActiveTask ? 'In Progress' : 'Upload'}
                            </button>
                        </div>

                        {error && (
                            <div className="tool-error">
                                {error}
                            </div>
                        )}

                        {/* Active uploads summary */}
                        {Object.keys(activeUploadTasks).length > 0 && (
                            <div className="active-uploads-summary">
                                <div className="active-uploads-header">
                                    <RefreshCw size={14} className="spinning" />
                                    <span>Active Uploads ({Object.keys(activeUploadTasks).length})</span>
                                </div>
                                <div className="active-uploads-list">
                                    {Object.entries(activeUploadTasks).map(([key, task]) => (
                                        <div key={key} className="active-upload-item">
                                            <Upload size={12} />
                                            <span>{task.username || 'Raw DICOM'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Models Configuration Modal - Run AI models on study data
const ModelsConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningModels, setRunningModels] = useState({});
    const [error, setError] = useState(null);
    const [togglingServer, setTogglingServer] = useState(null);
    const [gpuInputs, setGpuInputs] = useState({});

    useEffect(() => {
        fetchModels();
    }, [studyId]);

    // Initialize GPU inputs from model data
    useEffect(() => {
        const inputs = {};
        models.forEach(m => {
            if (!(m.id in gpuInputs)) {
                inputs[m.id] = m.gpu_device_ids || '0';
            }
        });
        if (Object.keys(inputs).length > 0) {
            setGpuInputs(prev => ({ ...inputs, ...prev }));
        }
    }, [models]);

    // Poll for server status changes when any model is starting
    useEffect(() => {
        const hasStarting = models.some(m => m.keep_loaded && m.server_status === 'starting');
        if (!hasStarting) return;

        const interval = setInterval(async () => {
            try {
                const response = await API.get(`/ai-models/models/`, {
                    params: { study: studyId }
                });
                setModels(response.data || []);
                const stillStarting = (response.data || []).some(m => m.keep_loaded && m.server_status === 'starting');
                if (!stillStarting) clearInterval(interval);
            } catch (err) {
                // ignore polling errors
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [models.some(m => m.keep_loaded && m.server_status === 'starting')]);

    const fetchModels = async () => {
        setLoading(true);
        try {
            const response = await API.get(`/ai-models/models/`, {
                params: { study: studyId }
            });
            setModels(response.data || []);
        } catch (err) {
            console.error('Error fetching models:', err);
            setError('Failed to load models');
        } finally {
            setLoading(false);
        }
    };

    const handleRunOnAll = async (model) => {
        setRunningModels(prev => ({ ...prev, [model.id]: true }));
        try {
            // Get all patients in the study
            const patientsResponse = await API.get(`/studies/${studyId}/patients/`);
            const patients = patientsResponse.data || [];

            if (patients.length === 0) {
                toast.warning('No patients found in this study');
                return;
            }

            toast.info(`Starting ${model.name} on ${patients.length} patients...`);

            // Run model on each patient (in parallel batches)
            const batchSize = 5;
            for (let i = 0; i < patients.length; i += batchSize) {
                const batch = patients.slice(i, i + batchSize);
                await Promise.all(batch.map(patient =>
                    API.post(`/ai-models/models/${model.id}/run/`, {
                        patient_id: patient.patient_id,
                        study_id: studyId
                    }).catch(err => {
                        console.error(`Error running model on ${patient.patient_id}:`, err);
                    })
                ));
            }

            toast.success(`${model.name} started on all patients`);
            if (onRefreshData) onRefreshData();
        } catch (err) {
            console.error('Error running model:', err);
            toast.error('Failed to run model');
        } finally {
            setRunningModels(prev => ({ ...prev, [model.id]: false }));
        }
    };

    const handleToggleKeepLoaded = async (model) => {
        const newValue = !model.keep_loaded;
        setTogglingServer(model.id);
        try {
            const gpuIds = gpuInputs[model.id] || model.gpu_device_ids || '0';
            const data = { enable: newValue, gpu_device_ids: gpuIds };
            await API.post(`/ai-models/models/${model.id}/toggle-keep-loaded/`, data);
            setModels(prev =>
                prev.map(m =>
                    m.id === model.id
                        ? { ...m, keep_loaded: newValue, server_status: newValue ? 'starting' : 'stopped', gpu_device_ids: gpuIds }
                        : m
                )
            );
            toast.success(newValue ? `Starting persistent server for ${model.name} on GPUs ${gpuIds}...` : `Stopping server for ${model.name}...`);
        } catch (err) {
            console.error('Error toggling keep loaded:', err);
            toast.error('Failed to toggle server');
        } finally {
            setTogglingServer(null);
        }
    };

    const handleRestartWithNewGpus = async (model) => {
        const gpuIds = gpuInputs[model.id] || model.gpu_device_ids || '0';
        setTogglingServer(model.id);
        try {
            // Stop current server
            await API.post(`/ai-models/models/${model.id}/toggle-keep-loaded/`, { enable: false });
            // Start with new GPUs
            await API.post(`/ai-models/models/${model.id}/toggle-keep-loaded/`, { enable: true, gpu_device_ids: gpuIds });
            setModels(prev =>
                prev.map(m =>
                    m.id === model.id
                        ? { ...m, keep_loaded: true, server_status: 'starting', gpu_device_ids: gpuIds }
                        : m
                )
            );
            toast.success(`Restarting server on GPUs ${gpuIds}...`);
        } catch (err) {
            console.error('Error restarting server:', err);
            toast.error('Failed to restart server');
        } finally {
            setTogglingServer(null);
        }
    };

    const gpuChanged = (model) => {
        const current = gpuInputs[model.id];
        return current !== undefined && current !== model.gpu_device_ids;
    };

    const getServerStatusDot = (model) => {
        if (!model.keep_loaded) return null;
        const cfg = {
            stopped: { color: '#6c757d', label: 'Stopped' },
            starting: { color: '#ffc107', label: 'Starting...' },
            running: { color: '#28a745', label: 'Running' },
            error: { color: '#dc3545', label: 'Error' },
        };
        const s = cfg[model.server_status] || cfg.stopped;
        return (
            <span
                className="model-server-dot"
                title={`Server: ${s.label}`}
                style={{ background: s.color }}
            />
        );
    };

    const isServerActive = (model) => {
        return model.keep_loaded && ['running', 'starting'].includes(model.server_status);
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal models-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header">
                    <h3><Cpu size={20} /> AI Models</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="config-modal-body">
                    {loading ? (
                        <div className="loading-state">Loading models...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : models.length === 0 ? (
                        <div className="empty-state">
                            <Cpu size={32} />
                            <p>No AI models assigned to this study</p>
                            <small>Assign models from the Models page</small>
                        </div>
                    ) : (
                        <div className="models-list">
                            {models.map(model => (
                                <div key={model.id} className="model-item">
                                    <div className="model-info">
                                        <div className="model-name">
                                            {model.name}
                                            {getServerStatusDot(model)}
                                        </div>
                                        <div className="model-description">{model.description || 'No description'}</div>
                                        <div className="model-meta">
                                            <span className="model-type">{model.model_type || 'Segmentation'}</span>
                                            {model.default_version && (
                                                <span className="model-version">v{model.default_version.version}</span>
                                            )}
                                        </div>

                                        {model.gpu_required && (
                                            <div className="model-gpu-config">
                                                <label>GPUs:</label>
                                                <input
                                                    type="text"
                                                    className="model-gpu-input"
                                                    value={gpuInputs[model.id] !== undefined ? gpuInputs[model.id] : (model.gpu_device_ids || '0')}
                                                    onChange={(e) => setGpuInputs(prev => ({ ...prev, [model.id]: e.target.value }))}
                                                    disabled={togglingServer === model.id}
                                                    placeholder="0,1"
                                                    title="Comma-separated GPU IDs (e.g. 0,1 or 6,7)"
                                                />
                                                {isServerActive(model) && gpuChanged(model) && (
                                                    <button
                                                        className="model-gpu-apply-btn"
                                                        onClick={() => handleRestartWithNewGpus(model)}
                                                        disabled={togglingServer === model.id}
                                                        title="Restart server with new GPU assignment"
                                                    >
                                                        Apply
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        <div className="model-server-controls">
                                            <label
                                                className="model-keep-toggle"
                                                title={model.keep_loaded ? 'Server active - click to stop' : 'Keep model loaded in GPU memory'}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={model.keep_loaded || false}
                                                    disabled={togglingServer === model.id}
                                                    onChange={() => handleToggleKeepLoaded(model)}
                                                />
                                                <span className="model-toggle-slider"></span>
                                                <span className="model-keep-label">
                                                    {togglingServer === model.id ? (
                                                        <RefreshCw size={12} className="spinning" />
                                                    ) : (
                                                        'Keep loaded'
                                                    )}
                                                </span>
                                            </label>

                                            <button
                                                className="run-model-btn"
                                                onClick={() => handleRunOnAll(model)}
                                                disabled={runningModels[model.id]}
                                            >
                                                {runningModels[model.id] ? (
                                                    <>
                                                        <RefreshCw size={16} className="spinning" />
                                                        Running...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Play size={16} />
                                                        Run on All
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// DV Agents Configuration Modal - Workflow pipeline builder
const DVAgentsConfigModal = ({ studyId, study, onClose, onRefreshData, onWorkflowStart, onWorkflowUpdate, workflowState }) => {
    const [workflows, setWorkflows] = useState([]);
    const [activeWorkflow, setActiveWorkflow] = useState(null);
    const [loading, setLoading] = useState(false);

    // Demo workflow structure for UI
    const [editingWorkflow, setEditingWorkflow] = useState({
        name: 'New Workflow',
        description: '',
        steps: [
            { id: 1, name: 'Step 1', parallel_tasks: [] }
        ]
    });


    // Get assigned users from study (reviewers who can have isolated data)
    const assignedUsers = React.useMemo(() => {
        if (!study?.assignments) return [];
        return study.assignments.map(a => ({
            id: a.user.id,
            username: a.user.username,
            role: a.role
        }));
    }, [study]);

    const [availableTools, setAvailableTools] = useState([
        { id: 'convert_nifti', name: 'Convert NIfTI to DICOM', type: 'tool', icon: RefreshCw },
        { id: 'upload_orthanc', name: 'Upload to Orthanc', type: 'tool', icon: Upload, hasReviewerSelect: true },
        { id: 'preupload_annotations', name: 'Pre-upload Annotations', type: 'tool', icon: Layers, hasReviewerSelect: true },
        { id: 'preupload_maps', name: 'Pre-upload Maps', type: 'tool', icon: MapIcon, hasReviewerSelect: true },
    ]);

    const [availableModels, setAvailableModels] = useState([]);

    useEffect(() => {
        fetchModels();
    }, [studyId]);

    const fetchModels = async () => {
        try {
            const response = await API.get(`/ai-models/models/`, {
                params: { study: studyId }
            });
            setAvailableModels((response.data || []).map(m => ({
                id: `model_${m.id}`,
                modelId: m.id,
                name: m.name,
                type: 'model',
                icon: Cpu
            })));
        } catch (err) {
            console.error('Error fetching models:', err);
        }
    };

    const addStep = () => {
        setEditingWorkflow(prev => ({
            ...prev,
            steps: [
                ...prev.steps,
                { id: Date.now(), name: `Step ${prev.steps.length + 1}`, parallel_tasks: [] }
            ]
        }));
    };

    const removeStep = (stepId) => {
        setEditingWorkflow(prev => ({
            ...prev,
            steps: prev.steps.filter(s => s.id !== stepId)
        }));
    };

    // State for pending task that needs reviewer selection
    const [pendingTask, setPendingTask] = useState(null); // { stepId, task }

    // All available tasks (tools + models)
    const allTasks = [...availableTools, ...availableModels];

    const addTaskToStep = (stepId, task, reviewerId = null, reviewerName = null) => {
        const taskWithOptions = {
            ...task,
            taskId: Date.now(),
            reviewerId: reviewerId || null,
            reviewerName: reviewerName || null
        };
        setEditingWorkflow(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId
                    ? { ...step, parallel_tasks: [...step.parallel_tasks, taskWithOptions] }
                    : step
            )
        }));
    };

    const handleTaskSelect = (stepId, taskId) => {
        const task = allTasks.find(t => t.id === taskId);
        if (!task) return;

        // If this task has reviewer selection and there are assigned users, show the selector
        if (task.hasReviewerSelect && assignedUsers.length > 0) {
            setPendingTask({ stepId, task });
        } else {
            addTaskToStep(stepId, task);
        }
    };

    const handleConfirmTaskWithReviewer = (reviewerId) => {
        if (!pendingTask) return;
        const { stepId, task } = pendingTask;
        const reviewer = assignedUsers.find(u => u.id === parseInt(reviewerId));
        addTaskToStep(stepId, task, reviewerId || null, reviewer?.username || null);
        setPendingTask(null);
    };

    const handleCancelPendingTask = () => {
        setPendingTask(null);
    };

    const removeTaskFromStep = (stepId, taskId) => {
        setEditingWorkflow(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId
                    ? { ...step, parallel_tasks: step.parallel_tasks.filter(t => t.taskId !== taskId) }
                    : step
            )
        }));
    };

    const [isRunning, setIsRunning] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);
    const [stepProgress, setStepProgress] = useState({});

    // Helper to poll for task completion
    const waitForTaskCompletion = async (taskId, progressEndpoint, taskName) => {
        const pollInterval = 1000; // 1 second
        const maxAttempts = 600; // 10 minutes max
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {
                const response = await API.get(`${progressEndpoint}?task_id=${taskId}`);
                const progress = response.data;

                // Update progress state
                const newProgress = {
                    name: taskName,
                    progress: progress.converted || progress.uploaded || 0,
                    total: progress.total || 0,
                    status: progress.status
                };
                // Update local state first
                setStepProgress(prev => ({ ...prev, [taskId]: newProgress }));
                // Then update external state (outside of setState to avoid React warning)
                if (onWorkflowUpdate) {
                    onWorkflowUpdate({ stepProgress: { [taskId]: newProgress } });
                }

                if (progress.status === 'completed') {
                    return { success: true, data: progress };
                } else if (progress.status === 'stopped' || progress.status === 'error') {
                    return { success: false, error: `Task ${taskName} ${progress.status}` };
                }

                await new Promise(resolve => setTimeout(resolve, pollInterval));
                attempts++;
            } catch (err) {
                console.error(`Error polling ${taskName}:`, err);
                // Task might not exist yet or be cleaned up
                if (err.response?.status === 404) {
                    return { success: true }; // Task completed and cleaned up
                }
                attempts++;
            }
        }

        return { success: false, error: `Task ${taskName} timed out` };
    };

    const handleRunWorkflow = async () => {
        if (editingWorkflow.steps.some(s => s.parallel_tasks.length === 0)) {
            toast.warning('Please add at least one task to each step');
            return;
        }

        setIsRunning(true);
        setStepProgress({});
        toast.info(`Starting workflow: ${editingWorkflow.name}`);

        // Initialize external workflow state for tracking
        if (onWorkflowStart) {
            onWorkflowStart({
                name: editingWorkflow.name,
                steps: editingWorkflow.steps,
                currentStep: 0,
                status: 'running',
                stepProgress: {}
            });
        }

        // Execute steps sequentially, tasks within each step in parallel
        for (let stepIndex = 0; stepIndex < editingWorkflow.steps.length; stepIndex++) {
            const step = editingWorkflow.steps[stepIndex];
            setCurrentStep(stepIndex);
            if (onWorkflowUpdate) {
                onWorkflowUpdate({ currentStep: stepIndex, stepProgress: {} });
            }
            toast.info(`Executing ${step.name}...`);

            try {
                // Start all tasks in this step
                const taskPromises = step.parallel_tasks.map(async task => {
                    if (task.type === 'tool') {
                        if (task.id === 'convert_nifti') {
                            const response = await API.post('/viewers/batch_convert_nifti/', { study_id: studyId });
                            if (response.data.task_id) {
                                // Wait for conversion to complete
                                return waitForTaskCompletion(
                                    response.data.task_id,
                                    '/viewers/get_batch_conversion_progress/',
                                    'NIfTI Conversion'
                                );
                            } else if (response.data.status === 'completed') {
                                toast.info(response.data.message || 'No files to convert');
                                return { success: true };
                            }
                        } else if (task.id === 'upload_orthanc') {
                            // Use the reviewer from task options (set when adding the task)
                            const uploadParams = { study_id: studyId };
                            if (task.reviewerId) {
                                uploadParams.target_user_id = task.reviewerId;
                            }
                            const response = await API.post('/viewers/batch_upload_to_orthanc/', uploadParams);
                            if (response.data.task_id) {
                                // Wait for upload to complete
                                return waitForTaskCompletion(
                                    response.data.task_id,
                                    '/viewers/get_batch_upload_progress/',
                                    'Orthanc Upload'
                                );
                            } else if (response.data.status === 'completed') {
                                toast.info(response.data.message || 'No files to upload');
                                return { success: true };
                            }
                        } else if (task.id === 'preupload_annotations') {
                            const params = { study_id: studyId };
                            if (task.reviewerId) {
                                params.target_user_id = task.reviewerId;
                            }
                            const response = await API.post('/viewers/batch_preupload_annotations/', params);
                            if (response.data.task_id) {
                                return waitForTaskCompletion(
                                    response.data.task_id,
                                    '/viewers/get_batch_preupload_annotations_progress/',
                                    'Pre-upload Annotations'
                                );
                            } else if (response.data.status === 'completed') {
                                toast.info(response.data.message || 'No annotations to upload');
                                return { success: true };
                            }
                        } else if (task.id === 'preupload_maps') {
                            const params = { study_id: studyId };
                            if (task.reviewerId) {
                                params.target_user_id = task.reviewerId;
                            }
                            const response = await API.post('/viewers/batch_preupload_maps/', params);
                            if (response.data.task_id) {
                                return waitForTaskCompletion(
                                    response.data.task_id,
                                    '/viewers/get_batch_preupload_maps_progress/',
                                    'Pre-upload Maps'
                                );
                            } else if (response.data.status === 'completed') {
                                toast.info(response.data.message || 'No maps to upload');
                                return { success: true };
                            }
                        }
                    } else if (task.type === 'model') {
                        // Run model on all patients
                        const patientsResponse = await API.get(`/studies/${studyId}/patients/`);
                        const patients = patientsResponse.data || [];

                        if (patients.length === 0) {
                            toast.warning('No patients found');
                            return { success: true };
                        }

                        // Run model on each patient (batch of 5)
                        for (let i = 0; i < patients.length; i += 5) {
                            const batch = patients.slice(i, i + 5);
                            await Promise.all(batch.map(patient =>
                                API.post(`/ai-models/models/${task.modelId}/run/`, {
                                    patient_id: patient.patient_id,
                                    study_id: studyId
                                }).catch(err => {
                                    console.error(`Model run error for ${patient.patient_id}:`, err);
                                })
                            ));

                            // Update progress
                            const modelProgress = {
                                name: task.name,
                                progress: Math.min(i + 5, patients.length),
                                total: patients.length,
                                status: 'in_progress'
                            };
                            // Update local state first
                            setStepProgress(prev => ({ ...prev, [`model_${task.modelId}`]: modelProgress }));
                            // Then update external state (outside of setState to avoid React warning)
                            if (onWorkflowUpdate) {
                                onWorkflowUpdate({ stepProgress: { [`model_${task.modelId}`]: modelProgress } });
                            }
                        }

                        return { success: true };
                    }

                    return { success: true };
                });

                // Wait for all tasks in this step to complete
                const results = await Promise.all(taskPromises);
                const failed = results.find(r => !r?.success);

                if (failed) {
                    toast.error(`Step ${stepIndex + 1} failed: ${failed.error || 'Unknown error'}`);
                    setIsRunning(false);
                    setCurrentStep(null);
                    if (onWorkflowUpdate) {
                        onWorkflowUpdate({ status: 'error', currentStep: stepIndex });
                    }
                    return;
                }

                toast.success(`${step.name} completed!`);
            } catch (err) {
                console.error(`Error in ${step.name}:`, err);
                toast.error(`Failed at ${step.name}: ${err.message}`);
                setIsRunning(false);
                setCurrentStep(null);
                if (onWorkflowUpdate) {
                    onWorkflowUpdate({ status: 'error', currentStep: stepIndex });
                }
                return;
            }
        }

        setIsRunning(false);
        setCurrentStep(null);
        if (onWorkflowUpdate) {
            onWorkflowUpdate({ status: 'completed', currentStep: null });
        }
        toast.success('Workflow completed successfully!');
        if (onRefreshData) onRefreshData();
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal agents-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header">
                    <h3><GitBranch size={20} /> Workflows</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="config-modal-body workflow-builder">
                    {/* Workflow Name */}
                    <div className="workflow-header">
                        <input
                            type="text"
                            className="workflow-name-input"
                            value={editingWorkflow.name}
                            onChange={(e) => setEditingWorkflow(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Workflow name"
                        />
                    </div>

                    {/* Steps */}
                    <div className="workflow-steps">
                        {editingWorkflow.steps.map((step, stepIndex) => {
                            const isCurrentStep = isRunning && currentStep === stepIndex;
                            const isCompletedStep = isRunning && currentStep !== null && stepIndex < currentStep;
                            const isPendingStep = isRunning && currentStep !== null && stepIndex > currentStep;

                            return (
                                <div
                                    key={step.id}
                                    className={`workflow-step ${isCurrentStep ? 'running' : ''} ${isCompletedStep ? 'completed' : ''} ${isPendingStep ? 'pending' : ''}`}
                                >
                                    <div className="step-header">
                                        {isCurrentStep ? (
                                            <RefreshCw size={16} className="spinning step-status-icon" />
                                        ) : isCompletedStep ? (
                                            <div className="step-status-icon completed">✓</div>
                                        ) : (
                                            <GripVertical size={16} className="drag-handle" />
                                        )}
                                        <span className={`step-number ${isCurrentStep ? 'active' : ''} ${isCompletedStep ? 'done' : ''}`}>
                                            {stepIndex + 1}
                                        </span>
                                        <input
                                            type="text"
                                            className="step-name-input"
                                            value={step.name}
                                            onChange={(e) => setEditingWorkflow(prev => ({
                                                ...prev,
                                                steps: prev.steps.map(s =>
                                                    s.id === step.id ? { ...s, name: e.target.value } : s
                                                )
                                            }))}
                                            disabled={isRunning}
                                        />
                                        {!isRunning && editingWorkflow.steps.length > 1 && (
                                            <button
                                                className="remove-step-btn"
                                                onClick={() => removeStep(step.id)}
                                                title="Remove step"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="step-tasks">
                                        {step.parallel_tasks.length === 0 ? (
                                            <div className="empty-tasks">
                                                <small>Add tools or models to run in parallel</small>
                                            </div>
                                        ) : (
                                            <div className="tasks-list">
                                                {step.parallel_tasks.map(task => {
                                                    const TaskIcon = task.icon || Cpu;
                                                    return (
                                                        <div key={task.taskId} className={`task-chip ${task.type} ${isCurrentStep ? 'running' : ''}`}>
                                                            {isCurrentStep ? (
                                                                <RefreshCw size={14} className="spinning" />
                                                            ) : (
                                                                <TaskIcon size={14} />
                                                            )}
                                                            <span>{task.name}</span>
                                                            {task.reviewerName && (
                                                                <span className="task-reviewer-badge">
                                                                    {task.reviewerName}
                                                                </span>
                                                            )}
                                                            {task.id === 'upload_orthanc' && !task.reviewerName && (
                                                                <span className="task-reviewer-badge shared">
                                                                    shared
                                                                </span>
                                                            )}
                                                            {!isRunning && (
                                                                <button
                                                                    className="remove-task-btn"
                                                                    onClick={() => removeTaskFromStep(step.id, task.taskId)}
                                                                >
                                                                    <X size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Progress display when running */}
                                        {isCurrentStep && Object.keys(stepProgress).length > 0 && (
                                            <div className="step-progress">
                                                {Object.entries(stepProgress).map(([taskId, progress]) => (
                                                    <div key={taskId} className="task-progress">
                                                        <span className="progress-label">{progress.name}</span>
                                                        <div className="progress-bar-mini">
                                                            <div
                                                                className="progress-fill"
                                                                style={{ width: `${progress.total > 0 ? (progress.progress / progress.total) * 100 : 0}%` }}
                                                            />
                                                        </div>
                                                        <span className="progress-text">{progress.progress}/{progress.total}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Add task dropdown - hidden when running */}
                                        {!isRunning && (
                                            <div className="add-task-dropdown">
                                                <select
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleTaskSelect(step.id, e.target.value);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                    defaultValue=""
                                                >
                                                    <option value="" disabled>+ Add task...</option>
                                                    <optgroup label="Tools">
                                                        {availableTools.map(tool => (
                                                            <option key={tool.id} value={tool.id}>{tool.name}</option>
                                                        ))}
                                                    </optgroup>
                                                    {availableModels.length > 0 && (
                                                        <optgroup label="AI Models">
                                                            {availableModels.map(model => (
                                                                <option key={model.id} value={model.id}>{model.name}</option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                </select>

                                                {/* Reviewer selection popup for Upload to Orthanc */}
                                                {pendingTask && pendingTask.stepId === step.id && (
                                                    <div className="task-reviewer-selector">
                                                        <div className="reviewer-selector-header">
                                                            <Upload size={14} />
                                                            <span>Select target reviewer:</span>
                                                        </div>
                                                        <select
                                                            className="reviewer-select"
                                                            defaultValue=""
                                                            onChange={(e) => handleConfirmTaskWithReviewer(e.target.value)}
                                                            autoFocus
                                                        >
                                                            <option value="" disabled>Choose reviewer...</option>
                                                            <option value="">All reviewers (shared)</option>
                                                            {assignedUsers.map(user => (
                                                                <option key={user.id} value={user.id}>
                                                                    {user.username} ({user.role})
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            className="reviewer-cancel-btn"
                                                            onClick={handleCancelPendingTask}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Connector to next step */}
                                    {stepIndex < editingWorkflow.steps.length - 1 && (
                                        <div className={`step-connector ${isCompletedStep ? 'completed' : ''}`}>
                                            <ChevronDown size={20} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Add step button - hidden when running */}
                        {!isRunning && (
                            <button className="add-step-btn" onClick={addStep}>
                                <Plus size={16} />
                                Add Step
                            </button>
                        )}
                    </div>

                    {/* Run workflow button */}
                    <div className="workflow-actions">
                        <button
                            className="run-workflow-btn"
                            onClick={handleRunWorkflow}
                            disabled={isRunning || editingWorkflow.steps.every(s => s.parallel_tasks.length === 0)}
                        >
                            {isRunning ? (
                                <>
                                    <RefreshCw size={18} className="spinning" />
                                    Running...
                                </>
                            ) : (
                                <>
                                    <Play size={18} />
                                    Run Workflow
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Labels Configuration Modal
const LabelsConfigModal = ({ labels = [], onLabelsChange, labelDisplayMode = 'dropdown', onLabelDisplayModeChange, studyId, onClose }) => {
    // Default allow_multiple to match existing labels (if any are multi-select, default to multi)
    const defaultAllowMultiple = labels.some(l => l.allow_multiple);
    const [newLabel, setNewLabel] = useState({ name: "", color: "#007bff", shortcut: "", annotation_level: "patient", allow_multiple: defaultAllowMultiple });

    const handleDisplayModeChange = async (mode) => {
        try {
            await API.patch(`/studies/${studyId}/`, { label_display_mode: mode });
            onLabelDisplayModeChange && onLabelDisplayModeChange(mode);
            toast.success(`Label display set to ${mode === 'dropdown' ? 'dropdown' : 'side panel'}`);
        } catch (err) {
            console.error("Error saving label display mode:", err);
            toast.error("Failed to save display mode");
        }
    };

    const predefinedColors = [
        "#007bff", "#28a745", "#dc3545", "#ffc107",
        "#17a2b8", "#6f42c1", "#fd7e14", "#20c997"
    ];

    const annotationLevels = [
        { value: "patient", label: "Patient Level", description: "One annotation per patient" },
        { value: "data_type", label: "Data Type Level", description: "Separate per data type (imaging, document)" },
        { value: "series", label: "Series Level", description: "Separate per individual series" }
    ];

    const handleAddLabel = async () => {
        if (!newLabel.name.trim()) return;

        const label = {
            id: Date.now(),
            name: newLabel.name.trim(),
            color: newLabel.color,
            shortcut: newLabel.shortcut.trim().toUpperCase(),
            annotation_level: newLabel.annotation_level || "patient",
            allow_multiple: newLabel.allow_multiple || false
        };

        const updatedLabels = [...labels, label];

        try {
            await API.patch(`/studies/${studyId}/`, { labels: updatedLabels });
            onLabelsChange(updatedLabels);
            setNewLabel(prev => ({ name: "", color: "#007bff", shortcut: "", annotation_level: prev.annotation_level, allow_multiple: prev.allow_multiple }));
            toast.success("Label added successfully");
        } catch (err) {
            console.error("Error saving label:", err);
            toast.error("Failed to save label");
        }
    };

    const handleRemoveLabel = async (labelId) => {
        const updatedLabels = labels.filter((l) => l.id !== labelId);

        try {
            await API.patch(`/studies/${studyId}/`, { labels: updatedLabels });
            onLabelsChange(updatedLabels);
            toast.success("Label removed");
        } catch (err) {
            console.error("Error removing label:", err);
            toast.error("Failed to remove label");
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)' }}>
                    <Tags size={24} />
                    <div>
                        <h3>Study Labels</h3>
                        <p>Configure labels for data annotation</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="config-modal-body">
                    {/* Display Mode */}
                    <div className="config-section">
                        <h4>Viewer Display Mode</h4>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <div
                                onClick={() => handleDisplayModeChange('dropdown')}
                                style={{
                                    flex: 1, padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                                    fontSize: '12px', fontWeight: 500, border: '2px solid',
                                    borderColor: labelDisplayMode === 'dropdown' ? '#6f42c1' : '#dee2e6',
                                    backgroundColor: labelDisplayMode === 'dropdown' ? '#6f42c1' : '#fff',
                                    color: labelDisplayMode === 'dropdown' ? '#fff' : '#666',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'
                                }}
                            >
                                <i className="fa fa-bars" style={{ fontSize: '16px' }}></i>
                                <span>Dropdown</span>
                                <small style={{ opacity: 0.8, fontSize: '10px' }}>In navbar</small>
                            </div>
                            <div
                                onClick={() => handleDisplayModeChange('side_panel')}
                                style={{
                                    flex: 1, padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                                    fontSize: '12px', fontWeight: 500, border: '2px solid',
                                    borderColor: labelDisplayMode === 'side_panel' ? '#6f42c1' : '#dee2e6',
                                    backgroundColor: labelDisplayMode === 'side_panel' ? '#6f42c1' : '#fff',
                                    color: labelDisplayMode === 'side_panel' ? '#fff' : '#666',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'
                                }}
                            >
                                <i className="fa fa-columns" style={{ fontSize: '16px' }}></i>
                                <span>Side Panel</span>
                                <small style={{ opacity: 0.8, fontSize: '10px' }}>Collapsible</small>
                            </div>
                        </div>
                    </div>

                    {/* Existing Labels */}
                    <div className="config-section">
                        <h4>Current Labels ({labels.length})</h4>
                        {labels.length === 0 ? (
                            <p className="config-empty-text">No labels configured yet</p>
                        ) : (
                            <div className="config-items-list">
                                {labels.map((label) => (
                                    <div key={label.id} className="config-item">
                                        <span className="config-item-color" style={{ backgroundColor: label.color }} />
                                        <span className="config-item-name">{label.name}</span>
                                        {label.shortcut && (
                                            <span className="config-item-shortcut">{label.shortcut}</span>
                                        )}
                                        {label.annotation_level && label.annotation_level !== 'patient' && (
                                            <span className={`config-item-level level-${label.annotation_level}`}>
                                                {label.annotation_level === 'data_type' ? 'Data Type' : 'Series'}
                                            </span>
                                        )}
                                        <button className="config-item-remove" onClick={() => handleRemoveLabel(label.id)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Label */}
                    <div className="config-section">
                        <h4>Add New Label</h4>
                        <div className="config-add-form">
                            <div className="config-form-row">
                                <input
                                    type="text"
                                    className="config-input"
                                    placeholder="Label name"
                                    value={newLabel.name}
                                    onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                                    onKeyPress={(e) => e.key === "Enter" && handleAddLabel()}
                                />
                                <input
                                    type="text"
                                    className="config-input config-input-small"
                                    placeholder="Key"
                                    maxLength={1}
                                    value={newLabel.shortcut}
                                    onChange={(e) => setNewLabel({ ...newLabel, shortcut: e.target.value })}
                                />
                            </div>
                            <div className="config-color-row">
                                {predefinedColors.map((color) => (
                                    <button
                                        key={color}
                                        className={`config-color-btn ${newLabel.color === color ? "selected" : ""}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setNewLabel({ ...newLabel, color })}
                                    />
                                ))}
                            </div>
                            <div className="config-form-group">
                                <label>Selection Type</label>
                                <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                    <div
                                        onClick={() => setNewLabel({ ...newLabel, allow_multiple: false })}
                                        style={{
                                            flex: 1, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                            fontSize: '12px', fontWeight: 500, border: '2px solid',
                                            borderColor: !newLabel.allow_multiple ? '#007bff' : '#dee2e6',
                                            backgroundColor: !newLabel.allow_multiple ? '#007bff' : '#fff',
                                            color: !newLabel.allow_multiple ? '#fff' : '#666'
                                        }}
                                    >
                                        Single Select
                                    </div>
                                    <div
                                        onClick={() => setNewLabel({ ...newLabel, allow_multiple: true })}
                                        style={{
                                            flex: 1, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                            fontSize: '12px', fontWeight: 500, border: '2px solid',
                                            borderColor: newLabel.allow_multiple ? '#6f42c1' : '#dee2e6',
                                            backgroundColor: newLabel.allow_multiple ? '#6f42c1' : '#fff',
                                            color: newLabel.allow_multiple ? '#fff' : '#666'
                                        }}
                                    >
                                        Multi Select
                                    </div>
                                </div>
                                <small className="config-hint">
                                    {newLabel.allow_multiple ? "Multiple labels can be applied per patient" : "Only one label can be applied per patient"}
                                </small>
                            </div>
                            <div className="config-form-group">
                                <label>Annotation Level</label>
                                <select
                                    className="config-select"
                                    value={newLabel.annotation_level}
                                    onChange={(e) => setNewLabel({ ...newLabel, annotation_level: e.target.value })}
                                >
                                    {annotationLevels.map((level) => (
                                        <option key={level.value} value={level.value}>
                                            {level.label}
                                        </option>
                                    ))}
                                </select>
                                <small className="config-hint">
                                    {annotationLevels.find(l => l.value === newLabel.annotation_level)?.description}
                                </small>
                            </div>
                            <button className="config-add-btn" onClick={handleAddLabel} disabled={!newLabel.name.trim()}>
                                <Plus size={16} />
                                Add Label
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Questions Configuration Modal
const QuestionsConfigModal = ({ questions = [], onQuestionsChange, studyId, onClose }) => {
    // Canonical schema mirrors the inline study question editor (line ~2469):
    // { question, type: "text" | "multiple_choice" | "multiple_select", options, required }
    // The viewer (ViewerNavbar.jsx) reads exactly these field/value names.
    const [newQuestion, setNewQuestion] = useState({
        question: "",
        type: "text",
        options: [],
        required: false
    });
    const [newOption, setNewOption] = useState("");

    const questionTypes = [
        { value: "text", label: "Text Input", icon: Type },
        { value: "multiple_choice", label: "Single Choice", icon: List },
        { value: "multiple_select", label: "Multiple Choice", icon: List }
    ];

    const handleAddQuestion = async () => {
        if (!newQuestion.question.trim()) return;

        const question = {
            id: Date.now(),
            question: newQuestion.question.trim(),
            type: newQuestion.type,
            options: newQuestion.type !== "text" ? newQuestion.options : [],
            required: newQuestion.required,
            annotation_level: "patient",
        };

        const updatedQuestions = [...questions, question];

        try {
            await API.patch(`/studies/${studyId}/`, { questions: updatedQuestions });
            onQuestionsChange(updatedQuestions);
            setNewQuestion({ question: "", type: "text", options: [], required: false });
            toast.success("Question added successfully");
        } catch (err) {
            console.error("Error saving question:", err);
            toast.error("Failed to save question");
        }
    };

    const handleRemoveQuestion = async (questionId) => {
        const updatedQuestions = questions.filter((q) => q.id !== questionId);

        try {
            await API.patch(`/studies/${studyId}/`, { questions: updatedQuestions });
            onQuestionsChange(updatedQuestions);
            toast.success("Question removed");
        } catch (err) {
            console.error("Error removing question:", err);
            toast.error("Failed to remove question");
        }
    };

    const handleAddOption = () => {
        if (!newOption.trim()) return;
        setNewQuestion({
            ...newQuestion,
            options: [...newQuestion.options, newOption.trim()]
        });
        setNewOption("");
    };

    const handleRemoveOption = (index) => {
        setNewQuestion({
            ...newQuestion,
            options: newQuestion.options.filter((_, i) => i !== index)
        });
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)' }}>
                    <HelpCircle size={24} />
                    <div>
                        <h3>Study Questions</h3>
                        <p>Configure questions for data annotation</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="config-modal-body">
                    {/* Existing Questions */}
                    <div className="config-section">
                        <h4>Current Questions ({questions.length})</h4>
                        {questions.length === 0 ? (
                            <p className="config-empty-text">No questions configured yet</p>
                        ) : (
                            <div className="config-items-list">
                                {questions.map((question) => {
                                    const TypeIcon = questionTypes.find(t => t.value === question.type)?.icon || Type;
                                    return (
                                        <div key={question.id} className="config-item config-item-question">
                                            <TypeIcon size={16} className="config-item-type-icon" />
                                            <div className="config-item-content">
                                                <span className="config-item-name">{question.question || question.text}</span>
                                                {question.required && (
                                                    <span className="config-item-required">Required</span>
                                                )}
                                                {question.options && question.options.length > 0 && (
                                                    <span className="config-item-options">
                                                        {question.options.length} options
                                                    </span>
                                                )}
                                            </div>
                                            <button className="config-item-remove" onClick={() => handleRemoveQuestion(question.id)}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Add New Question */}
                    <div className="config-section">
                        <h4>Add New Question</h4>
                        <div className="config-add-form">
                            <input
                                type="text"
                                className="config-input"
                                placeholder="Question text"
                                value={newQuestion.question}
                                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                            />
                            <div className="config-form-row">
                                <select
                                    className="config-select"
                                    value={newQuestion.type}
                                    onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: [] })}
                                >
                                    {questionTypes.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                                <label className="config-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={newQuestion.required}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                                    />
                                    Required
                                </label>
                            </div>

                            {/* Options for select/multiselect */}
                            {newQuestion.type !== "text" && (
                                <div className="config-options-section">
                                    <label>Options</label>
                                    <div className="config-options-list">
                                        {newQuestion.options.map((option, index) => (
                                            <div key={index} className="config-option-item">
                                                <span>{option}</span>
                                                <button onClick={() => handleRemoveOption(index)}>
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="config-option-add">
                                        <input
                                            type="text"
                                            placeholder="Add option"
                                            value={newOption}
                                            onChange={(e) => setNewOption(e.target.value)}
                                            onKeyPress={(e) => e.key === "Enter" && handleAddOption()}
                                        />
                                        <button onClick={handleAddOption}>
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <button className="config-add-btn" onClick={handleAddQuestion} disabled={!newQuestion.question.trim()}>
                                <Plus size={16} />
                                Add Question
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Scores Configuration Modal
const ScoresConfigModal = ({ scores = [], onScoresChange, studyId, onClose }) => {
    const [newScore, setNewScore] = useState({
        name: "",
        min: 0,
        max: 10,
        step: 1,
        description: ""
    });

    const handleAddScore = async () => {
        if (!newScore.name.trim()) return;

        const score = {
            id: Date.now(),
            name: newScore.name.trim(),
            min: Number(newScore.min),
            max: Number(newScore.max),
            step: Number(newScore.step),
            description: newScore.description.trim()
        };

        const updatedScores = [...scores, score];

        try {
            await API.patch(`/studies/${studyId}/`, { scores: updatedScores });
            onScoresChange(updatedScores);
            setNewScore({ name: "", min: 0, max: 10, step: 1, description: "" });
            toast.success("Score metric added successfully");
        } catch (err) {
            console.error("Error saving score:", err);
            toast.error("Failed to save score metric");
        }
    };

    const handleRemoveScore = async (scoreId) => {
        const updatedScores = scores.filter((s) => s.id !== scoreId);

        try {
            await API.patch(`/studies/${studyId}/`, { scores: updatedScores });
            onScoresChange(updatedScores);
            toast.success("Score metric removed");
        } catch (err) {
            console.error("Error removing score:", err);
            toast.error("Failed to remove score metric");
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)' }}>
                    <Star size={24} />
                    <div>
                        <h3>Study Scores</h3>
                        <p>Configure scoring metrics for annotation</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="config-modal-body">
                    {/* Existing Scores */}
                    <div className="config-section">
                        <h4>Current Metrics ({scores.length})</h4>
                        {scores.length === 0 ? (
                            <p className="config-empty-text">No scoring metrics configured yet</p>
                        ) : (
                            <div className="config-items-list">
                                {scores.map((score) => (
                                    <div key={score.id} className="config-item">
                                        <Star size={16} className="config-item-icon" style={{ color: '#ffc107' }} />
                                        <div className="config-item-content">
                                            <span className="config-item-name">{score.name}</span>
                                            <span className="config-item-range">
                                                Range: {score.min} - {score.max} (step: {score.step})
                                            </span>
                                        </div>
                                        <button className="config-item-remove" onClick={() => handleRemoveScore(score.id)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Score */}
                    <div className="config-section">
                        <h4>Add New Metric</h4>
                        <div className="config-add-form">
                            <input
                                type="text"
                                className="config-input"
                                placeholder="Metric name (e.g., Image Quality)"
                                value={newScore.name}
                                onChange={(e) => setNewScore({ ...newScore, name: e.target.value })}
                            />
                            <div className="config-form-row config-score-range">
                                <div className="config-form-group">
                                    <label>Min</label>
                                    <input
                                        type="number"
                                        className="config-input"
                                        value={newScore.min}
                                        onChange={(e) => setNewScore({ ...newScore, min: e.target.value })}
                                    />
                                </div>
                                <div className="config-form-group">
                                    <label>Max</label>
                                    <input
                                        type="number"
                                        className="config-input"
                                        value={newScore.max}
                                        onChange={(e) => setNewScore({ ...newScore, max: e.target.value })}
                                    />
                                </div>
                                <div className="config-form-group">
                                    <label>Step</label>
                                    <input
                                        type="number"
                                        className="config-input"
                                        value={newScore.step}
                                        onChange={(e) => setNewScore({ ...newScore, step: e.target.value })}
                                    />
                                </div>
                            </div>
                            <input
                                type="text"
                                className="config-input"
                                placeholder="Description (optional)"
                                value={newScore.description}
                                onChange={(e) => setNewScore({ ...newScore, description: e.target.value })}
                            />
                            <button className="config-add-btn" onClick={handleAddScore} disabled={!newScore.name.trim()}>
                                <Plus size={16} />
                                Add Metric
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Sharing Configuration Modal
const SharingConfigModal = ({ isPublic, publicationUrl, onPublicChange, onPublicationUrlChange, shareAnnotations = false, onShareAnnotationsChange, studyId, onClose, onRefreshData }) => {
    const [tempUrl, setTempUrl] = useState(publicationUrl || "");
    const [saving, setSaving] = useState(false);

    const handleTogglePublic = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { is_public: !isPublic });
            onPublicChange(!isPublic);
            toast.success(isPublic ? "Study is now private" : "Study is now public!");
        } catch (err) {
            console.error("Error updating public status:", err);
            toast.error("Failed to update public status");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleShareAnnotations = async () => {
        setSaving(true);
        try {
            const next = !shareAnnotations;
            await API.patch(`/studies/${studyId}/`, { share_annotations: next });
            onShareAnnotationsChange?.(next);
            toast.success(
                next
                    ? "Annotations are now shared between all reviewers."
                    : "Annotations are now private to each reviewer."
            );
            onRefreshData?.();
        } catch (err) {
            console.error("Error updating share_annotations:", err);
            toast.error("Failed to update annotation sharing.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveUrl = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { publication_url: tempUrl || null });
            onPublicationUrlChange(tempUrl || null);
            toast.success("Publication URL saved");
        } catch (err) {
            console.error("Error saving publication URL:", err);
            toast.error("Failed to save publication URL");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal config-modal-small" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: isPublic ? 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' : 'linear-gradient(135deg, #6c757d 0%, #545b62 100%)' }}>
                    <Globe size={24} />
                    <div>
                        <h3>Sharing</h3>
                        <p>Annotation sharing between reviewers and public visibility</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="config-modal-body">
                    {/* Share Annotations Toggle */}
                    <div className="config-section">
                        <div className="config-toggle-row">
                            <div className="config-toggle-info">
                                <span className="config-toggle-label">Share annotations between reviewers</span>
                                <span className="config-toggle-hint">
                                    When on, every reviewer sees the same annotations (admin and other reviewers).
                                    When off, each reviewer only sees their own.
                                </span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={shareAnnotations}
                                    onChange={handleToggleShareAnnotations}
                                    disabled={saving}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                        {shareAnnotations && (
                            <div className="config-info-box" style={{ marginTop: 8 }}>
                                <i className="fa fa-info-circle"></i>
                                <span>
                                    Annotations uploaded before turning this on are stored in per-user DICOM
                                    copies and won't automatically appear to other reviewers — they'll need to
                                    be re-uploaded in the viewer.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Public Toggle */}
                    <div className="config-section">
                        <div className="config-toggle-row">
                            <div className="config-toggle-info">
                                <span className="config-toggle-label">Make study public</span>
                                <span className="config-toggle-hint">
                                    Visible in Open Research Studies
                                </span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isPublic}
                                    onChange={handleTogglePublic}
                                    disabled={saving}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>

                    {/* Publication URL */}
                    <div className="config-section">
                        <h4>
                            <ExternalLink size={14} />
                            Publication URL
                        </h4>
                        <div className="config-url-form">
                            <input
                                type="url"
                                className="config-input"
                                placeholder="https://doi.org/..."
                                value={tempUrl}
                                onChange={(e) => setTempUrl(e.target.value)}
                            />
                            <button
                                className="config-add-btn"
                                onClick={handleSaveUrl}
                                disabled={saving || tempUrl === (publicationUrl || "")}
                            >
                                Save URL
                            </button>
                        </div>
                    </div>

                    {/* Info about public studies */}
                    {isPublic && (
                        <div className="config-info-box">
                            <i className="fa fa-info-circle"></i>
                            <span>
                                Your study is visible on the{" "}
                                <a href="/OpenStudies" target="_blank" rel="noopener noreferrer">
                                    Research Constellations
                                </a>{" "}
                                page
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// Viewer Navbar configuration: per-study toggles for which buttons appear in
// the OHIF/DV viewer's top navbar. Persisted on DVStudy.viewer_navbar_config
// (JSONField). An empty/missing dict means "show everything" — the frontend
// falls back to true for any key not present.
const VIEWER_NAVBAR_OPTIONS = [
    {
        key: 'annotations',
        label: 'Annotations',
        description: 'Dropdown to load saved annotations for the current patient',
    },
    {
        key: 'maps',
        label: 'Maps',
        description: 'Dropdown to load probability/parametric maps',
    },
    {
        key: 'tools',
        label: 'AI Tools',
        description: 'Dropdown to run assigned AI tools on the current image',
    },
    {
        key: 'tool_history',
        label: 'Tool History',
        description: 'Recent AI tool executions and their results',
    },
    {
        key: 'request_bar',
        label: 'Request Bar',
        description: 'Free-text "Enter a request..." input for AI agent commands',
    },
    {
        key: 'mark_as_annotated',
        label: 'Mark as Annotated',
        description: 'Checkbox to manually mark the current patient as annotated',
    },
];

const ViewerNavbarConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    // Build initial toggle state: missing keys default to true (backwards-compatible).
    const initial = {};
    const stored = study?.viewer_navbar_config || {};
    VIEWER_NAVBAR_OPTIONS.forEach(opt => {
        initial[opt.key] = stored[opt.key] !== false;
    });

    const [config, setConfig] = useState(initial);
    const [saving, setSaving] = useState(false);

    const toggle = (key) => {
        setConfig(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { viewer_navbar_config: config });
            toast.success('Viewer navbar updated');
            if (onRefreshData) onRefreshData();
            onClose();
        } catch (err) {
            console.error('[ViewerNavbarConfig] save failed', err);
            toast.error(err.response?.data?.error || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal viewer-navbar-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header">
                    <h3><SlidersHorizontal size={20} /> Viewer Navbar</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="config-modal-body">
                    <p className="viewer-navbar-modal-intro">
                        Choose which buttons show up in the viewer's top navigation bar for
                        this study. Disabled buttons stay hidden for everyone opening this
                        study's viewer; turning them back on restores the default behavior.
                    </p>

                    <div className="viewer-navbar-options">
                        {VIEWER_NAVBAR_OPTIONS.map(opt => (
                            <label key={opt.key} className="viewer-navbar-option">
                                <input
                                    type="checkbox"
                                    checked={config[opt.key]}
                                    onChange={() => toggle(opt.key)}
                                    disabled={saving}
                                />
                                <div className="viewer-navbar-option-text">
                                    <span className="viewer-navbar-option-label">{opt.label}</span>
                                    <span className="viewer-navbar-option-desc">{opt.description}</span>
                                </div>
                            </label>
                        ))}
                    </div>

                    <div className="viewer-navbar-modal-actions">
                        <button
                            className="btn-secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Segmentation Preset configuration: a named scheme (e.g. "csPCa") with an
// ordered list of {label, color} segments. When set, the OHIF segmentation
// viewer auto-creates a labelmap pre-populated with these segments on load.
// Persisted on DVStudy.segmentation_preset (JSONField).
const SEGMENTATION_PRESET_COLORS = [
    '#dc3545', '#28a745', '#007bff', '#ffc107',
    '#17a2b8', '#6f42c1', '#fd7e14', '#20c997',
    '#e83e8c', '#6610f2',
];

const SegmentationPresetConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const stored = study?.segmentation_preset || {};
    const [name, setName] = useState(stored.name || '');
    const [segments, setSegments] = useState(
        Array.isArray(stored.segments) ? stored.segments : []
    );
    const [radius, setRadius] = useState(
        typeof stored.radius === 'number' ? String(stored.radius) : ''
    );
    const [draftLabel, setDraftLabel] = useState('');
    const [draftColor, setDraftColor] = useState(SEGMENTATION_PRESET_COLORS[0]);
    const [saving, setSaving] = useState(false);

    const handleAddSegment = () => {
        const trimmed = draftLabel.trim();
        if (!trimmed) return;
        setSegments(prev => [...prev, { label: trimmed, color: draftColor }]);
        setDraftLabel('');
        const next = SEGMENTATION_PRESET_COLORS[
            (segments.length + 1) % SEGMENTATION_PRESET_COLORS.length
        ];
        setDraftColor(next);
    };

    const handleRemoveSegment = (idx) => {
        setSegments(prev => prev.filter((_, i) => i !== idx));
    };

    const handleMoveSegment = (idx, dir) => {
        setSegments(prev => {
            const next = [...prev];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return prev;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };

    const handleUpdateSegment = (idx, patch) => {
        setSegments(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let payload;
            if (segments.length === 0) {
                payload = {};
            } else {
                payload = { name: name.trim() || 'Segmentation', segments };
                const parsedRadius = parseFloat(radius);
                if (Number.isFinite(parsedRadius) && parsedRadius > 0) {
                    payload.radius = parsedRadius;
                }
            }
            await API.patch(`/studies/${studyId}/`, { segmentation_preset: payload });
            toast.success('Segmentation preset saved');
            if (onRefreshData) onRefreshData();
            onClose();
        } catch (err) {
            console.error('[SegmentationPreset] save failed', err);
            toast.error(err.response?.data?.error || 'Failed to save preset');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Clear the segmentation preset for this study?')) return;
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { segmentation_preset: {} });
            toast.success('Segmentation preset cleared');
            if (onRefreshData) onRefreshData();
            onClose();
        } catch (err) {
            console.error('[SegmentationPreset] clear failed', err);
            toast.error('Failed to clear preset');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: 'linear-gradient(135deg, #9b59b6 0%, #6610f2 100%)' }}>
                    <Layers size={24} />
                    <div>
                        <h3>Segmentation Preset</h3>
                        <p>Auto-load a named segmentation with pre-defined segments when this study's case opens in the OHIF segmentation viewer</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="config-modal-body">
                    <div className="config-section">
                        <h4>Scheme Name</h4>
                        <input
                            type="text"
                            className="config-input"
                            placeholder="e.g. csPCa"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={saving}
                        />
                        <small className="config-hint">
                            Shown as the segmentation name in OHIF (e.g. replaces "Segmentation 1").
                        </small>
                    </div>

                    <div className="config-section">
                        <h4>Brush Radius (mm)</h4>
                        <input
                            type="number"
                            className="config-input"
                            placeholder="Leave blank to use OHIF default"
                            value={radius}
                            min="0.5"
                            max="99.5"
                            step="0.5"
                            onChange={(e) => setRadius(e.target.value)}
                            disabled={saving}
                        />
                        <small className="config-hint">
                            Initial brush radius applied to the Circular/Sphere brush when the
                            segmentation viewer opens. Leave blank to keep the OHIF default.
                        </small>
                    </div>

                    <div className="config-section">
                        <h4>Segments ({segments.length})</h4>
                        {segments.length === 0 ? (
                            <p className="config-empty-text">No segments defined yet</p>
                        ) : (
                            <div className="config-items-list">
                                {segments.map((seg, idx) => (
                                    <div key={idx} className="config-item">
                                        <span style={{ minWidth: 24, color: '#999', fontSize: 12 }}>{idx + 1}</span>
                                        <input
                                            type="color"
                                            value={seg.color || '#dc3545'}
                                            onChange={(e) => handleUpdateSegment(idx, { color: e.target.value })}
                                            disabled={saving}
                                            style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            className="config-input"
                                            style={{ flex: 1 }}
                                            value={seg.label}
                                            onChange={(e) => handleUpdateSegment(idx, { label: e.target.value })}
                                            disabled={saving}
                                        />
                                        <button
                                            className="config-item-remove"
                                            onClick={() => handleMoveSegment(idx, -1)}
                                            disabled={saving || idx === 0}
                                            title="Move up"
                                            style={{ opacity: idx === 0 ? 0.3 : 1 }}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className="config-item-remove"
                                            onClick={() => handleMoveSegment(idx, 1)}
                                            disabled={saving || idx === segments.length - 1}
                                            title="Move down"
                                            style={{ opacity: idx === segments.length - 1 ? 0.3 : 1 }}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            className="config-item-remove"
                                            onClick={() => handleRemoveSegment(idx)}
                                            disabled={saving}
                                            title="Remove"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="config-section">
                        <h4>Add Segment</h4>
                        <div className="config-add-form">
                            <div className="config-form-row">
                                <input
                                    type="text"
                                    className="config-input"
                                    placeholder="Segment label (e.g. GG1)"
                                    value={draftLabel}
                                    onChange={(e) => setDraftLabel(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddSegment()}
                                    disabled={saving}
                                />
                                <input
                                    type="color"
                                    value={draftColor}
                                    onChange={(e) => setDraftColor(e.target.value)}
                                    disabled={saving}
                                    style={{ width: 40, height: 40, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                                />
                            </div>
                            <div className="config-color-row">
                                {SEGMENTATION_PRESET_COLORS.map((color) => (
                                    <button
                                        key={color}
                                        className={`config-color-btn ${draftColor === color ? 'selected' : ''}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setDraftColor(color)}
                                        disabled={saving}
                                    />
                                ))}
                            </div>
                            <button
                                className="config-add-btn"
                                onClick={handleAddSegment}
                                disabled={saving || !draftLabel.trim()}
                            >
                                <Plus size={16} /> Add Segment
                            </button>
                        </div>
                    </div>

                    <div className="viewer-navbar-modal-actions">
                        <button
                            className="btn-secondary"
                            onClick={handleClear}
                            disabled={saving || segments.length === 0}
                        >
                            Clear Preset
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleSave}
                            disabled={saving || segments.length === 0}
                        >
                            {saving ? 'Saving…' : 'Save Preset'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Viewer Layout configuration: sets a default OHIF hanging protocol id that
// launch_viewer injects as &hangingprotocolId=<id> in the OHIF URL. Empty
// value means "no override" — OHIF picks its own default. Users can still
// switch layouts at runtime from the viewer's layout menu.
const VIEWER_LAYOUT_OPTIONS = [
    { group: 'Advanced', id: 'mpr',           name: 'MPR' },
    { group: 'Advanced', id: 'fourUp',        name: '3D four up' },
    { group: 'Advanced', id: 'main3D',        name: '3D main' },
    { group: 'Advanced', id: 'primaryAxial',  name: 'Axial Primary' },
    { group: 'Advanced', id: 'only3D',        name: '3D only' },
    { group: 'Advanced', id: 'primary3D',     name: '3D primary' },
    { group: 'Advanced', id: '@ohif/frameView', name: 'Frame View' },
    { group: 'Common',   id: 'default',       name: 'Single (default)' },
    { group: 'Common',   id: '@ohif/mnGrid',  name: 'Grid (multi)' },
    { group: 'Common',   id: '@ohif/mnGrid8', name: 'Grid (large)' },
];

const VIEWER_LAYOUT_LABELS = VIEWER_LAYOUT_OPTIONS.reduce((acc, o) => {
    acc[o.id] = o.name;
    return acc;
}, {});

const ViewerLayoutConfigModal = ({ studyId, study, onClose, onRefreshData }) => {
    const [selected, setSelected] = useState(study?.viewer_layout || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await API.patch(`/studies/${studyId}/`, { viewer_layout: selected });
            toast.success(selected ? 'Viewer layout saved' : 'Viewer layout cleared');
            if (onRefreshData) onRefreshData();
            onClose();
        } catch (err) {
            console.error('[ViewerLayout] save failed', err);
            toast.error(err.response?.data?.error || 'Failed to save layout');
        } finally {
            setSaving(false);
        }
    };

    const groups = ['Advanced', 'Common'];

    return (
        <div className="config-modal-overlay" onClick={onClose}>
            <div className="config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="config-modal-header" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0369a1 100%)' }}>
                    <LayoutGrid size={24} />
                    <div>
                        <h3>Viewer Layout</h3>
                        <p>Set the default OHIF layout when this study opens. Reviewers can still switch layouts at runtime from the viewer's layout menu.</p>
                    </div>
                    <button className="config-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="config-modal-body">
                    <div className="config-section">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid #dee2e6', borderRadius: 6, cursor: 'pointer', background: selected === '' ? '#eef7f6' : 'transparent' }}>
                            <input
                                type="radio"
                                name="viewer_layout"
                                value=""
                                checked={selected === ''}
                                onChange={() => setSelected('')}
                                disabled={saving}
                            />
                            <div>
                                <div style={{ fontWeight: 600 }}>No override</div>
                                <small className="config-hint">Let OHIF pick its default layout.</small>
                            </div>
                        </label>
                    </div>

                    {groups.map((group) => (
                        <div className="config-section" key={group}>
                            <h4>{group}</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                                {VIEWER_LAYOUT_OPTIONS.filter(o => o.group === group).map((opt) => (
                                    <label
                                        key={opt.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '10px 12px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            background: selected === opt.id ? '#eef7f6' : 'transparent',
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="viewer_layout"
                                            value={opt.id}
                                            checked={selected === opt.id}
                                            onChange={() => setSelected(opt.id)}
                                            disabled={saving}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{opt.name}</div>
                                            <small className="config-hint" style={{ fontFamily: 'monospace', fontSize: 11 }}>{opt.id}</small>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="config-modal-footer">
                    <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};


const ReviewerPickerBar = ({
    study,
    studyId,
    selectedReviewerId,
    onReviewerChange,
    showOnlyAssigned,
    onShowOnlyAssignedChange,
    assignedPatientCount = 0,
    shareAnnotations = false,
    onShareAnnotationsChange,
}) => {
    const [togglingShare, setTogglingShare] = useState(false);
    const reviewers = (study?.assignments || []).map(a => a.user).filter(Boolean);
    const counts = study?.patient_assignment_counts || {};
    const mode = study?.distribution_mode || 'shared';

    const handleToggleShare = async () => {
        if (togglingShare) return;
        setTogglingShare(true);
        try {
            const next = !shareAnnotations;
            await API.patch(`/studies/${studyId}/`, { share_annotations: next });
            onShareAnnotationsChange?.(next);
            toast.success(
                next
                    ? 'Annotations are now shared between all reviewers.'
                    : 'Annotations are now private to each reviewer.'
            );
        } catch (err) {
            console.error(err);
            toast.error('Failed to update annotation sharing.');
        } finally {
            setTogglingShare(false);
        }
    };

    const ShareAnnotationsToggle = (
        <label
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${shareAnnotations ? '#198754' : '#dc3545'}`,
                background: shareAnnotations ? '#d1e7dd' : '#f8d7da',
                color: shareAnnotations ? '#0f5132' : '#842029',
                cursor: togglingShare ? 'wait' : 'pointer',
                fontSize: 13,
                fontWeight: 500,
            }}
            title="When ON, every reviewer sees the same annotations across every viewer (OHIF, DV viewers, etc.). When OFF, each reviewer only sees their own."
        >
            <input
                type="checkbox"
                checked={shareAnnotations}
                onChange={handleToggleShare}
                disabled={togglingShare}
                style={{ margin: 0 }}
            />
            {shareAnnotations ? 'Annotations: Shared' : 'Annotations: Private (per reviewer)'}
        </label>
    );

    if (reviewers.length === 0) {
        return (
            <div className="reviewer-picker-bar" style={{
                background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, color: '#6c757d', fontSize: 14,
                display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center'
            }}>
                <UserCheck size={16} style={{ verticalAlign: 'middle' }} />
                <span>No reviewers assigned to this study yet. Use the project page to assign reviewers.</span>
                <div style={{ marginLeft: 'auto' }}>{ShareAnnotationsToggle}</div>
            </div>
        );
    }

    return (
        <div className="reviewer-picker-bar" style={{
            background: '#fff', border: '1px solid #e9ecef', borderRadius: 8,
            padding: '10px 14px', marginBottom: 12,
            display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserCheck size={18} style={{ color: '#0d6efd' }} />
                <strong>Reviewer view:</strong>
            </div>

            <select
                value={selectedReviewerId || ''}
                onChange={(e) => onReviewerChange(e.target.value)}
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: 220 }}
            >
                <option value="">— None (admin view, all patients) —</option>
                {reviewers.map(u => (
                    <option key={u.id} value={u.id}>
                        {u.username} ({counts[u.id] || 0} assigned)
                    </option>
                ))}
            </select>

            <span className="badge" style={{ background: '#e7f1ff', color: '#0d6efd' }}>
                Mode: {mode}
            </span>

            {ShareAnnotationsToggle}

            {selectedReviewerId && (
                <>
                    <span className="text-muted" style={{ fontSize: 13 }}>
                        <Eye size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Viewing as this reviewer · {assignedPatientCount} patient{assignedPatientCount !== 1 ? 's' : ''} assigned
                    </span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={showOnlyAssigned}
                            onChange={(e) => onShowOnlyAssignedChange(e.target.checked)}
                        />
                        Show only assigned patients
                    </label>
                </>
            )}
        </div>
    );
};


const StudyDashboard = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const studyId = params.StudyId;
    const viewAsUserId = searchParams.get('view_as');
    const [study, setStudy] = useState(null);
    const [studyData, setStudyData] = useState([]);
    const [selectedDataIds, setSelectedDataIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [labels, setLabels] = useState([]);
    const [labelDisplayMode, setLabelDisplayMode] = useState('dropdown');
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [allowedViewerTypes, setAllowedViewerTypes] = useState([]);
    const [annotatedPatients, setAnnotatedPatients] = useState([]);
    const [isPublic, setIsPublic] = useState(false);
    const [publicationUrl, setPublicationUrl] = useState(null);
    const [shareAnnotations, setShareAnnotations] = useState(false);

    // Reviewer-specific view state (admin only)
    const [selectedReviewerId, setSelectedReviewerId] = useState(
        searchParams.get('view_as') || ''
    );
    const [reviewerAssignedPatientIds, setReviewerAssignedPatientIds] = useState([]);
    const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);

    // First-time reviewer entry shows a "Create your study profile" modal. The
    // modal stops appearing only once the reviewer ticks the consent box AND
    // clicks Continue (which sets consented_at server-side and flips
    // reader_profile_completed to true). X / Cancel just dismiss locally so
    // the same modal returns on the next visit.
    const [showReaderProfileModal, setShowReaderProfileModal] = useState(false);
    useEffect(() => {
        if (!study) return;
        const isReviewer = study.user_assignment?.role === 'reviewer';
        const needsProfile = study.reader_profile_completed === false;
        setShowReaderProfileModal(isReviewer && needsProfile);
    }, [study]);
    const handleReaderProfileConfirmed = () => {
        setShowReaderProfileModal(false);
        fetchStudySpecs && fetchStudySpecs();
    };
    const handleReaderProfileClose = () => {
        // X button: dismiss only — let the reviewer browse the study without
        // committing. Modal will pop again on next entry until they consent.
        setShowReaderProfileModal(false);
    };
    const handleReaderProfileCancel = () => {
        // Cancel button: leave the page entirely.
        navigate(-1);
    };

    // Get current user from localStorage
    const currentUsername = localStorage.getItem('username');

    localStorage.setItem('studyId', studyId);

    const fetchStudySpecs = useCallback(async () => {
        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudy(response.data);
            setLabels(response.data.labels || []);
            setLabelDisplayMode(response.data.label_display_mode || 'dropdown');
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setAllowedViewerTypes(response.data.allowed_viewer_types || []);
            setIsPublic(response.data.is_public || false);
            setPublicationUrl(response.data.publication_url || null);
            setShareAnnotations(!!response.data.share_annotations);
        } catch (err) {
            console.error("Error fetching study specs:", err);
            toast.error("Failed to fetch study specs.");
        }
    }, [studyId]);

    // Handler for updating allowed viewer types
    const handleViewerTypesChange = async (newViewerTypes) => {
        try {
            await API.patch(`/studies/${studyId}/`, {
                allowed_viewer_types: newViewerTypes
            });
            setAllowedViewerTypes(newViewerTypes);
            toast.success("Allowed viewers updated successfully.");
        } catch (err) {
            console.error("Error updating allowed viewers:", err);
            toast.error("Failed to update allowed viewers.");
        }
    };

    // Check if current user can edit labels (creator, study lead, or project owner/admin)
    const canEditLabels = () => {
        if (!study || !currentUsername) return false;

        // Creator can always edit
        if (study.created_by_username === currentUsername) return true;

        // Check if user has lead role in assignments
        if (study.user_assignment?.role === 'lead') return true;

        // Project owner and project admins manage all studies in the project
        if (study.user_project_role === 'owner' || study.user_project_role === 'admin') return true;

        return false;
    };

    const fetchStudyData = useCallback(async () => {
        try {
            const url = viewAsUserId
                ? `/studies/${studyId}/get_study_data/?view_as=${viewAsUserId}`
                : `/studies/${studyId}/get_study_data/`;
            const response = await API.get(url);
            setStudyData(response.data || []);
        } catch (err) {
            console.error("Error fetching study data:", err);
        } finally {
            setLoading(false);
        }
    }, [studyId, viewAsUserId]);

    const fetchAnnotatedPatients = useCallback(async () => {
        try {
            const url = viewAsUserId
                ? `/studies/${studyId}/annotated_patients/?view_as=${viewAsUserId}`
                : `/studies/${studyId}/annotated_patients/`;
            const response = await API.get(url);
            console.log('Annotated patients API response:', response.data);
            const patientIds = response.data.patient_ids || [];
            console.log('Setting annotatedPatients to:', patientIds);
            setAnnotatedPatients(patientIds);
        } catch (err) {
            console.error("Error fetching annotated patients:", err);
        }
    }, [studyId, viewAsUserId]);

    const fetchReviewerAssignments = useCallback(async (reviewerId) => {
        if (!reviewerId) {
            setReviewerAssignedPatientIds([]);
            return;
        }
        try {
            const response = await API.get(
                `/studies/${studyId}/patient_assignments/?user_id=${reviewerId}`
            );
            const list = response.data?.assignments || [];
            setReviewerAssignedPatientIds(list.map(pa => pa.patient_id));
        } catch (err) {
            console.error("Error fetching reviewer assignments:", err);
        }
    }, [studyId]);

    useEffect(() => {
        fetchStudySpecs();
        fetchStudyData();
        fetchAnnotatedPatients();
    }, [fetchStudySpecs, fetchStudyData, fetchAnnotatedPatients]);

    // Refresh both the study specs (labels/questions/viewer_layout/...) and
    // the data list. Config modals patch fields on DVStudy, so refetching
    // specs is what makes the command-bar badges reflect the new value.
    const refreshAllStudyState = useCallback(async () => {
        await Promise.all([fetchStudySpecs(), fetchStudyData()]);
    }, [fetchStudySpecs, fetchStudyData]);

    useEffect(() => {
        fetchReviewerAssignments(selectedReviewerId);
    }, [selectedReviewerId, fetchReviewerAssignments]);

    // Keep selectedReviewerId in sync with the URL's view_as param when the URL
    // changes from outside (deep link, browser back/forward, Project page nav).
    useEffect(() => {
        const urlViewAs = searchParams.get('view_as') || '';
        if (urlViewAs !== selectedReviewerId) {
            setSelectedReviewerId(urlViewAs);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleSelectionChange = (selectedIds) => {
        setSelectedDataIds(selectedIds);
    };

    const handleReviewerChange = (reviewerId) => {
        setSelectedReviewerId(reviewerId);
        // Keep the URL in sync so opening a case carries view_as through.
        const params = new URLSearchParams(searchParams);
        if (reviewerId) {
            params.set('view_as', reviewerId);
        } else {
            params.delete('view_as');
        }
        navigate(`/Study/${studyId}?${params.toString()}`, { replace: true });
    };

    const refreshAfterChange = useCallback(async () => {
        await Promise.all([
            fetchStudySpecs(),
            fetchStudyData(),
            fetchAnnotatedPatients(),
            fetchReviewerAssignments(selectedReviewerId),
        ]);
    }, [fetchStudySpecs, fetchStudyData, fetchAnnotatedPatients, fetchReviewerAssignments, selectedReviewerId]);

    const handleAssignPatientsToReviewer = useCallback(async (patientIds) => {
        if (!selectedReviewerId) {
            toast.error('Select a reviewer first.');
            return;
        }
        if (!patientIds || patientIds.length === 0) return;
        try {
            await API.post(`/studies/${studyId}/patient_assignments/create/`, {
                user_id: parseInt(selectedReviewerId, 10),
                patient_ids: patientIds,
            });
            toast.success(`Assigned ${patientIds.length} patient${patientIds.length !== 1 ? 's' : ''}.`);
            await refreshAfterChange();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || 'Failed to assign patients.');
        }
    }, [studyId, selectedReviewerId, refreshAfterChange]);

    const handleRemoveCaseFromReviewer = useCallback(async (patientId, skipConfirm = false) => {
        if (!selectedReviewerId) return;
        if (!skipConfirm) {
            const ok = window.confirm(
                `Remove patient ${patientId} from this reviewer? This deletes their annotations and uploaded segmentations for this patient. This cannot be undone.`
            );
            if (!ok) return;
        }
        try {
            await API.delete(`/studies/${studyId}/patient_assignments/delete/`, {
                data: {
                    user_id: parseInt(selectedReviewerId, 10),
                    patient_id: patientId,
                },
            });
            toast.success(`Removed patient ${patientId}.`);
            await refreshAfterChange();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || 'Failed to remove patient.');
        }
    }, [studyId, selectedReviewerId, refreshAfterChange]);

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Reviewers on a randomized study see the streamlined Reader Dashboard
    // (progress + Start next case). Admins/leads keep the full StudyDashboard
    // so they can configure the study and view per-reviewer queues.
    const isRandomizedReader = study?.study_type === 'randomized' && !canEditLabels();

    if (isRandomizedReader) {
        return (
            <div className="study-dashboard-page">
                <div className="starfield-background"></div>
                <ToastContainer autoClose={3000} />
                <Navbar />
                {showReaderProfileModal && (
                    <ReaderProfileModal
                        studyId={studyId}
                        onConfirmed={handleReaderProfileConfirmed}
                        onCancel={handleReaderProfileCancel}
                        onClose={handleReaderProfileClose}
                    />
                )}
                {loading ? (
                    <div className="container py-4">
                        <div className="loading-card">
                            <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                        </div>
                    </div>
                ) : (
                    <ReaderDashboardView study={study} studyId={studyId} studyData={studyData} />
                )}
            </div>
        );
    }

    return (
        <div className="study-dashboard-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            {/* Toast Container for notifications */}
            <ToastContainer autoClose={3000} />

            {/* Header Navigation */}
            <Navbar />

            {/* First-time reviewer gate */}
            {showReaderProfileModal && (
                <ReaderProfileModal
                    studyId={studyId}
                    onConfirmed={handleReaderProfileConfirmed}
                    onCancel={handleReaderProfileCancel}
                    onClose={handleReaderProfileClose}
                />
            )}

            {/* Main Content */}
            <div className="container py-4">
                {/* Back Button - navigates to the study's project page */}
                <button
                    className="back-button"
                    onClick={() => study?.project ? navigate(`/Projects/${study.project}`) : navigate(-1)}
                >
                    <ArrowLeft size={18} />
                    Back
                </button>

                {/* Study Header Card */}
                <div className="study-header-card">
                    <div className="study-header-content">
                        <div className="study-info">
                            <h1 className="study-title">{study?.name || "Loading..."}</h1>
                            <p className="study-description">{study?.description || "No description"}</p>
                            {viewAsUserId && (
                                <div className="alert alert-info mt-2 mb-0 d-flex align-items-center gap-2 py-2">
                                    <i className="fa fa-user-circle"></i>
                                    <span>Viewing annotations as user ID: {viewAsUserId}</span>
                                    <button
                                        className="btn btn-sm btn-outline-primary ms-auto"
                                        onClick={() => navigate(`/Study/${studyId}`)}
                                    >
                                        Exit View As Mode
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="study-meta">
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span>Created {formatDate(study?.created_at)}</span>
                            </div>
                            <div className="meta-item">
                                <Users size={16} />
                                <span>{study?.project_name || "No project"}</span>
                            </div>
                            <span className={`study-status-badge ${study?.status || 'draft'}`}>
                                {study?.status || "Draft"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Study Command Bar - Configuration for Labels, Questions, Scores, Tools */}
                <StudyCommandBar
                    labels={labels}
                    onLabelsChange={setLabels}
                    labelDisplayMode={labelDisplayMode}
                    onLabelDisplayModeChange={setLabelDisplayMode}
                    questions={questions}
                    onQuestionsChange={setQuestions}
                    scores={scores}
                    onScoresChange={setScores}
                    isPublic={isPublic}
                    publicationUrl={publicationUrl}
                    onPublicChange={setIsPublic}
                    onPublicationUrlChange={setPublicationUrl}
                    shareAnnotations={shareAnnotations}
                    onShareAnnotationsChange={setShareAnnotations}
                    studyId={studyId}
                    study={study}
                    canEdit={canEditLabels()}
                    onRefreshData={refreshAllStudyState}
                />

                {/* Main Content Layout */}
                <div className="study-content-layout">
                    {/* Viewer Card - Compact on the left */}
                    <div className="study-viewer-section">
                        <ViewerCard
                            selectedDataIds={selectedDataIds}
                            studyId={studyId}
                            studyData={studyData}
                            customViewers={study?.viewers_detail || []}
                            allowedViewerTypes={allowedViewerTypes}
                            canEdit={canEditLabels()}
                            onViewerTypesChange={handleViewerTypesChange}
                            onRefreshData={fetchStudyData}
                            viewAsUserId={viewAsUserId}
                        />
                    </div>

                    {/* Data Table - Main content area */}
                    <div className="study-data-section">
                        {canEditLabels() && (
                            <ReviewerPickerBar
                                study={study}
                                studyId={studyId}
                                selectedReviewerId={selectedReviewerId}
                                onReviewerChange={handleReviewerChange}
                                showOnlyAssigned={showOnlyAssigned}
                                onShowOnlyAssignedChange={setShowOnlyAssigned}
                                assignedPatientCount={reviewerAssignedPatientIds.length}
                                shareAnnotations={shareAnnotations}
                                onShareAnnotationsChange={(next) => {
                                    setShareAnnotations(next);
                                    fetchStudyData();
                                    fetchAnnotatedPatients();
                                }}
                            />
                        )}
                        {loading ? (
                            <div className="loading-card">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <DatasetTable
                                studyData={studyData}
                                onSelectionChange={handleSelectionChange}
                                annotatedPatients={annotatedPatients}
                                canManage={canEditLabels()}
                                selectedReviewerId={selectedReviewerId}
                                reviewerAssignedPatientIds={reviewerAssignedPatientIds}
                                showOnlyAssigned={showOnlyAssigned && !!selectedReviewerId}
                                onAssignPatientsToReviewer={handleAssignPatientsToReviewer}
                                onRemoveCaseFromReviewer={handleRemoveCaseFromReviewer}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudyDashboard;
